import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ChevronLeft,
  ChevronRight,
  Upload,
  FileText,
  Clock,
  Settings,
  Brain,
  Users,
  UserPlus,
  X,
  Search,
  Loader2,
  Star,
  CheckCircle,
  Plus,
  Briefcase,
  ArrowRight,
  ArrowLeft,
  Edit,
  Edit2,
  Trash2,
  Calendar,
} from "lucide-react";
import { cn, cookieUtils } from "@/lib/utils";
import { aiModuleAPI, candidateFoldersAPI, candidatesAPI2, jobDescriptionAPI } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import AddCandidateModal from "@/components/admin/AddCandidateModal";
import CandidateSelectionModal from "@/components/admin/CandidateSelectionModal";

import AddQuestionModal from "@/components/admin/AddQuestionModal";
import ManualQuestionModal from "@/components/admin/ManualQuestionModal";
import EditManualQuestionModal from "@/components/admin/EditManualQuestionModal";
import QuestionBankSelectionModal from "@/components/admin/QuestionBankSelectionModal";
import QuestionGenerationConfirmDialog from "@/components/admin/QuestionGenerationConfirmDialog";
import ConfigurePopup from "@/components/admin/ConfigurePopup";
import { ResumeExtractionBuffer } from "@/components/candidate/exam-utils/DialogModals";
import ErrorBoundary from "@/components/ErrorBoundary";
import "./AIModule.css";

const AIModule = forwardRef(({ jobDescriptionData = null, onClose = null, isModal = false }, ref) => {
  const { toast } = useToast();
  
  // Utility function to render question text with proper code formatting
  const renderQuestionText = (questionText) => {
    if (!questionText) return null;

    // Decode HTML entities
    const decodeHtmlEntities = (text) => {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    };

    // Decode the text first
    const decodedText = decodeHtmlEntities(questionText);

    // Check if the text contains code (look for common code patterns)
    const hasCode = decodedText.includes('<') && decodedText.includes('>') || 
                   decodedText.includes('function') || 
                   decodedText.includes('const ') || 
                   decodedText.includes('let ') || 
                   decodedText.includes('var ') ||
                   decodedText.includes('class ') ||
                   decodedText.includes('import ') ||
                   decodedText.includes('export ') ||
                   decodedText.includes('{}') ||
                   decodedText.includes('[]') ||
                   decodedText.includes('()') ||
                   decodedText.includes('console.log') ||
                   decodedText.includes('document.') ||
                   decodedText.includes('window.') ||
                   decodedText.includes('React.') ||
                   decodedText.includes('useState') ||
                   decodedText.includes('useEffect');

    if (hasCode) {
      // Split text into parts (text and code)
      const parts = [];
      let currentText = decodedText;
      let partIndex = 0;

      // Look for code blocks (text between < and > or other code patterns)
      const codeRegex = /(<[^>]*>|`[^`]*`|```[\s\S]*?```)/g;
      let lastIndex = 0;
      let match;

      while ((match = codeRegex.exec(decodedText)) !== null) {
        // Add text before the code
        if (match.index > lastIndex) {
          const textPart = decodedText.substring(lastIndex, match.index);
          if (textPart.trim()) {
            parts.push(
              <span key={`text-${partIndex++}`}>
                {textPart}
              </span>
            );
          }
        }

        // Add the code part with styling
        const codePart = match[0];
        parts.push(
          <code 
            key={`code-${partIndex++}`}
            className="bg-gray-100 text-red-600 px-1 py-0.5 rounded text-xs font-mono border"
          >
            {codePart}
          </code>
        );

        lastIndex = match.index + match[0].length;
      }

      // Add remaining text
      if (lastIndex < decodedText.length) {
        const remainingText = decodedText.substring(lastIndex);
        if (remainingText.trim()) {
          parts.push(
            <span key={`text-${partIndex++}`}>
              {remainingText}
            </span>
          );
        }
      }

      return parts.length > 0 ? <>{parts}</> : decodedText;
    }

    // If no code detected, return plain text
    return decodedText;
  };
  
  // Cookie constants
  const COOKIE_NAME = 'ai_module_draft';
  const COOKIE_EXPIRY_DAYS = 1; // 1 day expiry
  
  const [currentStep, setCurrentStep] = useState(1);
  const [aiModuleId, setAiModuleId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [componentKey, setComponentKey] = useState(0); // Add key for force remounting

  // Cookie and unsaved changes state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveConfirmDialog, setShowSaveConfirmDialog] = useState(false);

  // Check for job description data passed from JobDescription page or props
  const [jobDescriptionDataState, setJobDescriptionDataState] = useState(() => {
    // If passed as prop, use it directly
    if (jobDescriptionData) {
      return jobDescriptionData;
    }

    // Check if job description data was passed via URL params or localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const jobDataParam = urlParams.get("jobData");

    if (jobDataParam) {
      try {
        return JSON.parse(decodeURIComponent(jobDataParam));
      } catch (error) {
        console.error("Error parsing job description data:", error);
      }
    }

    // Check localStorage as fallback
    const storedJobData = localStorage.getItem("aiModuleJobData");
    if (storedJobData) {
      try {
        const jobData = JSON.parse(storedJobData);
        // Clear from localStorage after reading
        localStorage.removeItem("aiModuleJobData");
        return jobData;
      } catch (error) {
        console.error("Error parsing stored job description data:", error);
      }
    }

    return null;
  });

  // Step 1 - Candidate Selection
  const [selectedExistingCandidates, setSelectedExistingCandidates] = useState(
    []
  );
  const [availableCandidates, setAvailableCandidates] = useState([]);
  const [candidateSearch, setCandidateSearch] = useState("");
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [isAddCandidateModalOpen, setIsAddCandidateModalOpen] = useState(false);
  const [isCandidateSelectionModalOpen, setIsCandidateSelectionModalOpen] = useState(false);
  const [folders, setFolders] = useState([]);

  // Resume upload states
  const [uploadingResumes, setUploadingResumes] = useState(new Set());
  const [candidateResumeFiles, setCandidateResumeFiles] = useState(new Map()); // candidateId -> File
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [candidatesWithoutResumes, setCandidatesWithoutResumes] = useState([]);

  const [formData, setFormData] = useState({
    // Job Description Information (from JobDescription page)
    jobDescription: jobDescriptionData || jobDescriptionDataState || null,

    // Step 2: Resume Extraction
    resumeFile: null,
    extractedSkills: [],
    extractedExperience: "",
    extractedEducation: "",
    candidateResumeData: [],
    extractionDate: null,

    // Step 3: Questions and Difficulty Level
    selectedCategories: [],
    customPrompt: "", // Custom prompt for single candidate

    // Step 3: New difficulty-based question counts
    totalQuestionsLimit: 10, // Total number of questions to generate
    easyQuestions: 0,
    mediumQuestions: 0,
    hardQuestions: 0,

    // Step 3: Candidate-specific configurations
    candidateConfigs: {}, // Object with candidateId as key and config as value

    // Step 5: Timings, Security and Advanced Settings
    examDateTime: "", // Exam date and time
    examDuration: 60, // Manual exam duration in minutes
    graceTime: 60, // Grace time in minutes (changed from array to number)
    sendInvitationEmail: true, // Send invitation email to candidate

    // Security Settings
    enableProctoring: false, // Enable proctoring
    captureImages: false, // Capture images during exam
    trackTabSwitching: false, // Track tab switching
    faceDetection: false, // Face detection monitoring
    warningsAllowed: 0, // Number of warnings before disqualification

    // Advanced Settings (from Exams.jsx)
    language: "English", // Default to English
    passingScore: [60], // Passing score percentage
    captureFrequency: 10000, // Capture frequency in milliseconds (10 seconds default)
  });

  // State for resume extraction
  const [extractingResume, setExtractingResume] = useState(false);
  const [resumeExtractionComplete, setResumeExtractionComplete] =
    useState(false);
  const [extractionAttempted, setExtractionAttempted] = useState(false);
  const [noResumesFound, setNoResumesFound] = useState(false);

  // State for tab-based candidate view
  const [activeTab, setActiveTab] = useState(null);
  
  // State for eligibility tabs (Eligible/Not Eligible)
  const [eligibilityTab, setEligibilityTab] = useState("eligible");

  // State for editing candidate data
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);



  // State for education validation
  const [educationValidationErrors, setEducationValidationErrors] = useState(
    {}
  );

  // State for Step 4 - Questions Generation
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [questionsGenerated, setQuestionsGenerated] = useState(false);
  const [activeQuestionTab, setActiveQuestionTab] = useState(null);
  const [questionCategories, setQuestionCategories] = useState({});
  const [addingManualQuestions, setAddingManualQuestions] = useState(false);

  // State for skill tags popup
  const [skillTagsPopup, setSkillTagsPopup] = useState({
    isOpen: false,
    skills: [],
    questionId: "",
    position: { x: 0, y: 0 },
  });

  // State for job skills popup in header
  const [jobSkillsPopup, setJobSkillsPopup] = useState({
    isOpen: false,
    skills: [],
    jobTitle: "",
  });

  // State for multiple resume uploads
  const [multipleResumeFiles, setMultipleResumeFiles] = useState([]);
  const [uploadingMultipleResumes, setUploadingMultipleResumes] = useState(false);
  const [resumeUploadProgress, setResumeUploadProgress] = useState({});
  const [processingResumes, setProcessingResumes] = useState(false);
  const [skippedResumes, setSkippedResumes] = useState([]);

  // State for Step 3 skills popup
  const [skillsPopup, setSkillsPopup] = useState({
    isOpen: false,
    candidateId: "",
    candidateName: "",
    skills: [],
  });

  // State for skills matching details popup
  const [skillsMatchingPopup, setSkillsMatchingPopup] = useState({
    isOpen: false,
    candidateId: "",
    candidateName: "",
    percentage: 0,
    matchedSkills: [],
    unmatchedSkills: [],
    totalJobSkills: 0,
    totalCandidateSkills: 0,
  });

  // State for question generation options (multiple candidates)
  const [questionGenerationOption, setQuestionGenerationOption] = useState("same"); // "same", "similar", "different"

  // State for Step 5 - Simplified without individual candidate configurations
  const [activeCandidateTab, setActiveCandidateTab] = useState(null);

  // State for warning dialog
  const [showWarningDialog, setShowWarningDialog] = useState(false);

  // State for proceed loading
  const [proceedLoading, setProceedLoading] = useState(false);
  
  // State for preventing backend overwrite of draft data
  const [draftRestored, setDraftRestored] = useState(false);

  // State for generate questions modal
  const [showGenerateQuestionsModal, setShowGenerateQuestionsModal] = useState(false);
  const [generateQuestionsFormData, setGenerateQuestionsFormData] = useState({
    questionGenerationOption: "same",
    totalQuestions: 10,
    easyQuestions: 0,
    mediumQuestions: 0,
    hardQuestions: 0,
    skillQuestions: {}, // New: skill-specific questions and difficulty
  });

  // State for add question modal
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [showManualQuestionModal, setShowManualQuestionModal] = useState(false);
  const [showQuestionBankModal, setShowQuestionBankModal] = useState(false);

  // State for editing/deleting manual questions
  const [editingManualQuestion, setEditingManualQuestion] = useState(null);
  const [showEditQuestionModal, setShowEditQuestionModal] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);

  // State for question generation confirmation dialog
  const [showQuestionGenerationConfirm, setShowQuestionGenerationConfirm] = useState(false);
  const [manualQuestionsCount, setManualQuestionsCount] = useState(0);
  const [questionGenerationChoice, setQuestionGenerationChoice] = useState(null); // Track user's choice
  const [pendingAction, setPendingAction] = useState(null); // Track what action to perform after confirmation
  const [currentActionType, setCurrentActionType] = useState("generate"); // Track action type for dialog context

  // State for configure popup
  const [showConfigurePopup, setShowConfigurePopup] = useState(false);
  
  // State for assignment cards display
  const [assignmentCards, setAssignmentCards] = useState([]);
  const [showAssignmentCards, setShowAssignmentCards] = useState(false);
  
  // State for editing assignment cards
  const [editingAssignmentIndex, setEditingAssignmentIndex] = useState(null);
  const [showEditAssignmentModal, setShowEditAssignmentModal] = useState(false);
  const [editAssignmentData, setEditAssignmentData] = useState({});

  // State for completion confirmation dialog
  const [showCompletionConfirmDialog, setShowCompletionConfirmDialog] = useState(false);
  const [pendingCompletionAction, setPendingCompletionAction] = useState(null);

  // Initialize modal form data when modal opens
  useEffect(() => {
    if (showGenerateQuestionsModal) {
      // Initialize skill questions with default values (0 for each difficulty)
      const skillQuestions = {};
      if (formData.jobDescription?.skills) {
        formData.jobDescription.skills.forEach(skill => {
          skillQuestions[skill] = {
            easy: 0,
            medium: 1, // Default 1 medium question per skill
            hard: 0
          };
        });
      }

      setGenerateQuestionsFormData({
        questionGenerationOption: questionGenerationOption,
        totalQuestions: formData.jobDescription?.skills?.length || 10,
        easyQuestions: formData.easyQuestions || 0,
        mediumQuestions: formData.mediumQuestions || 0,
        hardQuestions: formData.hardQuestions || 0,
        skillQuestions: skillQuestions,
      });
    }
  }, [showGenerateQuestionsModal, questionGenerationOption, formData.totalQuestionsLimit, formData.easyQuestions, formData.mediumQuestions, formData.hardQuestions, formData.jobDescription?.skills]);

  const totalSteps = 4; // Reduced from 5 since we skip the separate question generation step
  const progress = (currentStep / totalSteps) * 100;

  // Load candidates and folders on component mount
  useEffect(() => {
    fetchCandidates();
    fetchFolders();
  }, []);

  // Load existing AI module data if aiModuleId is provided
  useEffect(() => {
    const loadAIModuleData = async () => {
      if (aiModuleId) {
        try {
          const response = await aiModuleAPI.getAIModuleById(aiModuleId);
          if (response.data.success) {
            const aiModuleData = response.data.data;

            // Load candidate selection data
            if (aiModuleData.candidateSelection) {
              setSelectedExistingCandidates(
                aiModuleData.candidateSelection.existingCandidates || []
              );
            }

            // Load job description data if available - FETCH LATEST FROM BACKEND
            if (aiModuleData.jobDescription && aiModuleData.jobDescription._id) {
              try {
                const jdResponse = await jobDescriptionAPI.getOne(aiModuleData.jobDescription._id);
                
                if (jdResponse.data.success) {
                  const latestJobDescription = jdResponse.data.data;
                  
                  // Update both formData and jobDescriptionDataState with latest data
                  setFormData((prev) => ({
                    ...prev,
                    jobDescription: latestJobDescription,
                  }));
                  
                  setJobDescriptionDataState(latestJobDescription);
                  
                  
                  // Recalculate skills matching if resume data exists
                  if (aiModuleData.resumeExtraction?.candidateResumeData?.length > 0) {
                    try {
                      const recalcResponse = await aiModuleAPI.recalculateSkillsMatching(aiModuleId);
                      if (recalcResponse.data.success) {
                        // Reload AI Module data to get updated skills matching
                        const updatedResponse = await aiModuleAPI.getAIModuleById(aiModuleId);
                        if (updatedResponse.data.success) {
                          const updatedData = updatedResponse.data.data;
                          if (updatedData.resumeExtraction) {
                            setFormData((prev) => ({
                              ...prev,
                              candidateResumeData: updatedData.resumeExtraction.candidateResumeData || [],
                            }));
                          }
                        }
                      }
                    } catch (recalcError) {
                      console.warn('⚠️ Failed to recalculate skills matching:', recalcError);
                      // Continue anyway - not critical
                    }
                  }
                } else {
                  // Fallback to stored JD data if fetch fails
                  console.warn('⚠️ Failed to fetch latest job description, using stored data');
                  setFormData((prev) => ({
                    ...prev,
                    jobDescription: aiModuleData.jobDescription,
                  }));
                  setJobDescriptionDataState(aiModuleData.jobDescription);
                }
              } catch (jdError) {
                console.error('❌ Error fetching latest job description:', jdError);
                // Fallback to stored JD data
                setFormData((prev) => ({
                  ...prev,
                  jobDescription: aiModuleData.jobDescription,
                }));
                setJobDescriptionDataState(aiModuleData.jobDescription);
              }
            } else if (aiModuleData.jobDescription) {
              // No JD ID, use stored data
              setFormData((prev) => ({
                ...prev,
                jobDescription: aiModuleData.jobDescription,
              }));
              setJobDescriptionDataState(aiModuleData.jobDescription);
            }

            // Load resume extraction data (CRITICAL FOR STEP 2 RESTORATION)
            if (aiModuleData.resumeExtraction) {
              setFormData((prev) => ({
                ...prev,
                extractedSkills:
                  aiModuleData.resumeExtraction.extractedSkills || [],
                extractedExperience:
                  aiModuleData.resumeExtraction.extractedExperience || "",
                extractedEducation:
                  aiModuleData.resumeExtraction.extractedEducation || "",
                candidateResumeData:
                  aiModuleData.resumeExtraction.candidateResumeData || [],
                extractionDate: aiModuleData.resumeExtraction.extractionDate,
              }));

              // Set resume extraction states based on data availability
              if (aiModuleData.resumeExtraction.extractedSkills?.length > 0 || 
                  aiModuleData.resumeExtraction.candidateResumeData?.length > 0) {
                setResumeExtractionComplete(true);
                setExtractionAttempted(true);
              }
            }

            // Load exam configuration data (preserve draft data if restored)
            if (aiModuleData.examConfiguration && !draftRestored) {
              if (Array.isArray(aiModuleData.examConfiguration)) {
                // New format: array of candidate-specific configurations
                const candidateConfigs = {};
                aiModuleData.examConfiguration.forEach((config) => {
                  candidateConfigs[config.candidateId] = {
                    // New difficulty count fields
                    easyQuestions: config.easyQuestions || 0,
                    mediumQuestions: config.mediumQuestions || 0,
                    hardQuestions: config.hardQuestions || 0,
                    prompt: config.prompt,
                  };
                });

                setFormData((prev) => ({
                  ...prev,
                  candidateConfigs,
                  // For single candidate, also populate main form fields
                  ...(aiModuleData.examConfiguration.length === 1
                    ? {
                        easyQuestions:
                          aiModuleData.examConfiguration[0].easyQuestions || 0,
                        mediumQuestions:
                          aiModuleData.examConfiguration[0].mediumQuestions ||
                          0,
                        hardQuestions:
                          aiModuleData.examConfiguration[0].hardQuestions || 0,
                        customPrompt:
                          aiModuleData.examConfiguration[0].prompt || "",
                      }
                    : {}),
                }));
              } else {
                // Legacy format: single configuration object
                setFormData((prev) => ({
                  ...prev,
                  selectedCategories:
                    aiModuleData.examConfiguration.selectedCategories || [],
                }));
              }
            }

            // Load question generation option - preserve draft data if restored
            if (aiModuleData.questionGenerationOption && !draftRestored) {
              setQuestionGenerationOption(aiModuleData.questionGenerationOption);
            } else if (draftRestored) {
              console.log('🔄 Draft restored - preserving question generation option from draft:', questionGenerationOption);
            }

            // Load generated questions data (Step 4) - preserve draft data if restored
            if (
              (aiModuleData.generatedQuestions && aiModuleData.generatedQuestions.length > 0) ||
              (aiModuleData.Manual_questions && aiModuleData.Manual_questions.length > 0)
            ) {
              if (!draftRestored) {
                const mergedQuestions = mergeGeneratedAndManualQuestions(aiModuleData);
                
                if (mergedQuestions.length > 0) {
                  setGeneratedQuestions(mergedQuestions);
                  setQuestionsGenerated(true);

                  // Set active tab to first candidate
                  if (!activeQuestionTab) {
                    setActiveQuestionTab(mergedQuestions[0].candidateId);
                  }

                  // Initialize categories (empty since they're not saved until Step 4 completion)
                  const initialCategories = {};
                  mergedQuestions.forEach((candidateQuestions) => {
                    initialCategories[candidateQuestions.candidateId] = "";
                  });
                  setQuestionCategories(initialCategories);
                }
              } else {
                // Load questions from backend but preserve draft states
                const mergedQuestions = mergeGeneratedAndManualQuestions(aiModuleData);
                if (mergedQuestions.length > 0) {
                  setGeneratedQuestions(mergedQuestions);
                }
                // Keep the questionsGenerated and activeQuestionTab states from draft
                // Keep questionCategories from draft
              }
            }

            // Load timing configuration data
            if (aiModuleData.timingConfiguration) {
              setFormData((prev) => ({
                ...prev,
                graceTime: aiModuleData.timingConfiguration.graceTime || 60,
              }));
            }

            // Load exam assignment configuration data (Step 5) - preserve draft data if restored
            if (aiModuleData.examAssignmentConfiguration && !draftRestored) {
              // Handle both old format (object) and new format (array)
              let configData;

              if (Array.isArray(aiModuleData.examAssignmentConfiguration)) {
                // New array format - use first candidate for single candidate or set up candidate configs
                configData = aiModuleData.examAssignmentConfiguration[0] || {};

                // Individual candidate configurations removed - using single configuration for all candidates
              } else {
                // Old object format - for backward compatibility
                configData = aiModuleData.examAssignmentConfiguration;
              }

              setFormData((prev) => ({
                ...prev,
                examDateTime: configData.examDateTime || "",
                examDuration: configData.examDuration || 60,
                graceTime: configData.graceTime || 60,
                sendInvitationEmail: configData.sendInvitationEmail !== false,
                enableProctoring: configData.enableProctoring || false,
                captureImages: configData.captureImages || false,
                trackTabSwitching: configData.trackTabSwitching || false,
                faceDetection: configData.faceDetection || false,
                warningsAllowed: configData.warningsAllowed || 0,
                captureFrequency: configData.captureFrequency || 10000,
                language: configData.language || "English",
                passingScore: [configData.passingScore || 60],
                showResults: configData.showResults !== false,
                allowReview: configData.allowReview || false,
                shuffleQuestions: configData.shuffleQuestions !== false,
                shuffleOptions: configData.shuffleOptions !== false,
              }));
            } else if (draftRestored) {
              console.log('🔄 Draft restored - preserving Step 5 timing and security settings from draft');
            }

            // Use explicit completed steps from backend
            const completed = new Set(aiModuleData.completedSteps || []);
            setCompletedSteps(completed);
            
            // Set current step based on completed steps
            if (completed.has(5)) {
              setCurrentStep(5); // All steps completed, stay on step 5
            } else if (completed.has(4)) {
              setCurrentStep(5); // Step 4 completed, go to step 5
            } else if (completed.has(3)) {
              setCurrentStep(4); // Step 3 completed, go to step 4
            } else if (completed.has(2)) {
              setCurrentStep(3); // Step 2 completed, go to step 3
            } else if (completed.has(1)) {
              setCurrentStep(2); // Step 1 completed, go to step 2
            }
            
          }
        } catch (error) {
          console.error("Error loading AI Module data:", error);
          toast({
            title: "Error",
            description: "Failed to load AI Module data",
            variant: "destructive",
          });
        }
      }
    };

    loadAIModuleData();
  }, [aiModuleId, draftRestored]);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (window.candidateSearchTimeout) {
        clearTimeout(window.candidateSearchTimeout);
      }
    };
  }, []);

  // Add cleanup for component unmount to prevent state updates after unmount
  useEffect(() => {
    let isMounted = true;

    return () => {
      isMounted = false;
      // Clear any pending state updates
      setLoading(false);
      setExtractingResume(false);
      setGeneratingQuestions(false);
      setSavingEdit(false);
      setUploadingResumes(new Set());
    };
  }, []);

  // Force component remount on critical errors
  const forceRemount = () => {
    setComponentKey((prev) => prev + 1);
  };

  // Function to fetch/reload AI Module data with retry logic
  const fetchAIModuleData = async (moduleId, retryCount = 0, maxRetries = 3) => {
    if (!moduleId) return;
    
    try {
      const response = await aiModuleAPI.getAIModuleById(moduleId);
      if (response.data.success) {
        const aiModuleData = response.data.data;
        
        // Log question details for debugging
        if (aiModuleData.Manual_questions) {
          aiModuleData.Manual_questions.forEach((candidateQuestions, index) => {
            console.log(`📝 Debug - Candidate ${index + 1} (${candidateQuestions.candidateName}): ${candidateQuestions.questions?.length || 0} questions`);
          });
        }
        
        // Merge questions and update state
        const mergedQuestions = mergeGeneratedAndManualQuestions(aiModuleData);
        
        // Always update state, even if no questions
        setGeneratedQuestions(mergedQuestions);
        
        // Set questionsGenerated to true if we have any questions
        const hasQuestions = mergedQuestions.length > 0 && 
          mergedQuestions.some(candidate => candidate.questions && candidate.questions.length > 0);
        
        
        setQuestionsGenerated(hasQuestions);
        
        if (hasQuestions) {
          // Update active tab if not set
          if (!activeQuestionTab && mergedQuestions.length > 0) {
            setActiveQuestionTab(mergedQuestions[0].candidateId);
          }

        } else {
          console.log('⚠️ Debug - No questions found after merge');
        }
        
        // Force a component update to reflect changes
        const newKey = Date.now();
        setComponentKey(newKey);
      }
    } catch (error) {
      console.error(`❌ Error fetching AI Module data (attempt ${retryCount + 1}):`, error);
      
      // Retry logic for database consistency issues
      if (retryCount < maxRetries && (
        error.message?.includes('timeout') || 
        error.message?.includes('network') ||
        error.response?.status >= 500
      )) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchAIModuleData(moduleId, retryCount + 1, maxRetries);
      }
      
      toast({
        title: "Error",
        description: "Failed to refresh AI Module data",
        variant: "destructive",
      });
    }
  };

  // Helper function to merge generated questions and manual questions
  const mergeGeneratedAndManualQuestions = (aiModuleData) => {
    
    const candidateMap = new Map();
    
    // Add generated questions first
    if (aiModuleData.generatedQuestions && aiModuleData.generatedQuestions.length > 0) {
      aiModuleData.generatedQuestions.forEach(candidateQuestions => {
        const candidateId = candidateQuestions.candidateId.toString();
        candidateMap.set(candidateId, {
          ...candidateQuestions,
          questions: candidateQuestions.questions?.map(question => ({
            ...question,
            source: question.source || 'ai',
            showEditDelete: !!question.manualQuestionId
          })) || []
        });
      });
    }
    
    // Add manual questions
    if (aiModuleData.Manual_questions && aiModuleData.Manual_questions.length > 0) {
      aiModuleData.Manual_questions.forEach(candidateQuestionGroup => {
        const candidateId = candidateQuestionGroup.candidateId.toString();
        
        // Skip AI-generated questions stored in wrong place
        if (candidateQuestionGroup.category === 'AI Generated') {
          return;
        }
        
        // Only process manual questions
        if (candidateQuestionGroup.category === 'Manual Questions' && 
            candidateQuestionGroup.questions && 
            candidateQuestionGroup.questions.length > 0) {
          
          const manualQuestions = candidateQuestionGroup.questions.map(question => ({
            ...question,
            source: question.source || 'manual',
            isManual: true,
            showEditDelete: !!question.manualQuestionId
          }));
          
          if (candidateMap.has(candidateId)) {
            // Merge with existing candidate
            const existingCandidate = candidateMap.get(candidateId);
            candidateMap.set(candidateId, {
              ...existingCandidate,
              questions: [...(existingCandidate.questions || []), ...manualQuestions]
            });
          } else {
            // Add as new candidate
            candidateMap.set(candidateId, {
              candidateId: candidateId,
              candidateName: candidateQuestionGroup.candidateName,
              questions: manualQuestions
            });
          }
        }
      });
    }
    
    const allQuestions = Array.from(candidateMap.values());
    
    return allQuestions;
  };

  // Helper function to check if any questions exist (manual or generated)
  const checkForExistingManualQuestions = () => {

    let totalQuestions = 0;
    
    // Check both formData.questions and generatedQuestions for ANY questions
    const questionsToCheck = [];
    
    // Add questions from formData if available
    if (formData && formData.questions && Array.isArray(formData.questions)) {
      questionsToCheck.push(...formData.questions);
    }
    
    // Add questions from generatedQuestions if available
    if (generatedQuestions && Array.isArray(generatedQuestions)) {
      questionsToCheck.push(...generatedQuestions);
    }
    
    
    // Count ALL questions across all candidates (both manual and generated)
    questionsToCheck.forEach((candidateQuestionGroup, index) => {
      
      if (candidateQuestionGroup.questions && candidateQuestionGroup.questions.length > 0) {
        candidateQuestionGroup.questions.forEach((question, qIndex) => {
          console.log(`🔍 Debug - Question ${qIndex}:`, {
            source: question.source,
            manualQuestionId: question.manualQuestionId,
            generatedBy: question.generatedBy,
            hasSource: !!question.source,
            questionText: question.question?.substring(0, 50) + '...'
          });
        });
        
        // Count ALL questions (both manual and generated)
        const allQuestions = candidateQuestionGroup.questions;
        
        totalQuestions += allQuestions.length;
      }
    });


    return {
      hasManualQuestions: totalQuestions > 0, // Changed to check for ANY questions, not just manual
      count: totalQuestions
    };
  };

  // Handle Generate Questions button click with confirmation check
  const handleGenerateQuestionsClick = () => {
    const existingQuestionsCheck = checkForExistingManualQuestions();
    
    
    if (existingQuestionsCheck.hasManualQuestions) {
      // Show confirmation dialog if any questions exist
      setManualQuestionsCount(existingQuestionsCheck.count);
      setQuestionGenerationChoice(null); // Reset choice
      setCurrentActionType("generate"); // Set action type for dialog context
      setShowQuestionGenerationConfirm(true);
      // Store the intended action for after confirmation
      setPendingAction('generateQuestions');
    } else {
      // Proceed directly to generate questions modal if no questions exist
      setShowGenerateQuestionsModal(true);
    }
  };

  // Handle confirmation dialog response
  const handleQuestionGenerationConfirm = async (choice) => {
    setShowQuestionGenerationConfirm(false);
    
    if (choice === "existing") {
      // Store the choice
      setQuestionGenerationChoice("existing");
      
      // Execute the pending action
      if (pendingAction === 'generateQuestions') {
        setShowGenerateQuestionsModal(true);
      } else if (pendingAction === 'addManually') {
        setShowManualQuestionModal(true);
      } else if (pendingAction === 'addFromQuestionBank') {
        setShowQuestionBankModal(true);
      }
      
    } else if (choice === "new") {
      
      // Store the choice
      setQuestionGenerationChoice("new");
      
      // For "new" choice, we need to clear existing questions first
      if (pendingAction === 'generateQuestions') {
        setShowGenerateQuestionsModal(true);
        toast({
          title: "Ready to Generate",
          description: "Existing manual questions will be replaced with new AI questions.",
        });
      } else if (pendingAction === 'addManually') {
        // For manual questions, "new" means clear existing and add new manual questions
        setShowManualQuestionModal(true);
        toast({
          title: "Ready to Add",
          description: "Existing questions will be replaced with new manual questions.",
        });
      } else if (pendingAction === 'addFromQuestionBank') {
        // For question bank, "new" means clear existing and add from question bank
        setShowQuestionBankModal(true);
        toast({
          title: "Ready to Select",
          description: "Existing questions will be replaced with questions from question bank.",
        });
      }
    }
    
    // Reset pending action
    setPendingAction(null);
    // If choice is neither "existing" nor "new", user cancelled, so do nothing
  };

  // Add global error handler for DOM manipulation errors
  useEffect(() => {
    const handleError = (event) => {
      if (
        event.error?.message?.includes("removeChild") ||
        event.error?.message?.includes(
          "The node to be removed is not a child"
        ) ||
        event.error?.name === "NotFoundError"
      ) {
        console.warn("DOM manipulation error caught, attempting recovery...");
        // Force remount after a short delay
        setTimeout(() => {
          forceRemount();
        }, 100);
      }
    };

    window.addEventListener("error", handleError);
    return () => window.removeEventListener("error", handleError);
  }, []);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isModal) {
      // Add class to body to prevent scrolling
      document.body.classList.add('modal-open');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Remove class and restore scrolling when modal closes
        document.body.classList.remove('modal-open');
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      };
    }
  }, [isModal]);

  // Cookie and form change management
  useEffect(() => {
    // Check for saved draft on component mount
    if (isModal) {
      // First check with simple method
      const rawCookies = document.cookie;
      const cookieExists = rawCookies.includes(`${COOKIE_NAME}=`);
      
      
      if (cookieExists) {
        try {
          // Extract cookie value manually
          const cookieMatch = rawCookies.match(new RegExp(`${COOKIE_NAME}=([^;]+)`));
          if (cookieMatch) {
            const savedDraft = JSON.parse(cookieMatch[1]);
            
            // Restore candidate selection
            if (savedDraft.candidateIds && savedDraft.candidateNames) {
              const candidateIds = savedDraft.candidateIds.split(',');
              const candidateNames = savedDraft.candidateNames.split('|');
              const candidateEmails = savedDraft.candidateEmails ? savedDraft.candidateEmails.split('|') : [];
              
              const restoredCandidates = candidateIds.map((id, index) => ({
                _id: id,
                name: candidateNames[index] || 'Unknown',
                email: candidateEmails[index] || ''
              }));
              
              setSelectedExistingCandidates(restoredCandidates);
            }
            
            // Restore AI Module ID if available
            if (savedDraft.aiModuleId) {
              setAiModuleId(savedDraft.aiModuleId);
            }
            
            // Restore Step 2 resume extraction states
            if (savedDraft.resumeExtractionComplete === '1') {
              setResumeExtractionComplete(true);
            }
            if (savedDraft.extractionAttempted === '1') {
              setExtractionAttempted(true);
            }
            if (savedDraft.noResumesFound === '1') {
              setNoResumesFound(true);
            }
            
            // Restore Step 3 question generation option
            if (savedDraft.questionGenerationOption) {
              setQuestionGenerationOption(savedDraft.questionGenerationOption);
            }
            
            // Restore Step 4 questions generation states
            if (savedDraft.questionsGenerated === '1') {
              setQuestionsGenerated(true);
            }
            if (savedDraft.activeQuestionTab) {
              setActiveQuestionTab(savedDraft.activeQuestionTab);
            }
            
            // Restore Step 5 active candidate tab
            if (savedDraft.activeCandidateTab) {
              setActiveCandidateTab(savedDraft.activeCandidateTab);
            }
            
            // Restore assignment cards state (Step 4)
            if (savedDraft.showAssignmentCards === '1') {
              setShowAssignmentCards(true);
            }
            if (savedDraft.assignmentCardsData) {
              try {
                const restoredCards = JSON.parse(savedDraft.assignmentCardsData);
                setAssignmentCards(restoredCards);
              } catch (error) {
                console.warn('🍪 Error parsing assignment cards data:', error);
              }
            }
            if (savedDraft.editingAssignmentIndex && savedDraft.editingAssignmentIndex !== '') {
              setEditingAssignmentIndex(parseInt(savedDraft.editingAssignmentIndex));
            }
            if (savedDraft.showEditAssignmentModal === '1') {
              setShowEditAssignmentModal(true);
            }
            
            // Restore candidate-specific configurations
            try {
              if (savedDraft.candidateConfigs) {
                const candidateConfigs = JSON.parse(savedDraft.candidateConfigs);
                setFormData(prev => ({
                  ...prev,
                  candidateConfigs
                }));
              }
              
              // Individual candidate timing configurations removed - using single configuration
              
              if (savedDraft.questionCategories) {
                const questionCategories = JSON.parse(savedDraft.questionCategories);
                setQuestionCategories(questionCategories);
              }
            } catch (configError) {
              console.warn('🍪 Error parsing candidate configurations:', configError);
            }
            
            // Restore form data (including difficulty questions for single candidate)
            setFormData(prev => {
              const restoredData = {
                ...prev,
                customPrompt: savedDraft.customPrompt || prev.customPrompt,
                easyQuestions: parseInt(savedDraft.easyQuestions) || prev.easyQuestions,
                mediumQuestions: parseInt(savedDraft.mediumQuestions) || prev.mediumQuestions,
                hardQuestions: parseInt(savedDraft.hardQuestions) || prev.hardQuestions,
                totalQuestionsLimit: parseInt(savedDraft.totalQuestionsLimit) || prev.totalQuestionsLimit,
                
                // Step 5: Timing settings
                examDateTime: savedDraft.examDateTime || prev.examDateTime,
                examDuration: parseInt(savedDraft.examDuration) || prev.examDuration,
                graceTime: parseInt(savedDraft.graceTime) || prev.graceTime,
                sendInvitationEmail: savedDraft.sendInvitationEmail === '1',
                
                // Step 5: Security settings
                enableProctoring: savedDraft.enableProctoring === '1',
                captureImages: savedDraft.captureImages === '1',
                trackTabSwitching: savedDraft.trackTabSwitching === '1',
                faceDetection: savedDraft.faceDetection === '1',
                warningsAllowed: parseInt(savedDraft.warningsAllowed) || prev.warningsAllowed,
                
                // Step 5: Advanced settings
                language: savedDraft.language || prev.language || 'English',
                passingScore: [parseInt(savedDraft.passingScore) || 60],
                captureFrequency: parseInt(savedDraft.captureFrequency) || prev.captureFrequency || 10000,
                showResults: savedDraft.showResults === '1',
                allowReview: savedDraft.allowReview === '1',
                shuffleQuestions: savedDraft.shuffleQuestions !== '0',
                shuffleOptions: savedDraft.shuffleOptions !== '0',
              };
              
              return restoredData;
            });
            
            // Restore current step if saved
            if (savedDraft.currentStep) {
              setCurrentStep(parseInt(savedDraft.currentStep));
            }
            
            setHasUnsavedChanges(true);
            setDraftRestored(true); // Mark that draft was restored
            toast({
              title: "Draft Restored",
              description: `Your previous AI Module draft has been restored (Step ${savedDraft.currentStep || 1})`,
            });
          }
        } catch (error) {
          console.error('🍪 Error parsing saved draft:', error);
        }
      } else {
        console.log('🍪 No saved draft found');
      }
    }
  }, [isModal]);

  // Handle page navigation without showing browser reload dialog
  useEffect(() => {
    if (isModal && hasUnsavedChanges) {
      // Only handle programmatic navigation, not browser refresh
      const handlePopState = (e) => {
        setPendingAction('navigate');
        setShowSaveConfirmDialog(true);
        // Prevent navigation temporarily
        window.history.pushState(null, '', window.location.href);
      };

      window.addEventListener('popstate', handlePopState);
      // Push a state to handle back button
      window.history.pushState(null, '', window.location.href);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [isModal, hasUnsavedChanges]);

  // Track form changes for unsaved changes detection
  useEffect(() => {
    // Only track changes if we're in modal mode
    if (isModal) {
      const hasChanges = (
        selectedExistingCandidates.length > 0 || 
        formData.customPrompt?.trim() || 
        formData.easyQuestions > 0 || 
        formData.mediumQuestions > 0 || 
        formData.hardQuestions > 0 ||
        formData.examDateTime?.trim() ||
        (formData.examDuration && formData.examDuration !== 60) ||
        (formData.graceTime && formData.graceTime !== 60) ||
        formData.enableProctoring ||
        formData.captureImages ||
        formData.trackTabSwitching ||
        formData.faceDetection ||
        formData.warningsAllowed > 0
      );
      setHasUnsavedChanges(hasChanges);
      
      // Debug logging
      if (hasChanges) {
        console.log('🔄 AIModule: Unsaved changes detected', {
          selectedCandidates: selectedExistingCandidates.length,
          customPrompt: !!formData.customPrompt?.trim(),
          easyQuestions: formData.easyQuestions,
          mediumQuestions: formData.mediumQuestions,
          hardQuestions: formData.hardQuestions,
          examDateTime: !!formData.examDateTime?.trim(),
          examDuration: formData.examDuration,
          hasChanges
        });
      }
    } else {
      setHasUnsavedChanges(false);
    }
  }, [
    isModal, 
    selectedExistingCandidates, 
    formData.customPrompt, 
    formData.easyQuestions, 
    formData.mediumQuestions, 
    formData.hardQuestions, 
    formData.examDateTime, 
    formData.examDuration,
    formData.graceTime,
    formData.enableProctoring,
    formData.captureImages,
    formData.trackTabSwitching,
    formData.faceDetection,
    formData.warningsAllowed
  ]);

  // Removed refresh/reload handling to prevent browser dialog
  // The component now only handles navigation via popstate events

  // Cookie management functions
  const saveToCookie = () => {
    // Use a much simpler data format to avoid JSON issues
    const draftData = {
      candidateCount: selectedExistingCandidates.length,
      candidateIds: selectedExistingCandidates.map(c => c._id).join(','),
      candidateNames: selectedExistingCandidates.map(c => c.name).join('|'),
      candidateEmails: selectedExistingCandidates.map(c => c.email || '').join('|'),
      customPrompt: formData.customPrompt || '',
      easyQuestions: formData.easyQuestions || 0,
      mediumQuestions: formData.mediumQuestions || 0,
      hardQuestions: formData.hardQuestions || 0,
      totalQuestionsLimit: formData.totalQuestionsLimit || 10,
      
      // Step 5: Complete timing and security settings
      examDateTime: formData.examDateTime || '',
      examDuration: formData.examDuration || 60,
      graceTime: formData.graceTime || 60,
      sendInvitationEmail: formData.sendInvitationEmail ? '1' : '0',
      
      // Step 5: Security settings
      enableProctoring: formData.enableProctoring ? '1' : '0',
      captureImages: formData.captureImages ? '1' : '0',
      trackTabSwitching: formData.trackTabSwitching ? '1' : '0',
      faceDetection: formData.faceDetection ? '1' : '0',
      warningsAllowed: formData.warningsAllowed || 0,
      
      // Step 5: Advanced settings
      language: formData.language || 'English',
      passingScore: (formData.passingScore && formData.passingScore[0]) || 60,
      captureFrequency: formData.captureFrequency || 10000,
      showResults: formData.showResults ? '1' : '0',
      allowReview: formData.allowReview ? '1' : '0',
      shuffleQuestions: formData.shuffleQuestions !== false ? '1' : '0',
      shuffleOptions: formData.shuffleOptions !== false ? '1' : '0',
      
      currentStep: currentStep || 1,
      
      // Step 2: Resume extraction data (save completion status and AI Module ID)
      resumeExtractionComplete: resumeExtractionComplete ? '1' : '0',
      extractionAttempted: extractionAttempted ? '1' : '0',
      noResumesFound: noResumesFound ? '1' : '0',
      aiModuleId: aiModuleId || '',
      
      // Step 3: Question generation option for multiple candidates
      questionGenerationOption: questionGenerationOption || 'same',
      
      // Step 4: Questions generation status
      questionsGenerated: questionsGenerated ? '1' : '0',
      activeQuestionTab: activeQuestionTab || '',
      
      // Step 5: Active candidate tab for timing
      activeCandidateTab: activeCandidateTab || '',
      
      // Assignment cards state (Step 4)
      showAssignmentCards: showAssignmentCards ? '1' : '0',
      assignmentCardsCount: assignmentCards.length || 0,
      assignmentCardsData: JSON.stringify(assignmentCards || []),
      editingAssignmentIndex: editingAssignmentIndex !== null ? editingAssignmentIndex.toString() : '',
      showEditAssignmentModal: showEditAssignmentModal ? '1' : '0',
      
      // Candidate-specific configurations (simplified for cookie storage)
      candidateConfigs: JSON.stringify(formData.candidateConfigs || {}),
      // Individual candidate timing configurations removed - using single configuration
      questionCategories: JSON.stringify(questionCategories || {})
    };
    
    
    // Try setting with the simple approach first
    cookieUtils.setSimple(COOKIE_NAME, JSON.stringify(draftData));
    
    // Verify after a delay
    setTimeout(() => {
      const rawCookies = document.cookie;
      const cookieExists = rawCookies.includes(`${COOKIE_NAME}=`);
      
      
      if (cookieExists) {
        setHasUnsavedChanges(false);
        toast({
          title: "Draft Saved",
          description: "Your AI Module draft has been saved temporarily",
        });
      } else {
        console.error('❌ Cookie verification failed after save');
        toast({
          title: "Save Failed",
          description: "Failed to save draft. Please try again.",
          variant: "destructive",
        });
      }
    }, 500);
  };

  const clearCookie = () => {
    // Use simple deletion approach
    document.cookie = `${COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
    setHasUnsavedChanges(false);
  };

  const handleModalClose = () => {
    if (hasUnsavedChanges) {
      setPendingAction('close');
      setShowSaveConfirmDialog(true);
    } else {
      // Only clear cookie if there are no unsaved changes (meaning no draft to preserve)
      if (onClose) {
        onClose();
      }
    }
  };

  const executeClose = () => {
    // Don't clear cookie here - it should only be cleared when user chooses "Don't Save"
    // or when the process completes successfully
    if (onClose) {
      onClose();
    }
  };

  const handleSaveConfirmation = (shouldSave) => {
    if (shouldSave) {
      saveToCookie();
      // Don't clear cookie when saving - let it persist
    } else {
      clearCookie();
    }
    
    if (pendingAction === 'close') {
      executeClose();
    } else if (pendingAction === 'navigate') {
      // Handle navigation after save/don't save choice
      // Allow the navigation to proceed
      window.history.back();
    }
    // Removed refresh handling to prevent browser reload dialog
    
    setShowSaveConfirmDialog(false);
    setPendingAction(null);
  };

  // Expose methods and properties to parent component via ref
  useImperativeHandle(ref, () => ({
    hasUnsavedChanges,
    handleModalClose
  }), [hasUnsavedChanges]);

  const fetchCandidates = async (search = "") => {
    try {
      setLoadingCandidates(true);
      const response = await aiModuleAPI.getCandidatesForSelection({ search });
      setAvailableCandidates(response.data.data || []);
    } catch (error) {
      console.error("Error fetching candidates:", error);
      toast({
        title: "Error",
        description: "Failed to load candidates",
        variant: "destructive",
      });
    } finally {
      setLoadingCandidates(false);
    }
  };

  const fetchFolders = async () => {
    try {
      const response = await candidateFoldersAPI.getAll();
      setFolders(response.data.data || []);
    } catch (error) {
      console.error("Error fetching folders:", error);
    }
  };

  const handleCandidateSearch = (searchTerm) => {
    setCandidateSearch(searchTerm);
    // Debounce the search to avoid too many API calls
    clearTimeout(window.candidateSearchTimeout);
    window.candidateSearchTimeout = setTimeout(async () => {
      await fetchCandidates(searchTerm);
    }, 300);
  };

  // Auto-select candidate when only 1 result is found
  useEffect(() => {
    if (availableCandidates.length === 1 && candidateSearch.trim() !== "") {
      const singleCandidate = availableCandidates[0];
      const isAlreadySelected = selectedExistingCandidates.some(
        (c) => c._id === singleCandidate._id
      );

      if (!isAlreadySelected) {
        setSelectedExistingCandidates((prev) => [...prev, singleCandidate]);
        toast({
          title: "Auto-selected",
          description: `${singleCandidate.name} was automatically selected as the only match`,
        });
      }
    }
  }, [availableCandidates, candidateSearch, selectedExistingCandidates]);

  // Auto-trigger resume extraction when entering step 2 (but don't mark as completed)
  useEffect(() => {
    if (
      currentStep === 2 &&
      aiModuleId &&
      !extractingResume &&
      !resumeExtractionComplete &&
      !extractionAttempted &&
      formData.extractedSkills.length === 0
    ) {
      // Small delay to ensure UI is ready
      const timer = setTimeout(() => {
        extractResumeInformation();
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [
    currentStep,
    aiModuleId,
    extractingResume,
    resumeExtractionComplete,
    extractionAttempted,
    formData.extractedSkills.length,
  ]);

  // Initialize active candidate tab when entering Step 5 - only for eligible candidates
  useEffect(() => {
    if (currentStep === 5 && !activeCandidateTab) {
      const { eligible } = categorizeCandidatesByEligibility();
      if (eligible.length > 0) {
        setActiveCandidateTab(eligible[0]._id);
      }
    }
  }, [currentStep, selectedExistingCandidates, activeCandidateTab]);

  const openAddCandidateModal = () => {
    setIsAddCandidateModalOpen(true);
  };

  const handleCandidateAdded = (newCandidate) => {
    const candidateData = {
      id: newCandidate._id,
      _id: newCandidate._id,
      name: newCandidate.name,
      email: newCandidate.email,
      mobileNumber: newCandidate.mobileNumber || "",
      position: "",
      department: "",
      experienceLevel: "",
      address: newCandidate.address || "",
      folderId: newCandidate.folderId || "",
    };

    // Add to available candidates list
    setAvailableCandidates((prev) => [candidateData, ...prev]);

    // Automatically select the newly added candidate
    setSelectedExistingCandidates((prev) => {
      const isAlreadySelected = prev.some((c) => c._id === candidateData._id);
      if (!isAlreadySelected) {
        return [...prev, candidateData];
      }
      return prev;
    });

    // Close add candidate modal and reopen candidate selection modal
    setIsAddCandidateModalOpen(false);
    setTimeout(() => {
      setIsCandidateSelectionModalOpen(true);
    }, 100);

    toast({
      title: "Success",
      description: `${candidateData.name} added and selected successfully`,
    });
  };

  const steps = [
    {
      id: 1,
      title: "Candidate Information",
      icon: <Settings className="h-5 w-5" />,
    },
    {
      id: 2,
      title: "Resume Extraction",
      icon: <FileText className="h-5 w-5" />,
    },
    {
      id: 3,
      title: "Questions Generation",
      icon: <Brain className="h-5 w-5" />,
    },
    {
      id: 4,
      title: "Exam Configuration",
      icon: <Clock className="h-5 w-5" />,
    },
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle candidate-specific configuration changes
  const handleCandidateConfigChange = (candidateId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      candidateConfigs: {
        ...prev.candidateConfigs,
        [candidateId]: {
          ...prev.candidateConfigs[candidateId],
          [field]: value,
        },
      },
    }));
  };

  // Individual candidate timing configurations removed - using single configuration for all candidates

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        resumeFile: file,
      }));

      // Simulate AI extraction (in real implementation, this would call an API)
      setTimeout(() => {
        setFormData((prev) => ({
          ...prev,
          extractedSkills: ["JavaScript", "React", "Node.js", "Python", "SQL"],
          extractedExperience: "5 years of full-stack development experience",
          extractedEducation: "Bachelor's in Computer Science",
        }));
      }, 2000);
    }
  };

  // Resume upload functions
  const handleResumeUpload = async (candidateId, file) => {
    const candidate = selectedExistingCandidates.find(
      (c) => c._id === candidateId
    );
    const candidateName = candidate?.name || "candidate";

    try {
      setUploadingResumes((prev) => new Set([...prev, candidateId]));

      // Step 1: Upload resume file to database
      toast({
        title: "Uploading Resume",
        description: `Uploading resume file for ${candidateName}...`,
      });

      const response = await aiModuleAPI.uploadCandidateResume(
        aiModuleId,
        candidateId,
        file
      );

      if (response.data.success) {
        toast({
          title: "Upload Complete",
          description: `Resume uploaded successfully for ${candidateName}. Starting analysis...`,
        });

        // Clear the file from temporary storage since it's now uploaded to database
        setCandidateResumeFiles((prev) => {
          const newMap = new Map(prev);
          newMap.delete(candidateId);
          return newMap;
        });

        // Clear the file input
        const fileInput = document.getElementById(
          `resume-upload-${candidateId}`
        );
        if (fileInput) {
          fileInput.value = null;
        }

        // Step 2: Analyze the uploaded resume from database
        // Small delay to ensure database upload is complete
        setTimeout(async () => {
          toast({
            title: "Analyzing Resume",
            description: `Extracting information from ${candidateName}'s resume...`,
          });

          const analysisSuccess = await extractSingleCandidateResume(
            candidateId
          );

          // Clear uploading state after analysis is complete with timeout to prevent unmount issues
          setTimeout(() => {
            setUploadingResumes((prev) => {
              const newSet = new Set(prev);
              newSet.delete(candidateId);
              return newSet;
            });
          }, 0);

          if (analysisSuccess) {
            toast({
              title: "Analysis Complete",
              description: `Resume analysis completed for ${candidateName}. Skills and experience extracted successfully.`,
            });
          }
        }, 1500); // Increased delay to ensure database operations complete
      }
    } catch (error) {
      console.error("Error uploading resume:", error);
      toast({
        title: "Upload Failed",
        description:
          error.response?.data?.message ||
          `Failed to upload resume for ${candidateName}`,
        variant: "destructive",
      });

      // Remove from uploading state on error with timeout to prevent unmount issues
      setTimeout(() => {
        setUploadingResumes((prev) => {
          const newSet = new Set(prev);
          newSet.delete(candidateId);
          return newSet;
        });
      }, 0);
    }
  };

  const handleResumeFileChange = (candidateId, event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        toast({
          title: "Invalid File",
          description: "Only PDF files are allowed for resume",
          variant: "destructive",
        });
        event.target.value = null;
        return;
      }

      // Store the file temporarily
      setCandidateResumeFiles((prev) => {
        const newMap = new Map(prev);
        newMap.set(candidateId, file);
        return newMap;
      });

      // Find candidate name for better feedback
      const candidate = selectedExistingCandidates.find(
        (c) => c._id === candidateId
      );
      toast({
        title: "File Selected",
        description: `Resume file selected for ${
          candidate?.name || "candidate"
        }. Click "Upload & Analyze" to proceed.`,
      });
    }
  };

  const removeResumeFile = (candidateId) => {
    setCandidateResumeFiles((prev) => {
      const newMap = new Map(prev);
      newMap.delete(candidateId);
      return newMap;
    });

    // Clear the file input
    const fileInput = document.getElementById(`resume-upload-${candidateId}`);
    if (fileInput) {
      fileInput.value = null;
    }
  };

  const uploadAllResumes = async () => {
    const uploadPromises = [];

    for (const [candidateId, file] of candidateResumeFiles.entries()) {
      uploadPromises.push(handleResumeUpload(candidateId, file));
    }

    await Promise.all(uploadPromises);
    setCandidateResumeFiles(new Map()); // Clear all files after upload
    setShowUploadSection(false);
  };

  const extractResumeInformation = async (forceReAnalysis = false) => {
    try {
      setExtractingResume(true);
      setExtractionAttempted(true);
      setNoResumesFound(false);

      if (!aiModuleId) {
        toast({
          title: "Error",
          description: "AI Module ID not found. Please save Step 1 first.",
          variant: "destructive",
        });
        return false;
      }

      const response = await aiModuleAPI.extractResumeFromCandidates({
        aiModuleId,
        forceReAnalysis,
      });

      if (response.data.success) {
        const extractedData = response.data.data.resumeExtraction;
        const { candidatesProcessed, totalCandidates, fromCache } =
          response.data.data;

        // Use functional updates to prevent stale state issues
        setFormData((prev) => ({
          ...prev,
          extractedSkills: extractedData.extractedSkills || [],
          extractedExperience: extractedData.extractedExperience || "",
          extractedEducation: extractedData.extractedEducation || "",
          candidateResumeData: extractedData.candidateResumeData || [],
          extractionDate: extractedData.extractionDate,
        }));

        // Log genuinity analysis for all candidates
        if (extractedData.candidateResumeData && extractedData.candidateResumeData.length > 0) {
          extractedData.candidateResumeData.forEach((candidateData, index) => {
            if (candidateData.genuinityAnalysis) {
              
              if (candidateData.genuinityAnalysis.categoryBreakdown && 
                  candidateData.genuinityAnalysis.categoryBreakdown.length > 0) {
                candidateData.genuinityAnalysis.categoryBreakdown.forEach((category) => {
                  console.log(`      - ${category.category}: ${category.score}% (${category.status})`);
                });
              }
            } else {
              console.log(`\n${index + 1}. ${candidateData.candidateName}: No genuinity analysis available`);
            }
          });
        }

        setResumeExtractionComplete(true);

        // Show appropriate success message based on processing results
        if (fromCache) {
          toast({
            title: "Analysis Retrieved",
            description:
              "Using previously analyzed resume data. No changes detected in candidate selection.",
          });
        } else if (candidatesProcessed === totalCandidates) {
          toast({
            title: "Success",
            description: `Resume extraction completed for all ${candidatesProcessed} candidates`,
          });
        } else if (candidatesProcessed > 0) {
          toast({
            title: "Partial Success",
            description: `Resume extraction completed for ${candidatesProcessed} out of ${totalCandidates} candidates. Some candidates may not have resume files.`,
            variant: "default",
          });
        } else {
          setNoResumesFound(true);
          // Don't show toast here - we'll show it in the UI instead
          return false;
        }

        return true;
      }
    } catch (error) {
      console.error("Error extracting resume information:", error);

      // Check if it's a "no resumes found" error (various patterns)
      const errorMessage = error.response?.data?.message || error.message || "";
      const isNoResumeError =
        errorMessage.includes("No resume files were found") ||
        errorMessage.includes("No resumes found") ||
        errorMessage.includes("no resume") ||
        errorMessage.includes("resume not found") ||
        errorMessage.includes("ensure candidates have uploaded resume files") ||
        (error.response?.status === 400 && errorMessage.includes("candidates"));

      if (isNoResumeError) {
        setNoResumesFound(true);
        // Don't show error toast for no resume case - let the UI handle it
      } else {
        toast({
          title: "Error",
          description: errorMessage || "Failed to extract resume information",
          variant: "destructive",
        });
      }
      return false;
    } finally {
      // Use setTimeout to prevent state updates during unmount
      setTimeout(() => {
        setExtractingResume(false);
      }, 0);
    }
  };

  // New function for extracting resume information with pre-extracted data
  const extractResumeInformationWithPreExtractedData = async (preExtractedData) => {
    try {
      setExtractingResume(true);
      setExtractionAttempted(true);
      setNoResumesFound(false);

      if (!aiModuleId) {
        toast({
          title: "Error",
          description: "AI Module ID not found. Please save Step 1 first.",
          variant: "destructive",
        });
        return false;
      }


      const response = await aiModuleAPI.extractResumeFromCandidates({
        aiModuleId,
        forceReAnalysis: false,
        preExtractedData: preExtractedData
      });

      if (response.data.success) {
        const extractedData = response.data.data.resumeExtraction;
        const { candidatesProcessed, totalCandidates, fromCache } =
          response.data.data;

        // Use functional updates to prevent stale state issues
        setFormData((prev) => ({
          ...prev,
          extractedSkills: extractedData.extractedSkills || [],
          extractedExperience: extractedData.extractedExperience || "",
          extractedEducation: extractedData.extractedEducation || "",
          candidateResumeData: extractedData.candidateResumeData || [],
          extractionDate: extractedData.extractionDate,
        }));

        setResumeExtractionComplete(true);

        toast({
          title: "Success",
          description: `Resume data saved for ${preExtractedData.length} candidate(s) without re-analysis`,
        });

        return true;
      }
    } catch (error) {
      console.error("Error saving pre-extracted resume data:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to save resume data",
        variant: "destructive",
      });
      return false;
    } finally {
      setTimeout(() => {
        setExtractingResume(false);
      }, 0);
    }
  };

  // New function for extracting resume information for a specific candidate only
  const extractSingleCandidateResume = async (candidateId) => {
    try {
      if (!aiModuleId) {
        toast({
          title: "Error",
          description: "AI Module ID not found. Please save Step 1 first.",
          variant: "destructive",
        });
        return false;
      }

      const candidate = selectedExistingCandidates.find(
        (c) => c._id === candidateId
      );
      if (!candidate) {
        toast({
          title: "Error",
          description: "Candidate not found",
          variant: "destructive",
        });
        return false;
      }

      // Check if this candidate already has resume data
      const existingCandidateData = formData.candidateResumeData?.find(
        (data) => data.candidateId === candidateId
      );

      if (existingCandidateData) {
        // Candidate already has data, no need to re-analyze
        toast({
          title: "Already Analyzed",
          description: `${candidate.name} already has analyzed resume data`,
        });
        return true;
      }

      // Call the extraction API with specific candidate ID to process only this candidate
      const response = await aiModuleAPI.extractResumeFromCandidates({
        aiModuleId,
        forceReAnalysis: false, // Don't force re-analysis to preserve existing data
        specificCandidateIds: [candidateId], // NEW: Process only this specific candidate
      });

      if (response.data.success) {
        const extractedData = response.data.data.resumeExtraction;

        // Debug: Log the extracted data to verify structure

        // Debug: Log all candidates' dataProvidedBy values received from backend
        extractedData.candidateResumeData?.forEach((candidateData) => {
          console.log(
            `  ${candidateData.candidateName}: "${candidateData.dataProvidedBy}"`
          );
        });

        // Find the data for our specific candidate from the API response
        const targetCandidateData = extractedData.candidateResumeData?.find(
          (data) => data.candidateId === candidateId
        );

        if (targetCandidateData) {
          // Verify the target candidate has the required fields for Visualize button
          const hasRequiredFields =
            targetCandidateData.aiParsedData &&
            targetCandidateData.aiParsedData.skills &&
            targetCandidateData.dataProvidedBy === "OpenAI ChatGPT";


          // Update our form data with the complete extracted data from backend
          // The backend now handles merging existing data with new candidate data
          setFormData((prev) => {
            // Verify that existing candidates' data is preserved
            const existingCandidates = prev.candidateResumeData || [];
            const updatedCandidates = extractedData.candidateResumeData || [];

            // Log preservation check
            existingCandidates.forEach((existingCandidate) => {
              if (existingCandidate.candidateId !== candidateId) {
                const updatedVersion = updatedCandidates.find(
                  (c) => c.candidateId === existingCandidate.candidateId
                );
                if (updatedVersion) {
                  // More detailed comparison
                  const originalDataProvidedBy =
                    existingCandidate.dataProvidedBy;
                  const updatedDataProvidedBy = updatedVersion.dataProvidedBy;
                  const originalHasAiParsedData =
                    !!existingCandidate.aiParsedData;
                  const updatedHasAiParsedData = !!updatedVersion.aiParsedData;
                  const originalHasSkills =
                    !!existingCandidate.aiParsedData?.skills;
                  const updatedHasSkills =
                    !!updatedVersion.aiParsedData?.skills;

                  // Check if skills structure is preserved
                  const originalSkillsKeys = existingCandidate.aiParsedData
                    ?.skills
                    ? Object.keys(existingCandidate.aiParsedData.skills)
                    : [];
                  const updatedSkillsKeys = updatedVersion.aiParsedData?.skills
                    ? Object.keys(updatedVersion.aiParsedData.skills)
                    : [];
                  const skillsStructurePreserved =
                    JSON.stringify(originalSkillsKeys.sort()) ===
                    JSON.stringify(updatedSkillsKeys.sort());

                  const isPreserved =
                    originalDataProvidedBy === updatedDataProvidedBy &&
                    originalHasAiParsedData === updatedHasAiParsedData &&
                    originalHasSkills === updatedHasSkills &&
                    skillsStructurePreserved;

                  // If not preserved, log the differences
                  if (!isPreserved) {
                    console.error(
                      `❌ PRESERVATION FAILED for ${existingCandidate.candidateName}:`,
                      {
                        dataProvidedByChanged:
                          originalDataProvidedBy !== updatedDataProvidedBy,
                        aiParsedDataChanged:
                          originalHasAiParsedData !== updatedHasAiParsedData,
                        skillsChanged: originalHasSkills !== updatedHasSkills,
                        skillsStructureChanged: !skillsStructurePreserved,
                      }
                    );
                  }
                }
              }
            });

            return {
              ...prev,
              extractedSkills:
                extractedData.extractedSkills || prev.extractedSkills || [],
              extractedExperience:
                extractedData.extractedExperience ||
                prev.extractedExperience ||
                "",
              extractedEducation:
                extractedData.extractedEducation ||
                prev.extractedEducation ||
                "",
              candidateResumeData:
                extractedData.candidateResumeData ||
                prev.candidateResumeData ||
                [],
              extractionDate:
                extractedData.extractionDate || prev.extractionDate,
            };
          });

          // Log genuinity analysis details for the newly extracted candidate
          if (targetCandidateData.genuinityAnalysis) {

            if (targetCandidateData.genuinityAnalysis.categoryBreakdown && 
                targetCandidateData.genuinityAnalysis.categoryBreakdown.length > 0) {
              targetCandidateData.genuinityAnalysis.categoryBreakdown.forEach((category, index) => {
                console.log(`\n${index + 1}. ${category.category} – ${category.score}%`);

              });
            } else if (targetCandidateData.genuinityAnalysis.analysis) {
              // Fallback to old format
              Object.entries(targetCandidateData.genuinityAnalysis.analysis).forEach(([key, value], index) => {
                const categoryName = key.replace(/([A-Z])/g, ' $1').trim();
              });
            }
            
            if (targetCandidateData.genuinityAnalysis.calculationReasoning) {
              console.log(`\n📐 Calculation Reasoning:`);
              console.log(`   ${targetCandidateData.genuinityAnalysis.calculationReasoning}`);
            }
            
            targetCandidateData.genuinityAnalysis.positiveIndicators?.forEach((indicator, index) => {
              console.log(`   ${index + 1}. ${indicator}`);
            });
            
            if (targetCandidateData.genuinityAnalysis.redFlags && 
                targetCandidateData.genuinityAnalysis.redFlags.length > 0) {
              targetCandidateData.genuinityAnalysis.redFlags.forEach((flag, index) => {
                console.log(`   ${index + 1}. ${flag}`);
              });
            } else {
              console.log(`\n✅ No Red Flags Detected`);
            }
            
          }

          setResumeExtractionComplete(true);

          return true;
        } else {
          toast({
            title: "Analysis Failed",
            description: `Could not extract information from ${candidate.name}'s resume. The file may be corrupted or in an unsupported format.`,
            variant: "destructive",
          });
          return false;
        }
      }
    } catch (error) {
      console.error("Error extracting single candidate resume:", error);

      // Check if it's a "no resumes found" error
      const errorMessage = error.response?.data?.message || error.message || "";
      const isNoResumeError =
        errorMessage.includes("No resume files were found") ||
        errorMessage.includes("No resumes found") ||
        errorMessage.includes("no resume") ||
        errorMessage.includes("resume not found");

      if (isNoResumeError) {
        toast({
          title: "Resume Not Found",
          description: `Resume file not found in database for ${
            candidate?.name || "candidate"
          }. Please try uploading again.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Analysis Error",
          description:
            errorMessage ||
            `Failed to analyze resume for ${candidate?.name || "candidate"}`,
          variant: "destructive",
        });
      }
      return false;
    }
  };

  const nextStep = async () => {
    
    // Show confirmation dialog when completing the final step
    if (currentStep === totalSteps) {
      setPendingCompletionAction('nextStep');
      setShowCompletionConfirmDialog(true);
      return; // Wait for user confirmation
    }

    await executeNextStep();
  };

  // Execute the actual next step logic
  const executeNextStep = async () => {
    let saveSuccess = false;

    if (currentStep === 1) {
      let candidatesToSave = [...selectedExistingCandidates];
      
      // Process resumes first if any are uploaded
      if (multipleResumeFiles.length > 0) {
        const { success, createdCandidates } = await processResumesAndCreateCandidates();
        
        
        if (!success) {
          console.error('❌ Resume processing failed, stopping');
          return;
        }

        // Add created candidates to the list
        if (createdCandidates.length > 0) {
          
          // Mark these candidates with their AI parsed data to skip re-extraction
          const candidatesWithAIData = createdCandidates.map(candidate => {
            const hasAIData = !!(candidate.aiParsedData && candidate.extractedText);
            
            return {
              ...candidate,
              _hasAIParsedData: hasAIData, // Flag to indicate AI data is already available
              _aiParsedData: candidate.aiParsedData, // Store the AI parsed data
              _extractedText: candidate.extractedText // Store extracted text
            };
          });
          
          candidatesToSave = [...candidatesToSave, ...candidatesWithAIData];
          setSelectedExistingCandidates(candidatesToSave);
        }
        
        // Clear the resume files after processing
        setMultipleResumeFiles([]);
      }

      // Check if we have any candidates to proceed
      if (candidatesToSave.length === 0) {
        console.warn('⚠️ No candidates to save');
        toast({
          title: "Validation Error",
          description: "Please select at least one candidate or upload resume(s)",
          variant: "destructive",
        });
        return;
      }

      
      // Check if any candidates have pre-extracted AI data
      const candidatesWithAIData = candidatesToSave.filter(c => c._hasAIParsedData);
      
      // Save Step 1 with the updated candidates list
      saveSuccess = await saveStep1WithCandidates(candidatesToSave);

      if (saveSuccess) {
        
        // Get the current aiModuleId (it should be set by saveStep1WithCandidates)
        // Wait a bit for state to update
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // If we have pre-extracted data, save it to the AI Module before moving to Step 2
        if (candidatesWithAIData.length > 0) {
          
          if (!aiModuleId) {
            console.error('❌ aiModuleId is not set, cannot save pre-extracted data');
          } else {
            // Prepare pre-extracted data for backend
            const preExtractedData = candidatesWithAIData.map(candidate => {
              const aiData = candidate._aiParsedData;
              
              // Extract all skills from different categories
              const allSkills = [
                ...(aiData.skills?.frontend || []),
                ...(aiData.skills?.backend || []),
                ...(aiData.skills?.database || []),
                ...(aiData.skills?.tools || []),
                ...(aiData.skills?.other || [])
              ];
              
              // Collect experience text
              const experienceText = aiData.experience?.details?.length > 0 
                ? aiData.experience.details.map(exp => `${exp.role || 'Role'} at ${exp.company || 'Company'}`).join('; ')
                : `${aiData.experience?.totalExperience?.years || 0} years, ${aiData.experience?.totalExperience?.months || 0} months of experience`;
              
              // Collect education text
              const educationText = aiData.education?.length > 0
                ? aiData.education.map(edu => `${edu.qualification || 'Degree'} in ${edu.specialization || 'Field'} from ${edu.institution || 'Institution'}`).join('; ')
                : 'Education details extracted';
              
              return {
                candidateId: candidate._id,
                candidateName: candidate.name,
                skills: allSkills,
                experience: experienceText,
                education: educationText,
                personalInfo: {
                  name: aiData.candidate?.fullName || candidate.name,
                  email: aiData.candidate?.contact?.email || candidate.email || '',
                  phone: aiData.candidate?.contact?.phone || candidate.mobileNumber || '',
                  linkedIn: aiData.candidate?.contact?.linkedin || ''
                },
                aiParsedData: aiData
              };
            });
            
            try {
              // Save pre-extracted data to AI Module
              const response = await aiModuleAPI.extractResumeFromCandidates({
                aiModuleId,
                forceReAnalysis: false,
                preExtractedData: preExtractedData
              });
              
              if (response.data.success) {
                console.log('✅ Pre-extracted data saved successfully');
              } else {
                console.error('❌ Failed to save pre-extracted data:', response.data);
              }
            } catch (error) {
              console.error('❌ Error saving pre-extracted data:', error);
              console.error('Error details:', error.response?.data || error.message);
            }
          }
        }
        
        // Move to step 2
        setCurrentStep(2);
      } else {
        console.error('❌ Failed to save Step 1');
      }
      return; // Exit early since we handled navigation manually
    } else if (currentStep === 2) {
      saveSuccess = await saveStep2();
    } else if (currentStep === 3) {
      saveSuccess = await saveStep3();
    } else if (currentStep === 4) {
      
      // If assignment cards are shown, create assignments directly
      if (showAssignmentCards && assignmentCards.length > 0) {
        saveSuccess = await executeConfirmAssignments();
      } else {
        saveSuccess = await saveStep4();
      }
    } else if (currentStep === 5) {
      saveSuccess = await saveStep5();
    }

    if (!saveSuccess) {
      return;
    }


    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      console.log('🏁 REACHED FINAL STEP - NO MORE STEPS TO PROCEED');
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const saveStep1 = async () => {
    return saveStep1WithCandidates(selectedExistingCandidates);
  };

  const saveStep1WithCandidates = async (candidatesToSave) => {
    try {
      setLoading(true);

      // Validate selection - allow either existing candidates OR uploaded resumes
      if (candidatesToSave.length === 0) {
        toast({
          title: "Validation Error",
          description: "Please select at least one candidate or upload resume(s)",
          variant: "destructive",
        });
        return false;
      }

      const candidateSelection = {
        type: "existing",
        existingCandidates: candidatesToSave.map((c) => c._id),
      };

      const response = await aiModuleAPI.saveStep1({
        candidateSelection,
        aiModuleId,
        jobDescription: formData.jobDescription, // Pass job description data
      });

      if (response.data.success) {
        setAiModuleId(response.data.data.aiModuleId);

        // Update completed steps from backend response
        if (response.data.data.completedSteps) {
          setCompletedSteps(new Set(response.data.data.completedSteps));
        }

        // If candidate selection changed, mark for re-extraction but don't clear existing data yet
        // The backend will preserve data for unchanged candidates
        if (response.data.data.candidateSelectionChanged) {
          setResumeExtractionComplete(false);
          setExtractionAttempted(false);
          setNoResumesFound(false);
          // Don't clear candidateResumeData here - let the backend preserve it
          // Only clear the aggregated fields that need recalculation
          setFormData((prev) => ({
            ...prev,
            extractedSkills: [],
            extractedExperience: "",
            extractedEducation: "",
            // Keep candidateResumeData - it will be updated from backend on next load
            extractionDate: null,
          }));

          toast({
            title: "Success",
            description:
              "Step 1 saved successfully. Resume analysis will be updated due to candidate changes.",
          });
        } else {
          toast({
            title: "Success",
            description: "Step 1 saved successfully",
          });
        }

        // Return the aiModuleId so it can be used immediately
        return { success: true, aiModuleId: response.data.data.aiModuleId };
      }
      return { success: false };
    } catch (error) {
      console.error("Error saving Step 1:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to save Step 1",
        variant: "destructive",
      });
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const saveStep2 = async () => {
    try {
      setLoading(true);

      // If extraction is still in progress, wait for it to complete
      if (extractingResume) {
        toast({
          title: "Please Wait",
          description:
            "Resume extraction is in progress. Please wait for it to complete.",
          variant: "default",
        });
        return false;
      }

      // If no extraction data is available and extraction hasn't been attempted, trigger it
      if (!resumeExtractionComplete && formData.extractedSkills.length === 0) {
        toast({
          title: "Starting Resume Analysis",
          description:
            "Resume extraction will start automatically. Please wait a moment.",
          variant: "default",
        });

        // Trigger extraction and wait for it (don't force re-analysis)
        const extractionSuccess = await extractResumeInformation(false);
        if (!extractionSuccess) {
          return false;
        }
      }

      const resumeExtractionData = {
        extractedSkills: formData.extractedSkills,
        extractedExperience: formData.extractedExperience,
        extractedEducation: formData.extractedEducation,
        candidateResumeData: formData.candidateResumeData,
        extractionDate: formData.extractionDate,
      };

      const response = await aiModuleAPI.saveStep2({
        aiModuleId,
        resumeExtraction: resumeExtractionData,
      });

      if (response.data.success) {
        // Update completed steps from backend response
        if (response.data.data.completedSteps) {
          setCompletedSteps(new Set(response.data.data.completedSteps));
        }

        toast({
          title: "Success",
          description: "Step 2 saved successfully",
        });
        return true;
      }
    } catch (error) {
      console.error("Error saving Step 2:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to save Step 2",
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const saveStep3 = async () => {
    try {
      setLoading(true);

      // Validate that all candidates have required configurations
      const validationErrors = [];

      // Check if there are any existing questions (manual or generated)
      const existingQuestionsCheck = checkForExistingManualQuestions();
      const hasAnyQuestions = existingQuestionsCheck.hasManualQuestions;

      // If no existing questions, validate skill configuration for generation
      if (!hasAnyQuestions) {
        // Calculate total questions from skill configuration
        let totalQuestionsFromSkills = 0;
        if (formData.jobDescription?.skills && generateQuestionsFormData.skillQuestions) {
          totalQuestionsFromSkills = formData.jobDescription.skills.reduce((sum, skill) => {
            const skillConfig = generateQuestionsFormData.skillQuestions[skill];
            return sum + (skillConfig?.easy || 0) + (skillConfig?.medium || 0) + (skillConfig?.hard || 0);
          }, 0);
        }

        // For skill-based questions, validate the skill configuration only if no existing questions
        if (totalQuestionsFromSkills === 0) {
          validationErrors.push("Please configure at least one question across all skills or add questions manually");
        }

        if (totalQuestionsFromSkills !== (generateQuestionsFormData.totalQuestions || 10)) {
          validationErrors.push(
            `Sum of questions per skill (${totalQuestionsFromSkills}) must equal total questions (${generateQuestionsFormData.totalQuestions || 10})`
          );
        }
      } else {
        console.log(`✅ Found ${existingQuestionsCheck.count} existing questions, skipping skill configuration validation`);
      }

      // Validate that we have eligible candidates (but allow proceeding with warnings)
      const { eligible: eligibleCandidates } = categorizeCandidatesByEligibility();
      if (eligibleCandidates.length === 0) {
        validationErrors.push("⚠️ No eligible candidates found. You can still configure exam settings, but only eligible candidates will receive exam assignments. Please check skills matching and experience requirements.");
      }

      if (validationErrors.length > 0) {
        toast({
          title: "Validation Error",
          description: validationErrors.join(", "),
          variant: "destructive",
        });
        return false;
      }

      // Prepare candidate-specific exam configurations for ELIGIBLE candidates only
      const examConfigurationArray = eligibleCandidates.map(
        (candidate) => {
          const candidateData = formData.candidateResumeData?.find(
            (data) => data.candidateId === candidate._id
          );

          // Extract experience details from AI parsed data
          const aiParsedData = candidateData?.aiParsedData || {};
          const experienceYears =
            aiParsedData.experience?.totalExperience?.years || 0;
          const experienceMonths =
            aiParsedData.experience?.totalExperience?.months || 0;

          // Prepare experience details for database storage
          const experienceDetails = {
            years: experienceYears,
            months: experienceMonths,
            isFresher: experienceYears === 0 && experienceMonths === 0,
          };

          // Use JD skills instead of candidate skills for question generation
          const jobDescriptionSkills = formData.jobDescription?.skills || [];

          // Calculate difficulty distribution from skill-based configuration OR existing questions
          let easyQuestions = 0, mediumQuestions = 0, hardQuestions = 0, totalQuestionCount = 0;
          
          if (hasAnyQuestions) {
            // If existing questions are present, calculate from actual questions
            const candidateQuestions = generatedQuestions.find(gq => gq.candidateId === candidate._id);
            if (candidateQuestions && candidateQuestions.questions) {
              candidateQuestions.questions.forEach(q => {
                if (q.difficulty === 'Easy') easyQuestions++;
                else if (q.difficulty === 'Medium') mediumQuestions++;
                else if (q.difficulty === 'Hard') hardQuestions++;
              });
              totalQuestionCount = candidateQuestions.questions.length;
            }
            
            // If no questions found for this candidate but others have questions, set minimum valid values
            if (totalQuestionCount === 0) {
              easyQuestions = 1; // Set minimum to satisfy schema validation
              totalQuestionCount = 1;
            }
          } else {
            // Use skill-based configuration for new question generation
            if (formData.jobDescription?.skills && generateQuestionsFormData.skillQuestions) {
              formData.jobDescription.skills.forEach(skill => {
                const skillConfig = generateQuestionsFormData.skillQuestions[skill];
                if (skillConfig) {
                  easyQuestions += skillConfig.easy || 0;
                  mediumQuestions += skillConfig.medium || 0;
                  hardQuestions += skillConfig.hard || 0;
                }
              });
              totalQuestionCount = easyQuestions + mediumQuestions + hardQuestions;
            }
          }

          return {
            candidateId: candidate._id,
            candidateName: candidate.name,
            extractedSkills: jobDescriptionSkills,
            extractedExperience: candidateData?.experience || "",
            experienceDetails: experienceDetails,
            easyQuestions: easyQuestions,
            mediumQuestions: mediumQuestions,
            hardQuestions: hardQuestions,
            questionCount: Math.max(totalQuestionCount, 1), // Ensure minimum of 1 for schema validation
            totalQuestionsLimit: generateQuestionsFormData.totalQuestions || Math.max(totalQuestionCount, 10),
            prompt: null, // No custom prompt in skill-based version
            skillQuestions: generateQuestionsFormData.skillQuestions || {}, // Include skill-specific configuration
          };
        }
      );

      const response = await aiModuleAPI.saveStep3({
        aiModuleId,
        examConfiguration: examConfigurationArray,
        questionGenerationOption: eligibleCandidates.length > 1 ? questionGenerationOption : "same",
      });

      if (response.data.success) {
        // Update completed steps from backend response
        if (response.data.data.completedSteps) {
          setCompletedSteps(new Set(response.data.data.completedSteps));
        }

        toast({
          title: "Success",
          description: "Step 3 saved successfully",
        });
        return true;
      }
    } catch (error) {
      console.error("Error saving Step 3:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to save Step 3",
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const saveStep4 = async () => {
    try {
      setLoading(true);

      // Validate that questions exist (either generated or manual)
      const existingQuestionsCheck = checkForExistingManualQuestions();
      if (!questionsGenerated && generatedQuestions.length === 0 && !existingQuestionsCheck.hasManualQuestions) {
        toast({
          title: "Validation Error",
          description: "Please generate questions or add manual questions first before saving",
          variant: "destructive",
        });
        return false;
      }

      // Validate that all candidates have categories
      const missingCategories = [];
      for (const candidateQuestions of generatedQuestions) {
        const category = questionCategories[candidateQuestions.candidateId];
        if (!category || category.trim() === "") {
          missingCategories.push(candidateQuestions.candidateName);
        }
      }

      if (missingCategories.length > 0) {
        toast({
          title: "Validation Error",
          description: `Please enter categories for: ${missingCategories.join(
            ", "
          )}`,
          variant: "destructive",
        });
        return false;
      }

      // Show confirmation alert before proceeding
      setShowWarningDialog(true);
      return false; // Don't proceed yet, wait for dialog confirmation
    } catch (error) {
      console.error("Error in saveStep4:", error);
      toast({
        title: "Error",
        description: "An error occurred while preparing to save Step 4",
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const confirmSaveStep4 = async () => {
    try {
      setLoading(true);
      setShowWarningDialog(false);

      // Prepare questions with categories for storage
      const questionsWithCategories = generatedQuestions.map(
        (candidateQuestions) => ({
          candidateId: candidateQuestions.candidateId,
          candidateName: candidateQuestions.candidateName,
          category: questionCategories[candidateQuestions.candidateId].trim(),
          questions: candidateQuestions.questions || [],
        })
      );

      const response = await aiModuleAPI.saveStep4({
        aiModuleId,
        questionsWithCategories,
      });

      if (response.data.success) {
        // Update completed steps from backend response
        if (response.data.data.completedSteps) {
          setCompletedSteps(new Set(response.data.data.completedSteps));
        }

        toast({
          title: "Success",
          description: `Step 4 saved successfully. ${response.data.data.questionsStored} questions stored for ${response.data.data.candidatesProcessed} candidates. Questions are now grouped by candidate name.`,
        });

        // Move to next step after successful save
        if (currentStep < totalSteps) {
          setCurrentStep(currentStep + 1);
        }

        return true;
      }
    } catch (error) {
      console.error("Error saving Step 4:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to save Step 4",
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Handle configuration save from ConfigurePopup
  const handleConfigurationSave = async (configData) => {
    
    try {
      
      // Update form data with configuration
      setFormData(prev => ({
        ...prev,
        ...configData.formData
      }));

      // Check if this is preview mode (just showing assignment cards)
      if (configData.isPreview) {
        
        setAssignmentCards(configData.assignments);
        setShowAssignmentCards(true);
        setShowConfigurePopup(false);
        
        toast({
          title: "Schedule Generated",
          description: `Generated schedule for ${configData.assignments.length} candidates. Review and confirm below.`,
        });
        
        return;
      }

      // Create batch exam assignments if assignments are configured
      if (configData.assignments && configData.assignments.length > 0) {
        setLoading(true);
        
        try {
          const response = await aiModuleAPI.createBatchExamAssignments({
            aiModuleId,
            assignments: configData.assignments,
            configurationData: configData.formData
          });


          if (response.data.success) {
            toast({
              title: "Exam Assignments Created",
              description: `Successfully created ${response.data.data.assignmentsCreated} exam assignments`,
            });

            // Clear any stored configuration and assignment cards
            localStorage.removeItem('examAssignmentConfig');
            setAssignmentCards([]);
            setShowAssignmentCards(false);
            
            // Close modal and potentially navigate away or refresh
            setShowConfigurePopup(false);
            
            // Optionally close the AI Module modal if this was the final step
            if (onClose) {
              onClose();
            }
          }
        } catch (error) {
          console.error('💥 ERROR CREATING BATCH ASSIGNMENTS:', error);
          toast({
            title: "Error",
            description: error.response?.data?.message || "Failed to create exam assignments",
            variant: "destructive",
          });
        } finally {
          setLoading(false);
        }
      } else {
        // Just save configuration without creating assignments
        const assignmentConfig = {
          configurationData: configData.formData
        };
        
        localStorage.setItem('examAssignmentConfig', JSON.stringify(assignmentConfig));
        
        toast({
          title: "Configuration Saved",
          description: "Exam configuration saved successfully",
        });
        
        setShowConfigurePopup(false);
      }

    } catch (error) {
      console.error('Error saving configuration:', error);
      toast({
        title: "Error",
        description: "Failed to save configuration",
        variant: "destructive",
      });
    }
  };

  // Handle assignment card editing
  const handleEditAssignmentCard = (index) => {
    const cardToEdit = assignmentCards[index];
    
    // Set up edit data with current card values
    setEditAssignmentData({
      examDate: cardToEdit.examDate,
      examTime: cardToEdit.examTime,
      duration: cardToEdit.duration,
      graceTime: cardToEdit.graceTime,
      enableProctoring: cardToEdit.enableProctoring || false,
      captureImages: cardToEdit.captureImages || false,
      trackTabSwitching: cardToEdit.trackTabSwitching || false,
      faceDetection: cardToEdit.faceDetection || false,
      warningsAllowed: cardToEdit.warningsAllowed || 0,
      language: cardToEdit.language || 'English',
      passingScore: cardToEdit.passingScore || 60,
      captureFrequency: cardToEdit.captureFrequency || 10,
    });
    
    setEditingAssignmentIndex(index);
    setShowEditAssignmentModal(true);
  };

  // Handle saving edited assignment card
  const handleSaveEditedAssignment = () => {
    if (editingAssignmentIndex === null) return;
    
    const updatedCards = [...assignmentCards];
    const originalCard = updatedCards[editingAssignmentIndex];
    
    // Update the card with new values
    updatedCards[editingAssignmentIndex] = {
      ...originalCard,
      examDate: editAssignmentData.examDate,
      examTime: editAssignmentData.examTime,
      examDateTime: `${editAssignmentData.examDate}T${editAssignmentData.examTime}:00.000Z`,
      duration: parseInt(editAssignmentData.duration),
      graceTime: parseInt(editAssignmentData.graceTime),
      enableProctoring: editAssignmentData.enableProctoring,
      captureImages: editAssignmentData.captureImages,
      trackTabSwitching: editAssignmentData.trackTabSwitching,
      faceDetection: editAssignmentData.faceDetection,
      warningsAllowed: parseInt(editAssignmentData.warningsAllowed),
      language: editAssignmentData.language,
      passingScore: parseInt(editAssignmentData.passingScore),
      captureFrequency: parseInt(editAssignmentData.captureFrequency),
    };
    
    setAssignmentCards(updatedCards);
    setShowEditAssignmentModal(false);
    setEditingAssignmentIndex(null);
    setEditAssignmentData({});
    
    toast({
      title: "Assignment Updated",
      description: `Updated exam assignment for ${originalCard.candidateName}`,
    });
  };

  // Handle canceling assignment edit
  const handleCancelEditAssignment = () => {
    setShowEditAssignmentModal(false);
    setEditingAssignmentIndex(null);
    setEditAssignmentData({});
  };

  // Handle confirming and saving assignment cards
  const handleConfirmAssignments = async () => {
    
    // Prevent double execution
    if (loading) {
      console.log('⚠️ ALREADY PROCESSING - IGNORING DUPLICATE CALL');
      return false;
    }
    
    // Show confirmation dialog before proceeding
    setPendingCompletionAction('confirmAssignments');
    setShowCompletionConfirmDialog(true);
    return true; // Return true to indicate dialog was shown
  };

  // Execute the actual assignment confirmation logic
  const executeConfirmAssignments = async () => {
    
    // Validate assignment cards
    if (!assignmentCards || assignmentCards.length === 0) {
      console.error('❌ NO ASSIGNMENT CARDS TO PROCESS');
      toast({
        title: "Error",
        description: "No assignment cards found. Please configure the exam schedule first.",
        variant: "destructive",
      });
      return false;
    }
    
    try {
      setLoading(true);
      
      // First ensure Step 4 is saved (questions are stored in database)
      
      // Prepare questions with categories for storage
      const questionsWithCategories = generatedQuestions.map(
        (candidateQuestions) => ({
          candidateId: candidateQuestions.candidateId,
          candidateName: candidateQuestions.candidateName,
          category: questionCategories[candidateQuestions.candidateId]?.trim() || 'General',
          questions: candidateQuestions.questions || [],
        })
      );

      const step4Response = await aiModuleAPI.saveStep4({
        aiModuleId,
        questionsWithCategories,
      });

      if (!step4Response.data.success) {
        console.error('❌ STEP 4 SAVE FAILED, CANNOT PROCEED WITH ASSIGNMENTS');
        toast({
          title: "Error",
          description: "Failed to save questions. Please try again.",
          variant: "destructive",
        });
        return false;
      }
      
      
      // Update completed steps from backend response
      if (step4Response.data.data.completedSteps) {
        setCompletedSteps(new Set(step4Response.data.data.completedSteps));
      }
      
      // Then ensure Step 5 is completed and AI Module status is set to 'completed'
      const step5Success = await saveStep5(false, false); // Don't manage loading state and don't show toast
      if (!step5Success) {
        console.error('❌ STEP 5 SAVE FAILED, CANNOT PROCEED WITH ASSIGNMENTS');
        return false;
      }

      // Wait longer to ensure backend processing is complete and status is updated
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      
      const response = await aiModuleAPI.createBatchExamAssignments({
        aiModuleId,
        assignments: assignmentCards,
        configurationData: formData
      });


      if (response.data.success) {
        
        toast({
          title: "Success",
          description: `AI Module configuration completed successfully. Created ${response.data.data.assignmentsCreated} exam assignments.`,
        });

        // Clear assignment cards and hide them
        setAssignmentCards([]);
        setShowAssignmentCards(false);
        
        // Clear the draft cookie since process is complete
        cookieUtils.delete(COOKIE_NAME);
        setHasUnsavedChanges(false);
        
        
        // Optionally close the AI Module modal if this was the final step
        if (onClose) {
          setTimeout(() => {
            onClose();
          }, 1000); // Small delay to show success message
        }
        
        return true; // Return success
      } else {
        console.error('❌ BATCH ASSIGNMENT CREATION FAILED:', response.data);
        toast({
          title: "Error",
          description: response.data.message || "Failed to create exam assignments",
          variant: "destructive",
        });
        return false; // Return failure
      }
    } catch (error) {
      console.error('💥 ERROR CREATING ASSIGNMENTS:', error);
      console.error('📋 ERROR DETAILS:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to create exam assignments",
        variant: "destructive",
      });
      return false; // Return failure
    } finally {
      setLoading(false);
    }
  };

  // Handle canceling assignment cards
  const handleCancelAssignments = () => {
    setAssignmentCards([]);
    setShowAssignmentCards(false);
    toast({
      title: "Schedule Cancelled",
      description: "Assignment schedule has been cancelled",
    });
  };

  // New function to handle the proceed action
  const handleProceed = async () => {
    // Show confirmation dialog before proceeding
    setPendingCompletionAction('proceed');
    setShowCompletionConfirmDialog(true);
  };

  // Execute the actual proceed logic
  const executeProceed = async () => {
    try {
      setProceedLoading(true);

      // First save Step 5 if not already saved
      const step5Success = await saveStep5();
      if (!step5Success) {
        console.error('❌ Step 5 save failed, cannot proceed with assignments');
        return;
      }

      // Wait longer to ensure backend processing is complete and status is updated
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Create exam assignments from the AI Module
      const response = await aiModuleAPI.createExamAssignments(aiModuleId);

      if (response.data.success) {
        const { assignmentsCreated, notEligibleCandidates, eligibleCandidates, totalCandidates } = response.data.data;
        
        // Build success message
        let successMessage = `${assignmentsCreated} exam assignment(s) created successfully for eligible candidates.`;
        
        if (notEligibleCandidates > 0) {
          successMessage += ` ${notEligibleCandidates} candidate(s) were skipped as they are not eligible.`;
        }
        
        toast({
          title: "Success",
          description: successMessage,
          duration: 5000,
        });

        // Clear the draft cookie since process is complete
        cookieUtils.delete(COOKIE_NAME);
        setHasUnsavedChanges(false);

        // If this is a modal, close it
        if (isModal && onClose) {
          setTimeout(() => {
            onClose();
          }, 1000); // Small delay to show success message
        }
      }
    } catch (error) {
      console.error("Error proceeding with AI Module:", error);
      toast({
        title: "Error",
        description:
          error.response?.data?.message ||
          "Failed to complete AI Module process",
        variant: "destructive",
      });
    } finally {
      setProceedLoading(false);
    }
  };

  // Handle completion confirmation dialog
  const handleCompletionConfirmation = async (confirmed) => {
    
    // Prevent double execution
    if (loading) {
      return;
    }
    
    if (!confirmed) {
      setShowCompletionConfirmDialog(false);
      setPendingCompletionAction(null);
      return; // User cancelled
    }


    // Don't close dialog immediately - let the action complete first
    try {
      // Execute the appropriate action based on what was pending
      if (pendingCompletionAction === 'nextStep') {

        await executeNextStep();
      } else if (pendingCompletionAction === 'confirmAssignments') {

        const success = await executeConfirmAssignments();

      } else if (pendingCompletionAction === 'proceed') {

        await executeProceed();
      }
    } catch (error) {
      console.error('💥 ERROR IN COMPLETION CONFIRMATION:', error);
    } finally {
      // Close dialog and reset state after action completes
      setShowCompletionConfirmDialog(false);
      setPendingCompletionAction(null);

    }
  };

  const saveStep5 = async (manageLoadingState = true, showSuccessToast = true) => {
    try {
      if (manageLoadingState) {
        setLoading(true);
      }

      // Simplified validation - all candidates use the same configuration
      // No individual candidate configuration needed anymore
      
      // Extract configuration from assignment cards if available
      let examDateTime = formData.examDateTime;
      let examDuration = formData.examDuration || 60;
      let graceTime = formData.graceTime || 60;
      
      // If we have assignment cards, use the first one's configuration as the base
      if (assignmentCards && assignmentCards.length > 0) {
        const firstCard = assignmentCards[0];
        examDateTime = firstCard.examDateTime || examDateTime;
        examDuration = firstCard.duration || examDuration;
        graceTime = firstCard.graceTime || graceTime;
        

      }
      
      // Basic validation for timing settings (if using manual configuration)
      if (examDateTime) {
        // Validate that exam date is in the future
        const examDate = new Date(examDateTime);
        const now = new Date();
        if (examDate <= now) {
          toast({
            title: "Validation Error",
            description: "Exam date and time must be in the future",
            variant: "destructive",
          });
          return false;
        }
      } else {
        // If no exam date/time, this might be a configuration-only save

        examDateTime = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(); // Default to tomorrow
      }

      // Validate exam duration
      if (examDuration && examDuration < 5) {
        toast({
          title: "Validation Error",
          description: "Please set a valid exam duration (minimum 5 minutes)",
          variant: "destructive",
        });
        return false;
      }

      // Create a single configuration that will be used for all candidates
      const examConfiguration = {
        examDateTime: examDateTime,
        examDuration: examDuration,
        graceTime: graceTime,
        sendInvitationEmail: formData.sendInvitationEmail !== false,
        enableProctoring: formData.enableProctoring || false,
        captureImages: formData.captureImages || false,
        trackTabSwitching: formData.trackTabSwitching || false,
        faceDetection: formData.faceDetection || false,
        warningsAllowed: formData.warningsAllowed || 0,
        captureFrequency: formData.captureFrequency || 10000,
        language: formData.language || "English",
        passingScore: formData.passingScore?.[0] || 60,
      };



      const response = await aiModuleAPI.saveStep5({
        aiModuleId,
        examConfiguration: examConfiguration,
      });



      if (response.data.success) {
        // Update completed steps from backend response
        if (response.data.data.completedSteps) {
          setCompletedSteps(new Set(response.data.data.completedSteps));
        }


        
        if (showSuccessToast) {
          toast({
            title: "Success",
            description: "AI Module configuration completed successfully",
          });
        }
        
        // Clear cookie on successful completion
        clearCookie();
        
        return true;
      }
    } catch (error) {
      console.error("Error saving Step 5:", error);
      console.error("Step 5 Error Details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to save configuration",
        variant: "destructive",
      });
      return false;
    } finally {
      if (manageLoadingState) {
        setLoading(false);
      }
    }
  };

  // Cancel editing function
  const cancelEditingCandidate = () => {
    setEditingCandidate(null);
    setEditFormData({});
    setEducationValidationErrors({});
  };

  // Start editing individual candidate
  const startEditingCandidate = (candidateId, candidateData) => {
    setEditingCandidate(candidateId);
    const aiParsedData = candidateData.aiParsedData || {};
    const experienceYears = calculateExperienceYears(aiParsedData);

    // Initialize education data for editing
    const educationData =
      aiParsedData.education && aiParsedData.education.length > 0
        ? aiParsedData.education
        : [
            {
              qualification: "",
              specialization: "",
              institution: "",
              yearOfPassing: null,
              score: "",
            },
          ];

    setEditFormData({
      skills: candidateData.skills || [],
      experience: candidateData.experience || "",
      education: candidateData.education || "",
      experienceYears:
        aiParsedData.experience?.totalExperience?.years ??
        Math.floor(experienceYears),
      experienceMonths:
        aiParsedData.experience?.totalExperience?.months ??
        Math.round((experienceYears % 1) * 12),
      experienceDecimal:
        aiParsedData.experience?.totalExperience?.yearsInDecimal ||
        experienceYears,
      // Initialize education editing
      educationDetails: educationData,
      educationEditMode: true,
      personalInfo: {
        name:
          aiParsedData.candidate?.fullName ||
          candidateData.personalInfo?.name ||
          "",
        currentRole: aiParsedData.candidate?.currentRole || "",
        location: aiParsedData.candidate?.location || "",
        phone:
          aiParsedData.candidate?.contact?.phone ||
          candidateData.personalInfo?.phone ||
          "",
        email:
          aiParsedData.candidate?.contact?.email ||
          candidateData.personalInfo?.email ||
          "",
        github:
          aiParsedData.candidate?.contact?.github ||
          candidateData.personalInfo?.github ||
          "",
        linkedin:
          aiParsedData.candidate?.contact?.linkedin ||
          candidateData.personalInfo?.linkedIn ||
          "",
      },
    });
  };

  // Save individual candidate edited data
  const saveCandidateEditedData = async (candidateId) => {
    try {
      setSavingEdit(true);

      // Validate education data first
      if (editFormData.educationEditMode && editFormData.educationDetails) {
        const currentYear = new Date().getFullYear();
        const validationErrors = [];
        const validEducation = [];
        const newValidationErrors = {};

        editFormData.educationDetails.forEach((edu, index) => {
          const errors = [];

          // Required field validation
          if (!edu.qualification || edu.qualification.trim() === "") {
            errors.push(`Education ${index + 1}: Qualification is required`);
            newValidationErrors[`${index}_qualification`] =
              "Qualification is required";
          }

          if (!edu.institution || edu.institution.trim() === "") {
            errors.push(`Education ${index + 1}: Institution is required`);
            newValidationErrors[`${index}_institution`] =
              "Institution is required";
          }

          // Year validation
          if (edu.yearOfPassing) {
            const year =
              edu.yearOfPassing === "Present"
                ? currentYear
                : parseInt(edu.yearOfPassing);

            if (isNaN(year)) {
              errors.push(`Education ${index + 1}: Invalid year format`);
              newValidationErrors[`${index}_year`] = "Invalid year format";
            } else if (year > currentYear) {
              errors.push(
                `Education ${
                  index + 1
                }: Year cannot be greater than current year (${currentYear})`
              );
              newValidationErrors[
                `${index}_year`
              ] = `Year cannot be greater than ${currentYear}`;
            } else if (year < 1950) {
              errors.push(`Education ${index + 1}: Year cannot be before 1950`);
              newValidationErrors[`${index}_year`] =
                "Year cannot be before 1950";
            }
          }

          if (errors.length === 0 && edu.qualification && edu.institution) {
            validEducation.push(edu);
          } else {
            validationErrors.push(...errors);
          }
        });

        // Update validation errors state
        setEducationValidationErrors(newValidationErrors);

        if (validEducation.length === 0) {
          toast({
            title: "Validation Error",
            description:
              "At least one education entry with qualification and institution is required",
            variant: "destructive",
          });
          return;
        }

        if (validationErrors.length > 0) {
          toast({
            title: "Validation Warnings",
            description:
              validationErrors.slice(0, 2).join(". ") +
              (validationErrors.length > 2 ? "..." : ""),
            variant: "destructive",
          });
          return;
        }
      }

      // Find the original candidate data to preserve important fields
      const originalCandidate = formData.candidateResumeData?.find(
        (data) => data.candidateId === candidateId
      );

      // Prepare updated candidate data for backend
      const updatedCandidateResumeData = formData.candidateResumeData.map(
        (candidate) =>
          candidate.candidateId === candidateId
            ? {
                ...candidate,
                // Skills are intentionally excluded - they remain unchanged
                experience: editFormData.experience || "",
                education: editFormData.education || "",
                personalInfo: editFormData.personalInfo || {},
                // Preserve important fields that should not be lost
                dataProvidedBy: candidate.dataProvidedBy || "OpenAI ChatGPT",
                aiExtractionConfirmed: candidate.aiExtractionConfirmed || true,
                extractionSource:
                  candidate.extractionSource || "OpenAI ChatGPT",
                aiParsedData: {
                  ...candidate.aiParsedData,
                  // Update candidate personal info in AI parsed data
                  candidate: {
                    ...candidate.aiParsedData?.candidate,
                    fullName:
                      editFormData.personalInfo?.name ||
                      candidate.aiParsedData?.candidate?.fullName,
                    currentRole:
                      editFormData.personalInfo?.currentRole ||
                      candidate.aiParsedData?.candidate?.currentRole,
                    location:
                      editFormData.personalInfo?.location ||
                      candidate.aiParsedData?.candidate?.location,
                    contact: {
                      ...candidate.aiParsedData?.candidate?.contact,
                      phone:
                        editFormData.personalInfo?.phone ||
                        candidate.aiParsedData?.candidate?.contact?.phone,
                      email:
                        editFormData.personalInfo?.email ||
                        candidate.aiParsedData?.candidate?.contact?.email,
                      github:
                        editFormData.personalInfo?.github ||
                        candidate.aiParsedData?.candidate?.contact?.github,
                      linkedin:
                        editFormData.personalInfo?.linkedin ||
                        candidate.aiParsedData?.candidate?.contact?.linkedin,
                    },
                  },
                  experience: {
                    ...candidate.aiParsedData?.experience,
                    totalExperience: {
                      years: editFormData.experienceYears || 0,
                      months: editFormData.experienceMonths || 0,
                      yearsInDecimal: editFormData.experienceDecimal || 0,
                    },
                  },
                  // Update education if it was edited
                  ...(editFormData.educationEditMode &&
                  editFormData.educationDetails
                    ? {
                        education: editFormData.educationDetails.map((edu) => ({
                          ...edu,
                          yearOfPassing:
                            edu.yearOfPassing === "Present"
                              ? new Date().getFullYear()
                              : edu.yearOfPassing,
                        })),
                      }
                    : {}),
                },
              }
            : candidate
      );

      const resumeExtractionData = {
        extractedSkills: formData.extractedSkills,
        extractedExperience: formData.extractedExperience,
        extractedEducation: formData.extractedEducation,
        candidateResumeData: updatedCandidateResumeData,
        extractionDate: formData.extractionDate,
      };

      const response = await aiModuleAPI.saveStep2({
        aiModuleId,
        resumeExtraction: resumeExtractionData,
      });

      if (response.data.success) {
        // Update the local state with the updated data to ensure UI reflects changes immediately
        setFormData((prev) => ({
          ...prev,
          candidateResumeData: updatedCandidateResumeData,
        }));

        setEditingCandidate(null);
        setEditFormData({});
        setEducationValidationErrors({});

        toast({
          title: "Success",
          description:
            "Personal information updated successfully. Database has been updated with the latest details.",
        });
      }
    } catch (error) {
      console.error("Error updating candidate data:", error);
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to update candidate data",
        variant: "destructive",
      });
    } finally {
      setSavingEdit(false);
    }
  };

  // Removed skill validation dialog state (no longer needed)

  // Skill adding functionality removed - skills can only be removed during editing

  // Removed skill validation dialog handlers (no longer needed)

  // Helper function to close skill tags popup
  const closeSkillTagsPopup = () => {
    setSkillTagsPopup({
      isOpen: false,
      skills: [],
      questionId: "",
      position: { x: 0, y: 0 },
    });
  };

  // Function to open job skills popup
  const openJobSkillsPopup = () => {
    if (jobDescriptionDataState?.skills && jobDescriptionDataState.skills.length > 5) {
      setJobSkillsPopup({
        isOpen: true,
        skills: jobDescriptionDataState.skills,
        jobTitle: jobDescriptionDataState.jobTitle || "Job",
      });
    }
  };

  // Function to close job skills popup
  const closeJobSkillsPopup = () => {
    setJobSkillsPopup({
      isOpen: false,
      skills: [],
      jobTitle: "",
    });
  };

  // Functions for multiple resume upload
  const handleMultipleResumeUpload = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => file.type === "application/pdf");
    
    if (validFiles.length !== files.length) {
      toast({
        title: "Invalid Files",
        description: "Only PDF files are allowed. Some files were skipped.",
        variant: "destructive",
      });
    }

    if (validFiles.length > 0) {
      const newFiles = validFiles.map(file => ({
        id: Date.now() + Math.random(),
        file: file,
        name: file.name,
        size: file.size,
        status: 'pending' // pending, uploading, completed, error
      }));

      setMultipleResumeFiles(prev => [...prev, ...newFiles]);
      
      toast({
        title: "Files Selected",
        description: `${validFiles.length} resume file(s) selected for upload.`,
      });
    }

    // Clear the input
    event.target.value = null;
  };

  const removeResumeFromList = (fileId) => {
    setMultipleResumeFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const uploadAllMultipleResumes = async () => {
    if (multipleResumeFiles.length === 0) {
      toast({
        title: "No Files",
        description: "Please select resume files to upload.",
        variant: "destructive",
      });
      return;
    }

    setUploadingMultipleResumes(true);
    
    try {
      for (const resumeFile of multipleResumeFiles) {
        if (resumeFile.status === 'pending') {
          // Update status to uploading
          setMultipleResumeFiles(prev => 
            prev.map(file => 
              file.id === resumeFile.id 
                ? { ...file, status: 'uploading' }
                : file
            )
          );

          try {
            // Here you would typically upload to your backend
            // For now, we'll simulate the upload
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Update status to completed
            setMultipleResumeFiles(prev => 
              prev.map(file => 
                file.id === resumeFile.id 
                  ? { ...file, status: 'completed' }
                  : file
              )
            );

            toast({
              title: "Upload Success",
              description: `${resumeFile.name} uploaded successfully.`,
            });
          } catch (error) {
            // Update status to error
            setMultipleResumeFiles(prev => 
              prev.map(file => 
                file.id === resumeFile.id 
                  ? { ...file, status: 'error' }
                  : file
              )
            );

            toast({
              title: "Upload Failed",
              description: `Failed to upload ${resumeFile.name}.`,
              variant: "destructive",
            });
          }
        }
      }
    } finally {
      setUploadingMultipleResumes(false);
    }
  };

  const clearAllResumes = () => {
    setMultipleResumeFiles([]);
    setSkippedResumes([]);
    toast({
      title: "Cleared",
      description: "All resume files have been removed.",
    });
  };

  // Process resumes and create candidate accounts
  const processResumesAndCreateCandidates = async () => {
    if (multipleResumeFiles.length === 0) {
      return { success: true, createdCandidates: [] };
    }

    setProcessingResumes(true);
    setSkippedResumes([]);
    const createdCandidates = [];

    try {

      
      toast({
        title: "Processing Resumes",
        description: `Processing ${multipleResumeFiles.length} resume(s)...`,
      });

      // Extract file objects from multipleResumeFiles
      const files = multipleResumeFiles.map(rf => rf.file);

      // Call backend to process resumes and create candidates

      const response = await aiModuleAPI.createCandidatesFromResumes(files);


      if (response.data.success) {
        const { created, skipped, failed, summary } = response.data.data;

        
        // Debug: Check if AI data is in the response
        if (created && created.length > 0) {

          created.forEach(c => {
            console.log(`  - ${c.candidateName}:`, {
              hasAiParsedData: !!c.aiParsedData,
              hasExtractedText: !!c.extractedText,
              aiDataKeys: c.aiParsedData ? Object.keys(c.aiParsedData) : [],
              extractedTextLength: c.extractedText ? c.extractedText.length : 0
            });
          });
        }

        // Add created candidates to the list
        if (created && created.length > 0) {
          
          // Small delay to ensure database consistency
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Fetch full candidate details for the created candidates
          for (const createdInfo of created) {
            try {

              const candidateResponse = await candidatesAPI2.getOne(createdInfo.candidateId);

              
              // Check if response has success wrapper or is direct data
              const candidateData = candidateResponse.data.success 
                ? candidateResponse.data.data 
                : candidateResponse.data;
              
              if (candidateData && candidateData._id) {
                // Merge the fetched candidate data with AI parsed data from creation
                createdCandidates.push({
                  ...candidateData,
                  aiParsedData: createdInfo.aiParsedData, // Add AI parsed data from creation
                  extractedText: createdInfo.extractedText // Add extracted text from creation
                });
              } else {
                console.error(`⚠️ Invalid candidate data for ${createdInfo.candidateId}:`, candidateResponse.data);
                // Fallback: use basic info from creation response
                createdCandidates.push({
                  _id: createdInfo.candidateId,
                  name: createdInfo.candidateName,
                  email: createdInfo.email,
                  fileName: createdInfo.fileName,
                  aiParsedData: createdInfo.aiParsedData, // Include AI parsed data
                  extractedText: createdInfo.extractedText // Include extracted text
                });
              }
            } catch (error) {
              console.error(`❌ Error fetching candidate ${createdInfo.candidateId}:`, error);
              console.error('Error details:', error.response?.data || error.message);
              // Fallback: use basic info from creation response
              createdCandidates.push({
                _id: createdInfo.candidateId,
                name: createdInfo.candidateName,
                email: createdInfo.email,
                fileName: createdInfo.fileName,
                aiParsedData: createdInfo.aiParsedData, // Include AI parsed data
                extractedText: createdInfo.extractedText // Include extracted text
              });
            }
          }
        }

        // Set skipped resumes for display
        const allSkipped = [...(skipped || []), ...(failed || [])];
        setSkippedResumes(allSkipped);

        // Show summary toast
        if (summary.created > 0) {
          toast({
            title: "Processing Complete",
            description: `${summary.created} candidate(s) created successfully${summary.skipped > 0 ? `, ${summary.skipped} skipped` : ''}${summary.failed > 0 ? `, ${summary.failed} failed` : ''}`,
          });
        } else if (summary.skipped > 0 || summary.failed > 0) {
          toast({
            title: "No Candidates Created",
            description: `All ${summary.total} resume(s) were skipped or failed. Check details below.`,
            variant: "destructive",
          });
        }

        return { success: true, createdCandidates };
      } else {
        console.error('❌ Backend returned error:', response.data.message);
        toast({
          title: "Error",
          description: response.data.message || "Failed to process resumes",
          variant: "destructive",
        });
        return { success: false, createdCandidates: [] };
      }
    } catch (error) {
      console.error('❌ Error processing resumes:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to process resumes",
        variant: "destructive",
      });
      return { success: false, createdCandidates: [] };
    } finally {
      setProcessingResumes(false);
    }
  };

  // Helper function to open skills popup for Step 3
  const openSkillsPopup = (candidateId, candidateName, skills) => {
    setSkillsPopup({
      isOpen: true,
      candidateId,
      candidateName,
      skills: skills || [],
    });
  };

  // Helper function to close skills popup
  const closeSkillsPopup = () => {
    setSkillsPopup({
      isOpen: false,
      candidateId: "",
      candidateName: "",
      skills: [],
    });
  };

  // Helper function to open skills matching popup
  const openSkillsMatchingPopup = (candidateId, candidateName, skillsMatching) => {
    setSkillsMatchingPopup({
      isOpen: true,
      candidateId,
      candidateName,
      percentage: skillsMatching?.percentage || 0,
      matchedSkills: skillsMatching?.matchedSkills || [],
      unmatchedSkills: skillsMatching?.unmatchedSkills || [],
      totalJobSkills: skillsMatching?.totalJobSkills || 0,
      totalCandidateSkills: skillsMatching?.totalCandidateSkills || 0,
    });
  };

  // Helper function to close skills matching popup
  const closeSkillsMatchingPopup = () => {
    setSkillsMatchingPopup({
      isOpen: false,
      candidateId: "",
      candidateName: "",
      percentage: 0,
      matchedSkills: [],
      unmatchedSkills: [],
      totalJobSkills: 0,
      totalCandidateSkills: 0,
    });
  };

  // Generate questions using OpenAI
  const generateQuestionsForCandidates = async () => {
    try {
      setGeneratingQuestions(true);

      if (!aiModuleId) {
        toast({
          title: "Error",
          description: "AI Module ID not found. Please save Step 3 first.",
          variant: "destructive",
        });
        return false;
      }

      const response = await aiModuleAPI.generateQuestions({
        aiModuleId,
        questionGenerationOption: selectedExistingCandidates.length > 1 ? questionGenerationOption : "same",
        questionGenerationChoice: questionGenerationChoice || 'existing', // Pass the user's choice
      });

      if (response.data.success) {
        const {
          generatedQuestions: questions,
          totalCandidates,
          successfulGenerations,
        } = response.data.data;

        setGeneratedQuestions(questions);
        setQuestionsGenerated(true);

        // Set active tab to first candidate
        if (questions.length > 0 && !activeQuestionTab) {
          setActiveQuestionTab(questions[0].candidateId);
        }

        // Initialize categories for each candidate
        const initialCategories = {};
        questions.forEach((candidateQuestions) => {
          initialCategories[candidateQuestions.candidateId] = "";
        });
        setQuestionCategories(initialCategories);

        // Show appropriate success message based on generation option
        let successMessage = `Questions generated successfully for ${successfulGenerations}/${totalCandidates} candidates`;
        
        if (selectedExistingCandidates.length > 1) {
          if (questionGenerationOption === "same") {
            successMessage += " with identical questions for all candidates";
          } else if (questionGenerationOption === "similar") {
            successMessage += " with same questions in different order for each candidate";
          } else if (questionGenerationOption === "different") {
            successMessage += " with personalized questions for each candidate";
          }
        }

        toast({
          title: "Success",
          description: successMessage,
        });

        return true;
      }
    } catch (error) {
      console.error("Error generating questions:", error);
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to generate questions",
        variant: "destructive",
      });
      return false;
    } finally {
      setGeneratingQuestions(false);
    }
  };

  // Handle category change for a candidate
  const handleCategoryChange = (candidateId, category) => {
    setQuestionCategories((prev) => ({
      ...prev,
      [candidateId]: category,
    }));
  };

  // Handle Generate Questions from Modal
  const handleGenerateQuestions = async () => {
    try {
      // Prevent multiple simultaneous calls
      if (generatingQuestions) {
        return;
      }
      
      // Close modal immediately when generate button is clicked
      setShowGenerateQuestionsModal(false);
      
      // Only clear existing questions if user chose "new", otherwise preserve them for merging
      if (questionGenerationChoice === "new") {
        setGeneratedQuestions([]);
        setQuestionsGenerated(false);
        setActiveQuestionTab(null);
      } else {
        console.log('🔄 User chose "Existing" - preserving existing questions for merging');
      }
      
      // Start loading state
      setGeneratingQuestions(true);

      // Validate aiModuleId first
      if (!aiModuleId) {
        // Reopen modal since validation failed
        setShowGenerateQuestionsModal(true);
        toast({
          title: "Error",
          description: "AI Module ID not found. Please save previous steps first.",
          variant: "destructive",
        });
        return;
      }

      // Get only eligible candidates
      const { eligible: eligibleCandidates, notEligible: notEligibleCandidates } = categorizeCandidatesByEligibility();

      // Check if we have any eligible candidates
      if (eligibleCandidates.length === 0) {
        setShowGenerateQuestionsModal(true);
        toast({
          title: "No Eligible Candidates",
          description: "No candidates meet the eligibility criteria. Please check skills matching and experience requirements.",
          variant: "destructive",
        });
        return;
      }

      // Extract skills that will be used for question generation
      const jobSkills = formData.jobDescription?.skills || [];
      const skillsWithQuestions = [];
      const skillsWithoutQuestions = [];

      // Validate skill-based form data
      if (jobSkills.length > 0) {
        const totalFromSkills = jobSkills.reduce((sum, skill) => {
          const skillConfig = generateQuestionsFormData.skillQuestions?.[skill];
          const skillTotal = (skillConfig?.easy || 0) + (skillConfig?.medium || 0) + (skillConfig?.hard || 0);
          
          if (skillTotal > 0) {
            skillsWithQuestions.push(skill);
          } else {
            skillsWithoutQuestions.push(skill);
          }
          
          return sum + skillTotal;
        }, 0);

        // Display skills as tags in console
        skillsWithQuestions.forEach(skill => {
          const config = generateQuestionsFormData.skillQuestions?.[skill];
          const total = (config?.easy || 0) + (config?.medium || 0) + (config?.hard || 0);
        });

        if (skillsWithoutQuestions.length > 0) {
          skillsWithoutQuestions.forEach(skill => {
            console.log(`   🚫 [${skill.toUpperCase()}] - 0 questions`);
          });
        }

        if (totalFromSkills !== generateQuestionsFormData.totalQuestions) {
          // Reopen modal since validation failed
          setShowGenerateQuestionsModal(true);
          toast({
            title: "Validation Error", 
            description: `Sum of questions per skill (${totalFromSkills}) must equal total questions (${generateQuestionsFormData.totalQuestions})`,
            variant: "destructive",
          });
          return;
        }

        if (totalFromSkills === 0) {
          // Reopen modal since validation failed
          setShowGenerateQuestionsModal(true);
          toast({
            title: "Validation Error", 
            description: "Please configure at least one question across all skills",
            variant: "destructive",
          });
          return;
        }
      }

      // Process only eligible candidates for question generation
      const examConfigurationArray = eligibleCandidates.map((candidate) => {
        const candidateData = formData.candidateResumeData?.find(
          (data) => data.candidateId === candidate._id
        );

        // Extract experience details from AI parsed data
        const aiParsedData = candidateData?.aiParsedData || {};
        const experienceYears = aiParsedData.experience?.totalExperience?.years || 0;
        const experienceMonths = aiParsedData.experience?.totalExperience?.months || 0;

        // Prepare experience details for database storage
        const experienceDetails = {
          years: experienceYears,
          months: experienceMonths,
          isFresher: experienceYears === 0 && experienceMonths === 0,
        };

        // Use skill-based configuration for question generation
        const skillQuestions = generateQuestionsFormData.skillQuestions || {};
        
        // Convert skill-based config to difficulty distribution for backend compatibility
        let easyQuestions = 0, mediumQuestions = 0, hardQuestions = 0;
        
        if (formData.jobDescription?.skills) {
          formData.jobDescription.skills.forEach(skill => {
            const skillConfig = skillQuestions[skill];
            if (skillConfig) {
              easyQuestions += skillConfig.easy || 0;
              mediumQuestions += skillConfig.medium || 0;
              hardQuestions += skillConfig.hard || 0;
            }
          });
        }

        return {
          candidateId: candidate._id,
          candidateName: candidate.name,
          extractedSkills: formData.jobDescription?.skills || [],
          extractedExperience: candidateData?.experience || "",
          experienceDetails: experienceDetails,
          easyQuestions: Number(easyQuestions) || 0,
          mediumQuestions: Number(mediumQuestions) || 0,
          hardQuestions: Number(hardQuestions) || 0,
          questionCount: Number(easyQuestions) + Number(mediumQuestions) + Number(hardQuestions),
          totalQuestionsLimit: Number(generateQuestionsFormData.totalQuestions) || 0,
          prompt: null, // No custom prompt in simplified version
          skillQuestions: skillQuestions, // Include skill-specific configuration
        };
      });

      
      for (const config of examConfigurationArray) {
        const totalQuestions = config.easyQuestions + config.mediumQuestions + config.hardQuestions;

        if (totalQuestions !== config.totalQuestionsLimit) {
          throw new Error(`Validation failed for ${config.candidateName}: Total questions (${totalQuestions}) must equal limit (${config.totalQuestionsLimit})`);
        }

        if (totalQuestions === 0) {
          throw new Error(`Validation failed for ${config.candidateName}: Must have at least one question`);
        }
      }


      // Additional validation before sending to backend
      examConfigurationArray.forEach((config, index) => {
        console.log(`   Eligible Candidate ${index + 1} (${config.candidateName}):`, {
          candidateId: config.candidateId,
          easyQuestions: config.easyQuestions,
          mediumQuestions: config.mediumQuestions,
          hardQuestions: config.hardQuestions,
          totalQuestions: config.questionCount,
          totalQuestionsLimit: config.totalQuestionsLimit,
          dataTypes: {
            easyQuestions: typeof config.easyQuestions,
            mediumQuestions: typeof config.mediumQuestions,
            hardQuestions: typeof config.hardQuestions,
            totalQuestionsLimit: typeof config.totalQuestionsLimit
          }
        });
      });

      const step3Response = await aiModuleAPI.saveStep3({
        aiModuleId,
        examConfiguration: examConfigurationArray,
        questionGenerationOption: eligibleCandidates.length > 1 
          ? generateQuestionsFormData.questionGenerationOption 
          : "same",
      });

      if (!step3Response.data.success) {
        throw new Error(step3Response.data.message || "Failed to save Step 3");
      }


      // Update completed steps
      if (step3Response.data.data.completedSteps) {
        setCompletedSteps(new Set(step3Response.data.data.completedSteps));
      }

      // Update form data with the configuration
      setFormData(prev => ({
        ...prev,
        easyQuestions: generateQuestionsFormData.easyQuestions,
        mediumQuestions: generateQuestionsFormData.mediumQuestions,
        hardQuestions: generateQuestionsFormData.hardQuestions,
        totalQuestionsLimit: generateQuestionsFormData.totalQuestions,
      }));

      // Update question generation option
      if (selectedExistingCandidates.length > 1) {
        setQuestionGenerationOption(generateQuestionsFormData.questionGenerationOption);
      }

      await new Promise(resolve => setTimeout(resolve, 1000));

      // Generate questions for ELIGIBLE candidates only

      const questionsResponse = await aiModuleAPI.generateQuestions({
        aiModuleId,
        questionGenerationOption: eligibleCandidates.length > 1 
          ? generateQuestionsFormData.questionGenerationOption 
          : "same",
        skillQuestions: generateQuestionsFormData.skillQuestions, // Include skill-specific configuration
        questionGenerationChoice: questionGenerationChoice || 'existing', // Pass the user's choice
      });

      if (questionsResponse.data.success) {
        const {
          generatedQuestions: questions,
          totalCandidates,
          successfulGenerations,
          eligibleCandidates = totalCandidates,
          notEligibleCandidates = 0,
          skillsWithQuestions = [],
          skillsWithoutQuestions = []
        } = questionsResponse.data.data;


        // CRITICAL DEBUG: Check if questions are being added in existing mode
        questions.forEach((candidateQuestions, index) => {
          const manualQuestions = candidateQuestions.questions?.filter(q => q.isManual === true || q.manualQuestionId) || [];
          const aiQuestions = candidateQuestions.questions?.filter(q => q.isAIGenerated === true || q.source === 'ai') || [];
          
          
          if (questionGenerationChoice === 'existing' && aiQuestions.length === 0) {
            console.log(`      ⚠️ WARNING: No AI questions found in existing mode!`);
          }
        });

        // Use the questions directly from backend (they are already merged based on choice)
        setGeneratedQuestions(questions);
        setQuestionsGenerated(true);

        // Set active tab to first candidate
        if (questions.length > 0 && !activeQuestionTab) {
          setActiveQuestionTab(questions[0].candidateId);
        }

        // Initialize categories
        const initialCategories = {};
        questions.forEach((candidateQuestions) => {
          initialCategories[candidateQuestions.candidateId] = "";
        });
        setQuestionCategories(initialCategories);

        // Show appropriate success message based on generation option
        let successMessage = `Questions generated successfully for ${successfulGenerations}/${eligibleCandidates.length} eligible candidates`;
        
        // Add information about skipped candidates
        if (notEligibleCandidates.length > 0) {
          successMessage += `. ${notEligibleCandidates.length} candidate(s) were skipped as they are not eligible`;
        }
        
        // Add information about skills
        if (skillsWithQuestions.length > 0) {
          successMessage += ` based on skills: ${skillsWithQuestions.join(', ')}`;
        }
        
        // Add information about skipped skills
        if (skillsWithoutQuestions.length > 0) {
          successMessage += `. Skills skipped (0 questions): ${skillsWithoutQuestions.join(', ')}`;
        }
        
        if (eligibleCandidates.length > 1) {
          if (generateQuestionsFormData.questionGenerationOption === "same") {
            successMessage += " with identical questions for all eligible candidates";
          } else if (generateQuestionsFormData.questionGenerationOption === "similar") {
            successMessage += " with same questions in different order for each eligible candidate";
          } else if (generateQuestionsFormData.questionGenerationOption === "different") {
            successMessage += " with personalized questions for each eligible candidate";
          }
        }

        toast({
          title: "Success",
          description: successMessage,
        });

        // Reset the question generation choice after successful completion
        setQuestionGenerationChoice(null);

        // Modal is already closed at the beginning of the function
        // Don't change the step - stay on Step 3 to show the generated questions
      } else {
        throw new Error(questionsResponse.data.message || "Failed to generate questions");
      }
    } catch (error) {
      console.error("Error in handleGenerateQuestions:", error);
      
      // If it's a validation error, reopen the modal so user can fix the configuration
      if (error.message && error.message.includes("Validation failed")) {
        setShowGenerateQuestionsModal(true);
      }
      
      toast({
        title: "Error",
        description: error.response?.data?.message || error.message || "Failed to generate questions",
        variant: "destructive",
      });
      
      // Reset the question generation choice even on error
      setQuestionGenerationChoice(null);
    } finally {
      setGeneratingQuestions(false);
    }
  };

  // Handle Add Question Modal Actions with confirmation check
  const handleAddQuestionManually = () => {
    const existingQuestionsCheck = checkForExistingManualQuestions();

    if (existingQuestionsCheck.hasManualQuestions) {
      // Show confirmation dialog if any questions exist
      setManualQuestionsCount(existingQuestionsCheck.count);
      setQuestionGenerationChoice(null); // Reset choice
      setCurrentActionType("addManually"); // Set action type for dialog context
      setShowQuestionGenerationConfirm(true);
      setPendingAction('addManually');
    } else {
      // Proceed directly to manual question modal if no questions exist
      setShowManualQuestionModal(true);
    }
  };



  const handleAddFromQuestionBank = () => {
    const existingQuestionsCheck = checkForExistingManualQuestions();
    
    setShowAddQuestionModal(false);
    
    if (existingQuestionsCheck.hasManualQuestions) {
      // Show confirmation dialog if any questions exist
      setManualQuestionsCount(existingQuestionsCheck.count);
      setQuestionGenerationChoice(null); // Reset choice
      setCurrentActionType("addFromQuestionBank"); // Set action type for dialog context
      setShowQuestionGenerationConfirm(true);
      // Store the intended action for after confirmation
      setPendingAction('addFromQuestionBank');
    } else {
      // Proceed directly to question bank modal if no questions exist
      setShowQuestionBankModal(true);
    }
  };

  // Handle questions selected from question bank
  const handleQuestionBankSelection = async (assignmentData) => {
    // If user chose "new", clear local state immediately before processing
    if (questionGenerationChoice === "new") {
      setGeneratedQuestions([]);
      setQuestionsGenerated(false);
      setActiveQuestionTab(null);
    }
    
    try {
      
      const { questions, assignments, candidateDetails } = assignmentData;
      
      if (!questions || questions.length === 0) {
        toast({
          title: "No Questions Selected",
          description: "No questions were selected for assignment.",
          variant: "destructive",
        });
        return;
      }

      // Check if any assignments were made
      const totalAssignments = Object.values(assignments).reduce((total, candidateIds) => {
        return total + (candidateIds?.length || 0);
      }, 0);

      if (totalAssignments === 0) {
        toast({
          title: "No Assignments Made",
          description: "Please assign questions to at least one candidate.",
          variant: "destructive",
        });
        return;
      }

      // Process each question with its individual candidate assignments
      let successfulAssignments = 0;
      let failedAssignments = 0;
      
      for (const question of questions) {
        const assignedCandidateIds = assignments[question._id] || [];
        
        if (assignedCandidateIds.length > 0) {
          try {
            
            // Find the correct answer index from the choices array
            let correctAnswerIndex = 0;
            const filteredOptions = (question.choices || []).filter(opt => opt && opt.trim());
            
            if (filteredOptions.length < 2) {
              console.error(`❌ Debug - Question has insufficient options:`, {
                question: question.question.substring(0, 50) + "...",
                originalChoices: question.choices,
                filteredOptions: filteredOptions
              });
              throw new Error(`Question must have at least 2 non-empty options`);
            }
            
            if (question.answers && question.answers.length > 0) {
              correctAnswerIndex = filteredOptions.findIndex(choice => 
                question.answers.includes(choice)
              );
              // If not found, default to 0
              if (correctAnswerIndex === -1) {
                console.warn(`⚠️ Debug - Correct answer not found in options, defaulting to index 0:`, {
                  correctAnswers: question.answers,
                  filteredOptions: filteredOptions
                });
                correctAnswerIndex = 0;
              }
            }
            
            const questionToAdd = {
              question: question.question.trim(),
              options: filteredOptions,
              correctAnswerIndex: correctAnswerIndex,
              difficulty: (question.difficultyLevel || "medium").charAt(0).toUpperCase() + (question.difficultyLevel || "medium").slice(1).toLowerCase(),
              category: question.category || "General",
              subCategory: question.subCategory || "",
              tags: question.tags || [],
            };

            // Call the API for this specific question and its assigned candidates
            const response = await aiModuleAPI.addManualQuestions({
              aiModuleId: aiModuleId,
              questions: [questionToAdd],
              candidateIds: assignedCandidateIds,
              questionGenerationChoice: questionGenerationChoice, // Pass the choice to backend
            });

            if (response.data && response.data.success) {
              successfulAssignments++;
            } else {
              failedAssignments++;
              console.error(`❌ Debug - Failed to assign question:`, response.data?.message || response.message);
            }
          } catch (error) {
            failedAssignments++;
            console.error(`❌ Debug - Error assigning question:`, {
              error: error,
              message: error.message,
              response: error.response?.data,
              status: error.response?.status,
              statusText: error.response?.statusText
            });
          }
        }
      }


      if (successfulAssignments > 0) {
        toast({
          title: "Questions Added Successfully",
          description: `${successfulAssignments} questions assigned to candidates successfully.${failedAssignments > 0 ? ` ${failedAssignments} assignments failed.` : ''}`,
        });

        // Refresh the data using the same method as manual questions
        if (aiModuleId) {
          try {
            await new Promise(resolve => setTimeout(resolve, 1000)); // Longer delay to ensure DB updates
            await fetchAIModuleData(aiModuleId);
            
            // Ensure questionsGenerated is set to true after refresh
            setQuestionsGenerated(true);
            
            // Reset the question generation choice after successful completion
            setQuestionGenerationChoice(null);
          } catch (error) {
            console.error('❌ Error refreshing AI Module data:', error);
            // Even if refresh fails, set questionsGenerated to true since questions were added
            setQuestionsGenerated(true);
            
            // Reset the question generation choice even on error
            setQuestionGenerationChoice(null);
          }
        }
      } else {
        throw new Error("No questions were successfully assigned");
      }
    } catch (error) {
      console.error('❌ Error adding questions from question bank:', error);
      toast({
        title: "Error Adding Questions",
        description: error.response?.data?.message || error.message || "Failed to add questions from question bank.",
        variant: "destructive",
      });
    } finally {
      setShowQuestionBankModal(false);
    }
  };

  const handleManualQuestionsAdded = async (data) => {
    
    // If user chose "new", clear local state immediately before refreshing
    if (questionGenerationChoice === "new") {
      setGeneratedQuestions([]);
      setQuestionsGenerated(false);
      setActiveQuestionTab(null);
    }
    
    // Set loading state
    setAddingManualQuestions(true);
    
    // Check verification results if available
    if (data && data.verificationResults) {
      
      // Log any verification issues
      const failedVerifications = data.verificationResults.filter(result => 
        !result.hasQuestions || result.questionsCount === 0
      );
      
      if (failedVerifications.length > 0) {
        console.warn('⚠️ FRONTEND: Warning - Some candidates have verification issues:', failedVerifications);
      }
    }
    
    // Show success message with proper count
    if (data && data.totalQuestionsAdded > 0) {
      const totalAdded = data.totalQuestionsAdded || data.questionsAdded || 0;
      const totalVerified = data.totalQuestionsVerified || totalAdded;
      const candidatesCount = data.candidatesUpdated?.length || 1;
      
      // Show different message based on verification success
      const verificationSuccess = data.verificationSuccess !== false;
      const description = verificationSuccess 
        ? `${totalAdded} question(s) have been added to ${candidatesCount} candidate(s).`
        : `${totalAdded} question(s) added, but only ${totalVerified} verified. Please refresh if questions don't appear.`;
      
      
      toast({
        title: "Questions Added Successfully",
        description: description,
        variant: verificationSuccess ? "default" : "destructive",
      });
    }
    
    // Refresh the AI Module data to show the new questions
    if (aiModuleId) {
      try {
        
        // Add a longer delay if verification failed
        const delay = (data && data.verificationSuccess === false) ? 1000 : 500;
        await new Promise(resolve => setTimeout(resolve, delay));
        
        await fetchAIModuleData(aiModuleId);
        
        // Show accordion state AFTER refresh
        
        // Force a small delay to ensure state updates are processed
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Ensure questionsGenerated is set to true after refresh
        setQuestionsGenerated(true);
        
        // Reset the question generation choice after successful completion
        setQuestionGenerationChoice(null);
      } catch (error) {
        console.error('❌ FRONTEND: Error refreshing AI Module data:', error);
        // Even if refresh fails, set questionsGenerated to true since questions were added
        setQuestionsGenerated(true);
        
        // Reset the question generation choice even on error
        setQuestionGenerationChoice(null);
      }
    } else {
      // If no aiModuleId, still set questionsGenerated to true
      setQuestionsGenerated(true);
    }
    
    // Clear loading state
    setAddingManualQuestions(false);
  };

  // Handle Edit Question (both manual and AI-generated)
  const handleEditManualQuestion = (candidateId, questionIndex, question) => {
    // Extract question ID - prefer questionId, then manualQuestionId, then _id
    const questionId = question.questionId || question.manualQuestionId || question._id;
    
    setEditingManualQuestion({
      candidateId,
      questionIndex, // Keep for backward compatibility
      questionId,    // Add question ID for more reliable identification
      question: { ...question },
    });
    setShowEditQuestionModal(true);
  };

  // Handle Delete Question (both manual and AI-generated)
  const handleDeleteManualQuestion = (candidateId, questionIndex, question) => {
    // Extract question ID - prefer questionId, then manualQuestionId, then _id
    const questionId = question.questionId || question.manualQuestionId || question._id;
    
    setQuestionToDelete({
      candidateId,
      questionIndex, // Keep for backward compatibility
      questionId,    // Add question ID for more reliable identification
      question,
    });
    setShowDeleteConfirmDialog(true);
  };

  // Confirm Delete Question (both manual and AI-generated)
  const confirmDeleteManualQuestion = async () => {
    if (!questionToDelete || !aiModuleId) return;

    try {
      setLoading(true);
      
      const response = await aiModuleAPI.deleteManualQuestion({
        aiModuleId,
        candidateId: questionToDelete.candidateId,
        questionId: questionToDelete.questionId,     // Preferred: use question ID
        questionIndex: questionToDelete.questionIndex, // Fallback: use question index
      });

      if (response.data.success) {
        
        // Clear current state first to force a complete refresh
        setGeneratedQuestions([]);
        setQuestionsGenerated(false);
        
        // Wait a moment for state to clear
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Refresh the AI Module data
        await fetchAIModuleData(aiModuleId);
        
        
        toast({
          title: "Question Deleted",
          description: "The question has been successfully deleted.",
        });
      } else {
        throw new Error(response.data.message || "Failed to delete question");
      }
    } catch (error) {
      console.error("Error deleting manual question:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete question",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setShowDeleteConfirmDialog(false);
      setQuestionToDelete(null);
    }
  };

  // Save Edited Question (both manual and AI-generated)
  const saveEditedManualQuestion = async (editedQuestion) => {
    if (!editingManualQuestion || !aiModuleId) return;

    try {
      setLoading(true);
      
      const response = await aiModuleAPI.updateManualQuestion({
        aiModuleId,
        candidateId: editingManualQuestion.candidateId,
        questionId: editingManualQuestion.questionId,     // Preferred: use question ID
        questionIndex: editingManualQuestion.questionIndex, // Fallback: use question index
        question: editedQuestion,
      });

      if (response.data.success) {
        // Refresh the AI Module data
        await fetchAIModuleData(aiModuleId);
        
        toast({
          title: "Question Updated",
          description: "The question has been successfully updated.",
        });
        
        setShowEditQuestionModal(false);
        setEditingManualQuestion(null);
      } else {
        throw new Error(response.data.message || "Failed to update question");
      }
    } catch (error) {
      console.error("Error updating manual question:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update question",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Skills are now read-only during editing - no add/remove functionality

  // Check for candidates without resumes
  const checkCandidatesWithoutResumes = () => {
    const candidatesWithoutResumes = selectedExistingCandidates.filter(
      (candidate) => !candidate.resume || candidate.resume.trim() === ""
    );
    setCandidatesWithoutResumes(candidatesWithoutResumes);
  };

  // Check for candidates without resumes when candidates change
  useEffect(() => {
    if (selectedExistingCandidates.length > 0) {
      checkCandidatesWithoutResumes();
      // Don't automatically set activeTab - let user click View button to open dialog
      // Only reset activeTab if the currently selected candidate is no longer in the list
      if (
        activeTab &&
        !selectedExistingCandidates.find((c) => c._id === activeTab)
      ) {
        setActiveTab(null);
      }
    }
  }, [selectedExistingCandidates, activeTab]);

  // Helper function to calculate exact months between two dates
  const calculateMonthsDifference = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();

    // If the end day is before the start day, subtract a month
    if (end.getDate() < start.getDate()) {
      months--;
    }

    // Convert years to months and add
    const totalMonths = years * 12 + months;

    // Ensure we don't return negative values
    return Math.max(0, totalMonths);
  };

  // Helper function to calculate years of experience from aiParsedData
  const calculateExperienceYears = (aiParsedData) => {
    if (!aiParsedData) return 0;

    // First check if totalExperience is available
    if (
      aiParsedData.experience?.totalExperience?.yearsInDecimal !== undefined
    ) {
      return aiParsedData.experience.totalExperience.yearsInDecimal;
    }

    if (aiParsedData.experience?.totalExperience?.years !== undefined) {
      const years = aiParsedData.experience.totalExperience.years || 0;
      const months = aiParsedData.experience.totalExperience.months || 0;
      return years + months / 12;
    }

    // If no totalExperience, calculate from experience details
    if (
      aiParsedData.experience?.details &&
      Array.isArray(aiParsedData.experience.details)
    ) {
      let totalMonths = 0;
      const currentDate = new Date();

      aiParsedData.experience.details.forEach((exp) => {
        if (exp.durationMonths) {
          totalMonths += exp.durationMonths;
        } else if (exp.startDate || exp.endDate) {
          // Calculate duration from dates
          const startDate = exp.startDate || "2020-01-01"; // Default if no start date
          let endDate;

          // Handle "Present" case - use current date
          if (!exp.endDate || exp.endDate.toLowerCase() === "present") {
            endDate = currentDate;
          } else {
            endDate = new Date(exp.endDate);
          }

          // Calculate exact months difference using helper function
          const monthsDiff = calculateMonthsDifference(startDate, endDate);
          totalMonths += monthsDiff;
        }
      });

      return totalMonths / 12; // Convert to years
    }

    return 0;
  };

  // Helper function to format experience years
  const formatExperienceYears = (years) => {
    if (years === 0) return "0 years";
    if (years < 1) {
      const months = Math.round(years * 12);
      return `${months} month${months !== 1 ? "s" : ""}`;
    }

    const wholeYears = Math.floor(years);
    const remainingMonths = Math.round((years - wholeYears) * 12);

    if (remainingMonths === 0) {
      return `${wholeYears} year${wholeYears !== 1 ? "s" : ""}`;
    }

    return `${wholeYears} year${
      wholeYears !== 1 ? "s" : ""
    }, ${remainingMonths} month${remainingMonths !== 1 ? "s" : ""}`;
  };

  // Helper function to format experience for Step 3 display
  const formatExperienceForStep3 = (aiParsedData) => {
    const years = aiParsedData?.experience?.totalExperience?.years || 0;
    const months = aiParsedData?.experience?.totalExperience?.months || 0;

    // If both years and months are 0, show "Fresher"
    if (years === 0 && months === 0) {
      return "Fresher";
    }

    // Format as "X years, Y months" or just "X years" or "Y months"
    if (years > 0 && months > 0) {
      return `${years} year${years !== 1 ? "s" : ""}, ${months} month${
        months !== 1 ? "s" : ""
      }`;
    } else if (years > 0) {
      return `${years} year${years !== 1 ? "s" : ""}`;
    } else {
      return `${months} month${months !== 1 ? "s" : ""}`;
    }
  };

  // Helper function to move candidate between eligible and not eligible
  const handleMoveCandidateEligibility = (candidateId, currentTab) => {
    const candidateData = formData.candidateResumeData?.find(
      (data) => data.candidateId === candidateId
    );
    
    if (!candidateData) {
      toast({
        title: "Error",
        description: "Candidate data not found",
        variant: "destructive",
      });
      return;
    }
    
    const candidateName = candidateData.candidateName || "Candidate";
    const targetTab = currentTab === "eligible" ? "Not Eligible" : "Eligible";
    
    // Update the candidate's manual override flag
    const updatedCandidateResumeData = formData.candidateResumeData.map((data) => {
      if (data.candidateId === candidateId) {
        return {
          ...data,
          manualEligibilityOverride: {
            isOverridden: true,
            overriddenTo: currentTab === "eligible" ? "notEligible" : "eligible",
            overriddenAt: new Date().toISOString(),
            originalSkillsMatching: data.skillsMatching?.percentage ?? 0,
          },
        };
      }
      return data;
    });
    
    setFormData((prev) => ({
      ...prev,
      candidateResumeData: updatedCandidateResumeData,
    }));
    
    // Mark as having unsaved changes
    setHasUnsavedChanges(true);
    
    toast({
      title: "Candidate Moved",
      description: `${candidateName} manually moved to ${targetTab}. Original skills matching (${candidateData.skillsMatching?.percentage ?? 0}%) preserved.`,
    });
  };

  // Helper function to categorize candidates by eligibility
  const categorizeCandidatesByEligibility = () => {
    const eligible = [];
    const notEligible = [];
    
    selectedExistingCandidates.forEach((candidate) => {
      const candidateData = formData.candidateResumeData?.find(
        (data) => data.candidateId === candidate._id
      );
      
      if (!candidateData) {
        console.warn('⚠️ No resume data for candidate:', {
          candidateId: candidate._id,
          candidateName: candidate.name
        });
        return;
      }
      
      // Check if candidate has manual override
      if (candidateData.manualEligibilityOverride?.isOverridden) {
        
        // Use manual override
        if (candidateData.manualEligibilityOverride.overriddenTo === "eligible") {
          eligible.push(candidate);
        } else {
          notEligible.push(candidate);
        }
        return;
      }
      
      // Get skills matching percentage
      const skillsMatchingPercentage = candidateData.skillsMatching?.percentage ?? 0;
      
      // Get JD pass percentage
      const jdPassPercentage = formData.jobDescription?.passPercentage ?? 0;
      
      // Get candidate experience
      const candidateExperienceYears = candidateData.aiParsedData?.experience?.totalExperience?.years || 0;
      const candidateExperienceMonths = candidateData.aiParsedData?.experience?.totalExperience?.months || 0;
      const candidateTotalExperience = candidateExperienceYears + (candidateExperienceMonths / 12);
      
      // Get JD experience requirements
      const jdExperienceLevel = formData.jobDescription?.experienceLevel;
      const jdMinYears = formData.jobDescription?.experienceRange?.minYears ?? 0;
      const jdMaxYears = formData.jobDescription?.experienceRange?.maxYears ?? 999;
      
      // Check skills matching
      const skillsMatch = skillsMatchingPercentage >= jdPassPercentage;
      
      // Check experience matching
      let experienceMatch = false;
      
      if (jdExperienceLevel === 'fresher') {
        // For fresher positions, candidate should have 0-1 years of experience
        experienceMatch = candidateTotalExperience <= 1;
      } else if (jdExperienceLevel === 'experienced') {
        // For experienced positions, candidate should be within the range
        experienceMatch = candidateTotalExperience >= jdMinYears && candidateTotalExperience <= jdMaxYears;
      } else {
        // If no experience level specified, consider experience as matching
        experienceMatch = true;
      }
      
      
      // Categorize based on BOTH skills AND experience matching
      if (skillsMatch && experienceMatch) {
        eligible.push(candidate);
      } else {
        notEligible.push(candidate);
      }
    });
    
    
    return { eligible, notEligible };
  };

  // Render individual candidate tab content
  const renderCandidateTab = (candidate) => {
    // Add safety check to prevent rendering if candidate is null/undefined
    if (!candidate || !candidate._id) {
      return null;
    }

    const candidateData = formData.candidateResumeData?.find(
      (data) => data.candidateId === candidate._id
    );

    if (!candidateData) {
      return (
        <div className="text-center py-12">
          <FileText className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Resume Data
          </h3>
          <p className="text-gray-500 mb-4">
            No resume data found for {candidate.name}
          </p>

          {/* Upload Section for Individual Candidate */}
          <div className="max-w-md mx-auto space-y-4">
            <div className="space-y-3">
              <Label className="flex items-center gap-2 justify-center">
                <FileText className="h-4 w-4" />
                Upload Resume for {candidate.name}{" "}
                <span className="text-red-500">*</span>
              </Label>
              <p className="text-xs text-gray-500">
                Only PDF format is allowed
              </p>

              <div className="flex items-center gap-2">
                <Input
                  id={`resume-upload-${candidate._id}`}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(e) => handleResumeFileChange(candidate._id, e)}
                  className="flex-1"
                  disabled={uploadingResumes.has(candidate._id)}
                />
                {candidateResumeFiles.has(candidate._id) && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeResumeFile(candidate._id)}
                    disabled={uploadingResumes.has(candidate._id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {candidateResumeFiles.has(candidate._id) && (
                <p className="text-sm text-green-600 flex items-center gap-1 justify-center">
                  <FileText className="h-4 w-4" />
                  {candidateResumeFiles.get(candidate._id)?.name}
                </p>
              )}
            </div>

            {/* Upload Actions */}
            <div className="flex items-center justify-center gap-3">
              {candidateResumeFiles.has(candidate._id) && (
                <Button
                  onClick={() =>
                    handleResumeUpload(
                      candidate._id,
                      candidateResumeFiles.get(candidate._id)
                    )
                  }
                  disabled={uploadingResumes.has(candidate._id)}
                  className="flex items-center gap-2"
                >
                  {uploadingResumes.has(candidate._id) ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      Upload & Analyze Resume
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }

    const isEditing = editingCandidate === candidate._id;
    const aiParsedData = candidateData.aiParsedData || {};
    const experienceYears = calculateExperienceYears(aiParsedData);

    return (
      <div className="space-y-6">
        {/* Header with candidate name and actions */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
              {candidateData.candidateName?.charAt(0)?.toUpperCase() ||
                candidate.name?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <h4 className="text-xl font-semibold text-gray-900">
                {candidateData.candidateName || candidate.name}
              </h4>
              {aiParsedData.candidate?.currentRole && (
                <p className="text-sm text-gray-600">
                  {aiParsedData.candidate.currentRole}
                </p>
              )}
              {aiParsedData.candidate?.location && (
                <p className="text-xs text-gray-500">
                  {aiParsedData.candidate.location}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {experienceYears > 0 && (
              <Badge
                variant="outline"
                className="flex items-center gap-1 px-3 py-1"
              >
                <Clock className="h-3 w-3" />
                {formatExperienceYears(experienceYears)}
              </Badge>
            )}
            {isEditing ? (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => saveCandidateEditedData(candidate._id)}
                  disabled={savingEdit}
                  className="text-xs"
                >
                  {savingEdit ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : null}
                  Save
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={cancelEditingCandidate}
                  className="text-xs"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    startEditingCandidate(candidate._id, candidateData)
                  }
                  disabled={extractingResume}
                  className="text-xs"
                >
                  Edit
                </Button>

              </div>
            )}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Skills Section */}
            <Card className="p-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Brain className="h-5 w-5 text-blue-600" />
                  <Label className="text-base font-semibold">Skills</Label>
                </div>
                {isEditing ? (
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {editFormData.skills?.map((skill, skillIndex) => (
                        <div
                          key={skillIndex}
                          className="bg-gray-100 rounded-lg px-3 py-2 border border-gray-200"
                        >
                          <span className="text-sm font-medium text-gray-700">
                            {skill}
                          </span>
                        </div>
                      ))}
                    </div>
                    {/* Skills are now read-only during editing - no add/remove functionality */}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Show categorized skills if available from AI parsing */}
                    {aiParsedData.skills &&
                    Object.keys(aiParsedData.skills).length > 0 ? (
                      <div className="skill-category-grid">
                        {Object.entries(aiParsedData.skills).map(
                          ([category, skills]) => {
                            if (!skills || skills.length === 0) return null;

                            const categoryClasses = {
                              frontend: "skill-badge-frontend",
                              backend: "skill-badge-backend",
                              database: "skill-badge-database",
                              tools: "skill-badge-tools",
                              other: "skill-badge-other",
                            };

                            return (
                              <div key={category} className="space-y-2">
                                <Label className="text-sm font-medium text-gray-700 capitalize">
                                  {category === "frontend"
                                    ? "🎨 Frontend"
                                    : category === "backend"
                                    ? "⚙️ Backend"
                                    : category === "database"
                                    ? "🗄️ Database"
                                    : category === "tools"
                                    ? "🔧 Tools"
                                    : category === "other"
                                    ? "📦 Other"
                                    : category}
                                </Label>
                                <div className="flex flex-wrap gap-2">
                                  {skills.map((skill, index) => (
                                    <Badge
                                      key={index}
                                      className={`text-sm px-3 py-1 ${
                                        categoryClasses[category] ||
                                        categoryClasses.other
                                      }`}
                                    >
                                      {skill}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                        )}
                      </div>
                    ) : (
                      // Fallback to simple skills list
                      <div className="flex flex-wrap gap-2">
                        {candidateData.skills?.length > 0 ? (
                          candidateData.skills.map((skill, index) => (
                            <Badge
                              key={index}
                              variant="secondary"
                              className="text-sm px-3 py-1"
                            >
                              {skill}
                            </Badge>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 italic">
                            No skills extracted
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>

            {/* Experience Section */}
            <Card className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Clock className="h-5 w-5 text-green-600" />
                    <Label className="text-base font-semibold">
                      Experience
                    </Label>
                    {experienceYears > 0 && (
                      <Badge variant="outline" className="text-sm ml-2">
                        Total: {formatExperienceYears(experienceYears)}
                      </Badge>
                    )}
                  </div>
                </div>
                {isEditing ? (
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                      <div className="flex items-center gap-2 mb-3">
                        <Clock className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-900">
                          Edit Total Experience:
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-blue-800 mb-1">
                            Years
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={editFormData.experienceYears ?? 0}
                            onChange={(e) => {
                              const years = parseInt(e.target.value) ?? 0;
                              const months = editFormData.experienceMonths ?? 0;
                              setEditFormData((prev) => ({
                                ...prev,
                                experienceYears: years,
                                experienceDecimal: years + months / 12,
                              }));
                            }}
                            className="w-full p-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-400"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-blue-800 mb-1">
                            Months
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="11"
                            value={editFormData.experienceMonths ?? 0}
                            onChange={(e) => {
                              const months = parseInt(e.target.value) ?? 0;
                              const years = editFormData.experienceYears ?? 0;
                              setEditFormData((prev) => ({
                                ...prev,
                                experienceMonths: months,
                                experienceDecimal: years + months / 12,
                              }));
                            }}
                            className="w-full p-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-400"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Show only total experience summary */}
                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-900">
                          Total Experience Summary:
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-blue-800">
                        <div>
                          Years:{" "}
                          {aiParsedData.experience?.totalExperience?.years ??
                            Math.floor(experienceYears)}
                        </div>
                        <div>
                          Months:{" "}
                          {aiParsedData.experience?.totalExperience?.months ??
                            Math.round((experienceYears % 1) * 12)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>
            
            {/* Genuinity Analysis Section */}
            {candidateData.genuinityAnalysis && (
              <Card className="p-6 border-2 border-blue-200 bg-blue-50/30">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-5 w-5 text-blue-600" />
                      <Label className="text-base font-semibold text-blue-900">
                        ✅ Resume Genuineness Score: {candidateData.genuinityAnalysis.genuinityScore !== null && candidateData.genuinityAnalysis.genuinityScore !== undefined ? `${candidateData.genuinityAnalysis.genuinityScore}%` : 'Analysis in progress...'}
                      </Label>
                    </div>
                  </div>

                  {/* Resume file name */}
                  <div className="text-sm text-gray-600 italic">
                    (Based on the resume you shared: {candidateData.resumeFileName || candidateData.candidateName + "'s resume"})
                  </div>

                  {/* How this score is calculated */}
                  {candidateData.genuinityAnalysis.genuinityScore !== null && 
                   candidateData.genuinityAnalysis.genuinityScore !== undefined ? (
                  <>
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <div className="text-sm font-semibold text-gray-800 mb-3">
                      🔍 How this score is calculated
                    </div>
                    
                    {/* Category Breakdown */}
                    <div className="space-y-3">
                      {candidateData.genuinityAnalysis.categoryBreakdown && 
                       candidateData.genuinityAnalysis.categoryBreakdown.length > 0 ? (
                        candidateData.genuinityAnalysis.categoryBreakdown.map((category, index) => (
                          <div key={index} className="border-l-4 border-blue-400 pl-4 py-2">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-800">
                                  {index + 1}. {category.category} – {category.score}%
                                </div>
                              </div>
                            </div>
                            <div className="text-xs text-gray-600 mb-1">
                              {category.details}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge 
                                variant={
                                  category.status === "Very genuine" ? "default" :
                                  category.status === "Strong positive" ? "default" :
                                  category.status === "Minor issue" ? "secondary" :
                                  "destructive"
                                }
                                className="text-xs"
                              >
                                {category.status === "Very genuine" ? "✅ Very genuine" :
                                 category.status === "Strong positive" ? "✅ Strong positive factor" :
                                 category.status === "Minor issue" ? "⚠️ Minor issue" :
                                 "❌ Concern"}
                              </Badge>
                            </div>
                          </div>
                        ))
                      ) : (
                        // Fallback to old format if categoryBreakdown is not available
                        <>
                          {candidateData.genuinityAnalysis.analysis.educationTimeline && (
                            <div className="border-l-4 border-blue-400 pl-4 py-2">
                              <div className="text-sm font-medium text-gray-800 mb-1">
                                1. Education & Timeline Consistency – {candidateData.genuinityAnalysis.analysis.educationTimeline.score}%
                              </div>
                              <div className="text-xs text-gray-600">
                                {candidateData.genuinityAnalysis.analysis.educationTimeline.findings}
                              </div>
                            </div>
                          )}
                          {candidateData.genuinityAnalysis.analysis.skillsExperienceMatch && (
                            <div className="border-l-4 border-blue-400 pl-4 py-2">
                              <div className="text-sm font-medium text-gray-800 mb-1">
                                2. Skills vs Experience Match – {candidateData.genuinityAnalysis.analysis.skillsExperienceMatch.score}%
                              </div>
                              <div className="text-xs text-gray-600">
                                {candidateData.genuinityAnalysis.analysis.skillsExperienceMatch.findings}
                              </div>
                            </div>
                          )}
                          {candidateData.genuinityAnalysis.analysis.projectsCredibility && (
                            <div className="border-l-4 border-blue-400 pl-4 py-2">
                              <div className="text-sm font-medium text-gray-800 mb-1">
                                3. Projects Credibility – {candidateData.genuinityAnalysis.analysis.projectsCredibility.score}%
                              </div>
                              <div className="text-xs text-gray-600">
                                {candidateData.genuinityAnalysis.analysis.projectsCredibility.findings}
                              </div>
                            </div>
                          )}
                          {candidateData.genuinityAnalysis.analysis.professionalSummary && (
                            <div className="border-l-4 border-blue-400 pl-4 py-2">
                              <div className="text-sm font-medium text-gray-800 mb-1">
                                4. Professional Summary – {candidateData.genuinityAnalysis.analysis.professionalSummary.score}%
                              </div>
                              <div className="text-xs text-gray-600">
                                {candidateData.genuinityAnalysis.analysis.professionalSummary.findings}
                              </div>
                            </div>
                          )}
                          {candidateData.genuinityAnalysis.analysis.contactAuthenticity && (
                            <div className="border-l-4 border-blue-400 pl-4 py-2">
                              <div className="text-sm font-medium text-gray-800 mb-1">
                                5. Contact & Online Presence – {candidateData.genuinityAnalysis.analysis.contactAuthenticity.score}%
                              </div>
                              <div className="text-xs text-gray-600">
                                {candidateData.genuinityAnalysis.analysis.contactAuthenticity.findings}
                              </div>
                            </div>
                          )}
                          {candidateData.genuinityAnalysis.analysis.redFlagsCheck && (
                            <div className="border-l-4 border-blue-400 pl-4 py-2">
                              <div className="text-sm font-medium text-gray-800 mb-1">
                                6. Red Flags Check – {candidateData.genuinityAnalysis.analysis.redFlagsCheck.score}%
                              </div>
                              <div className="text-xs text-gray-600">
                                {candidateData.genuinityAnalysis.analysis.redFlagsCheck.findings}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Final Breakdown Table */}
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <div className="text-sm font-semibold text-gray-800 mb-3">
                      📊 Final Breakdown
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 px-3 font-medium text-gray-700">Category</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-700">Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {candidateData.genuinityAnalysis.categoryBreakdown && 
                           candidateData.genuinityAnalysis.categoryBreakdown.length > 0 ? (
                            candidateData.genuinityAnalysis.categoryBreakdown.map((category, index) => (
                              <tr key={index} className="border-b border-gray-100">
                                <td className="py-2 px-3 text-gray-700">{category.category}</td>
                                <td className="py-2 px-3 text-right font-medium text-gray-900">{category.score}%</td>
                              </tr>
                            ))
                          ) : (
                            // Fallback to old format
                            <>
                              {candidateData.genuinityAnalysis.analysis.educationTimeline && (
                                <tr className="border-b border-gray-100">
                                  <td className="py-2 px-3 text-gray-700">Education & Timeline</td>
                                  <td className="py-2 px-3 text-right font-medium text-gray-900">
                                    {candidateData.genuinityAnalysis.analysis.educationTimeline.score}%
                                  </td>
                                </tr>
                              )}
                              {candidateData.genuinityAnalysis.analysis.skillsExperienceMatch && (
                                <tr className="border-b border-gray-100">
                                  <td className="py-2 px-3 text-gray-700">Skills–Experience Match</td>
                                  <td className="py-2 px-3 text-right font-medium text-gray-900">
                                    {candidateData.genuinityAnalysis.analysis.skillsExperienceMatch.score}%
                                  </td>
                                </tr>
                              )}
                              {candidateData.genuinityAnalysis.analysis.projectsCredibility && (
                                <tr className="border-b border-gray-100">
                                  <td className="py-2 px-3 text-gray-700">Projects Credibility</td>
                                  <td className="py-2 px-3 text-right font-medium text-gray-900">
                                    {candidateData.genuinityAnalysis.analysis.projectsCredibility.score}%
                                  </td>
                                </tr>
                              )}
                              {candidateData.genuinityAnalysis.analysis.professionalSummary && (
                                <tr className="border-b border-gray-100">
                                  <td className="py-2 px-3 text-gray-700">Professional Summary</td>
                                  <td className="py-2 px-3 text-right font-medium text-gray-900">
                                    {candidateData.genuinityAnalysis.analysis.professionalSummary.score}%
                                  </td>
                                </tr>
                              )}
                              {candidateData.genuinityAnalysis.analysis.contactAuthenticity && (
                                <tr className="border-b border-gray-100">
                                  <td className="py-2 px-3 text-gray-700">Contact Authenticity</td>
                                  <td className="py-2 px-3 text-right font-medium text-gray-900">
                                    {candidateData.genuinityAnalysis.analysis.contactAuthenticity.score}%
                                  </td>
                                </tr>
                              )}
                              {candidateData.genuinityAnalysis.analysis.redFlagsCheck && (
                                <tr className="border-b border-gray-100">
                                  <td className="py-2 px-3 text-gray-700">Red Flags Check</td>
                                  <td className="py-2 px-3 text-right font-medium text-gray-900">
                                    {candidateData.genuinityAnalysis.analysis.redFlagsCheck.score}%
                                  </td>
                                </tr>
                              )}
                            </>
                          )}
                          <tr className="border-t-2 border-blue-300 bg-blue-50">
                            <td className="py-2 px-3 font-semibold text-gray-900">⭐ Overall Genuineness Score</td>
                            <td className="py-2 px-3 text-right font-bold text-blue-700 text-base">
                              {candidateData.genuinityAnalysis.genuinityScore}%
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Red Flags */}
                  {candidateData.genuinityAnalysis.redFlags && candidateData.genuinityAnalysis.redFlags.length > 0 && (
                    <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                      <div className="text-xs font-medium text-red-700 mb-2">⚠️ Red Flags:</div>
                      <ul className="list-disc list-inside space-y-1">
                        {candidateData.genuinityAnalysis.redFlags.map((flag, index) => (
                          <li key={index} className="text-xs text-red-600">{flag}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Positive Indicators */}
                  {candidateData.genuinityAnalysis.positiveIndicators && candidateData.genuinityAnalysis.positiveIndicators.length > 0 && (
                    <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                      <div className="text-xs font-medium text-green-700 mb-2">✓ Positive Indicators:</div>
                      <ul className="list-disc list-inside space-y-1">
                        {candidateData.genuinityAnalysis.positiveIndicators.map((indicator, index) => (
                          <li key={index} className="text-xs text-green-600">{indicator}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Calculation Reasoning */}
                  {candidateData.genuinityAnalysis.calculationReasoning && (
                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                      <div className="text-xs font-medium text-blue-800 mb-1">📐 How the score was calculated:</div>
                      <p className="text-xs text-blue-900">
                        {candidateData.genuinityAnalysis.calculationReasoning}
                      </p>
                    </div>
                  )}

                  {/* Recommendation */}
                  <div className="bg-blue-100 rounded-lg p-3 border border-blue-300">
                    <div className="text-xs font-medium text-blue-800 mb-1">Recommendation:</div>
                    <p className="text-sm text-blue-900 font-medium">
                      {candidateData.genuinityAnalysis.recommendation}
                    </p>
                  </div>

                  {/* Analysis Metadata */}
                  <div className="text-xs text-gray-500 text-center pt-2 border-t border-blue-200">
                    Analyzed by {candidateData.genuinityAnalysis.analyzedBy} • {candidateData.genuinityAnalysis.model}
                  </div>
                  </>
                  ) : (
                    /* Show message when genuinity analysis is incomplete or failed */
                    <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                      <div className="flex items-start gap-3">
                        <div className="text-yellow-600 text-2xl">⚠️</div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-yellow-800 mb-2">
                            Genuinity Analysis Unavailable
                          </div>
                          <div className="text-xs text-yellow-700 mb-3">
                            {candidateData.genuinityAnalysis.error 
                              ? `Analysis failed: ${candidateData.genuinityAnalysis.error}`
                              : 'The genuinity analysis could not be completed for this resume. This may be due to:'}
                          </div>
                          {!candidateData.genuinityAnalysis.error && (
                            <ul className="list-disc list-inside text-xs text-yellow-700 space-y-1 mb-3">
                              <li>Resume content is too short or incomplete</li>
                              <li>OpenAI API quota exceeded or rate limit reached</li>
                              <li>Network connectivity issues during analysis</li>
                              <li>Resume format is not supported for detailed analysis</li>
                            </ul>
                          )}
                          <div className="text-xs text-yellow-800 font-medium">
                            💡 Recommendation: {candidateData.genuinityAnalysis.recommendation || 'Manual verification recommended. Review the resume details above and conduct a thorough interview.'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Education Section */}
            <Card className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Settings className="h-5 w-5 text-purple-600" />
                    <Label className="text-base font-semibold">Education</Label>
                  </div>
                </div>

                {isEditing && editFormData.educationEditMode ? (
                  <div className="space-y-4">
                    {editFormData.educationDetails?.map((edu, index) => (
                      <div
                        key={index}
                        className="education-card border-2 border-purple-200"
                      >
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h6 className="font-medium text-purple-800">
                              Education {index + 1}
                            </h6>
                            {editFormData.educationDetails.length > 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const updatedEducation =
                                    editFormData.educationDetails.filter(
                                      (_, i) => i !== index
                                    );
                                  setEditFormData((prev) => ({
                                    ...prev,
                                    educationDetails: updatedEducation,
                                  }));
                                }}
                                className="text-red-600 hover:text-red-800 h-6 w-6 p-0"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {/* Qualification */}
                            <div>
                              <Label className="text-xs font-medium text-gray-700">
                                Qualification *
                              </Label>
                              <Select
                                value={edu.qualification || ""}
                                onValueChange={(value) => {
                                  const updatedEducation = [
                                    ...editFormData.educationDetails,
                                  ];
                                  updatedEducation[index] = {
                                    ...updatedEducation[index],
                                    qualification: value,
                                  };
                                  setEditFormData((prev) => ({
                                    ...prev,
                                    educationDetails: updatedEducation,
                                  }));

                                  // Clear validation error when field is filled
                                  const errors = {
                                    ...educationValidationErrors,
                                  };
                                  if (value) {
                                    delete errors[`${index}_qualification`];
                                  }
                                  setEducationValidationErrors(errors);
                                }}
                              >
                                <SelectTrigger
                                  className={`h-8 text-xs ${
                                    educationValidationErrors[
                                      `${index}_qualification`
                                    ]
                                      ? "border-red-500"
                                      : ""
                                  }`}
                                >
                                  <SelectValue placeholder="Select qualification" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="PhD/Doctorate">
                                    PhD/Doctorate
                                  </SelectItem>
                                  <SelectItem value="Masters/Post Graduate">
                                    Masters/Post Graduate
                                  </SelectItem>
                                  <SelectItem value="Bachelor/Graduate">
                                    Bachelor/Graduate
                                  </SelectItem>
                                  <SelectItem value="Diploma">
                                    Diploma
                                  </SelectItem>
                                  <SelectItem value="Certificate">
                                    Certificate
                                  </SelectItem>
                                  <SelectItem value="Higher Secondary/12th">
                                    Higher Secondary/12th
                                  </SelectItem>
                                  <SelectItem value="Secondary/10th">
                                    Secondary/10th
                                  </SelectItem>
                                  <SelectItem value="Other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                              {educationValidationErrors[
                                `${index}_qualification`
                              ] && (
                                <p className="text-xs text-red-600 mt-1">
                                  {
                                    educationValidationErrors[
                                      `${index}_qualification`
                                    ]
                                  }
                                </p>
                              )}
                            </div>

                            {/* Year of Passing */}
                            <div>
                              <Label className="text-xs font-medium text-gray-700">
                                Year of Passing
                              </Label>
                              <Select
                                value={edu.yearOfPassing?.toString() || ""}
                                onValueChange={(value) => {
                                  const currentYear = new Date().getFullYear();
                                  const updatedEducation = [
                                    ...editFormData.educationDetails,
                                  ];
                                  updatedEducation[index] = {
                                    ...updatedEducation[index],
                                    yearOfPassing:
                                      value === "present"
                                        ? "Present"
                                        : parseInt(value),
                                  };
                                  setEditFormData((prev) => ({
                                    ...prev,
                                    educationDetails: updatedEducation,
                                  }));

                                  // Real-time validation
                                  const selectedYear =
                                    value === "present"
                                      ? currentYear
                                      : parseInt(value);
                                  const errors = {
                                    ...educationValidationErrors,
                                  };

                                  if (selectedYear > currentYear) {
                                    errors[
                                      `${index}_year`
                                    ] = `Year cannot be greater than ${currentYear}`;
                                  } else {
                                    delete errors[`${index}_year`];
                                  }

                                  setEducationValidationErrors(errors);
                                }}
                              >
                                <SelectTrigger
                                  className={`h-8 text-xs ${
                                    educationValidationErrors[`${index}_year`]
                                      ? "border-red-500"
                                      : ""
                                  }`}
                                >
                                  <SelectValue placeholder="Select year" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="present">
                                    Present (Ongoing)
                                  </SelectItem>
                                  {(() => {
                                    const currentYear =
                                      new Date().getFullYear();
                                    const years = [];
                                    for (
                                      let year = currentYear;
                                      year >= 1950;
                                      year--
                                    ) {
                                      years.push(
                                        <SelectItem
                                          key={year}
                                          value={year.toString()}
                                        >
                                          {year}
                                        </SelectItem>
                                      );
                                    }
                                    return years;
                                  })()}
                                </SelectContent>
                              </Select>
                              {educationValidationErrors[`${index}_year`] && (
                                <p className="text-xs text-red-600 mt-1">
                                  {educationValidationErrors[`${index}_year`]}
                                </p>
                              )}
                            </div>

                            {/* Specialization */}
                            <div>
                              <Label className="text-xs font-medium text-gray-700">
                                Specialization/Field
                              </Label>
                              <Input
                                value={edu.specialization || ""}
                                onChange={(e) => {
                                  const updatedEducation = [
                                    ...editFormData.educationDetails,
                                  ];
                                  updatedEducation[index] = {
                                    ...updatedEducation[index],
                                    specialization: e.target.value,
                                  };
                                  setEditFormData((prev) => ({
                                    ...prev,
                                    educationDetails: updatedEducation,
                                  }));
                                }}
                                placeholder="e.g., Computer Science, MBA"
                                className="h-8 text-xs"
                              />
                            </div>

                            {/* Institution */}
                            <div>
                              <Label className="text-xs font-medium text-gray-700">
                                Institution *
                              </Label>
                              <Input
                                value={edu.institution || ""}
                                onChange={(e) => {
                                  const updatedEducation = [
                                    ...editFormData.educationDetails,
                                  ];
                                  updatedEducation[index] = {
                                    ...updatedEducation[index],
                                    institution: e.target.value,
                                  };
                                  setEditFormData((prev) => ({
                                    ...prev,
                                    educationDetails: updatedEducation,
                                  }));

                                  // Clear validation error when field is filled
                                  const errors = {
                                    ...educationValidationErrors,
                                  };
                                  if (e.target.value.trim()) {
                                    delete errors[`${index}_institution`];
                                  }
                                  setEducationValidationErrors(errors);
                                }}
                                placeholder="University/College name"
                                className={`h-8 text-xs ${
                                  educationValidationErrors[
                                    `${index}_institution`
                                  ]
                                    ? "border-red-500"
                                    : ""
                                }`}
                              />
                              {educationValidationErrors[
                                `${index}_institution`
                              ] && (
                                <p className="text-xs text-red-600 mt-1">
                                  {
                                    educationValidationErrors[
                                      `${index}_institution`
                                    ]
                                  }
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Add Education Button */}
                    <div className="pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newEducation = {
                            qualification: "",
                            specialization: "",
                            institution: "",
                            yearOfPassing: null,
                            score: "",
                          };
                          setEditFormData((prev) => ({
                            ...prev,
                            educationDetails: [
                              ...(prev.educationDetails || []),
                              newEducation,
                            ],
                          }));
                        }}
                        className="text-xs"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Education
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Show detailed education if available from AI parsing */}
                    {aiParsedData.education &&
                    aiParsedData.education.length > 0 ? (
                      <div className="space-y-3">
                        {aiParsedData.education.map((edu, index) => (
                          <div key={index} className="education-card">
                            <div className="space-y-2">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h6 className="font-medium text-gray-900">
                                    {edu.qualification ||
                                      "Qualification Not Specified"}
                                  </h6>
                                  {edu.specialization && (
                                    <p className="text-sm text-gray-600">
                                      Specialization: {edu.specialization}
                                    </p>
                                  )}
                                  <p className="text-sm text-gray-600">
                                    {edu.institution ||
                                      "Institution Not Specified"}
                                  </p>
                                </div>
                                <div className="text-right">
                                  {edu.yearOfPassing && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs"
                                    >
                                      {edu.yearOfPassing}
                                    </Badge>
                                  )}
                                  {edu.score && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      {edu.score}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      // Fallback to simple education text
                      <div className="education-card">
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {candidateData.education ||
                            "No education information extracted"}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>

            {/* Projects Section */}
            {aiParsedData.projects && aiParsedData.projects.length > 0 && (
              <Card className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-5 w-5 text-indigo-600" />
                    <Label className="text-base font-semibold">Projects</Label>
                  </div>
                  <div className="space-y-3">
                    {aiParsedData.projects.map((project, index) => (
                      <div key={index} className="project-card">
                        <div className="space-y-2">
                          <div className="flex items-start justify-between">
                            <div>
                              <h6 className="font-medium text-gray-900">
                                {project.name || `Project ${index + 1}`}
                              </h6>
                              {project.type && (
                                <Badge
                                  variant="outline"
                                  className="text-xs mt-1"
                                >
                                  {project.type}
                                </Badge>
                              )}
                            </div>
                            {project.year && (
                              <Badge variant="secondary" className="text-xs">
                                {project.year}
                              </Badge>
                            )}
                          </div>

                          {project.description && (
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {project.description}
                            </p>
                          )}

                          {project.technologies &&
                            project.technologies.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                <span className="text-xs text-gray-600 mr-2">
                                  Technologies:
                                </span>
                                {project.technologies.map((tech, techIndex) => (
                                  <Badge
                                    key={techIndex}
                                    variant="outline"
                                    className="text-xs"
                                  >
                                    {tech}
                                  </Badge>
                                ))}
                              </div>
                            )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            )}

            {/* Languages Section */}
            {aiParsedData.languagesKnown &&
              aiParsedData.languagesKnown.length > 0 && (
                <Card className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Users className="h-5 w-5 text-pink-600" />
                      <Label className="text-base font-semibold">
                        Languages Known
                      </Label>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {aiParsedData.languagesKnown.map((language, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="text-sm px-3 py-1 bg-pink-50 text-pink-800 border-pink-200"
                        >
                          {language}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </Card>
              )}

            {/* Personal Information */}
            {(candidateData.personalInfo || aiParsedData.candidate) && (
              <Card className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Users className="h-5 w-5 text-indigo-600" />
                    <Label className="text-base font-semibold">
                      Personal Information
                    </Label>
                  </div>

                  {isEditing ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-gray-700">
                            Full Name
                          </Label>
                          <Input
                            value={editFormData.personalInfo?.name || ""}
                            onChange={(e) =>
                              setEditFormData((prev) => ({
                                ...prev,
                                personalInfo: {
                                  ...prev.personalInfo,
                                  name: e.target.value,
                                },
                              }))
                            }
                            className="text-sm mt-1"
                            placeholder="Enter full name"
                          />
                        </div>

                        <div>
                          <Label className="text-sm font-medium text-gray-700">
                            Current Role
                          </Label>
                          <Input
                            value={editFormData.personalInfo?.currentRole || ""}
                            onChange={(e) =>
                              setEditFormData((prev) => ({
                                ...prev,
                                personalInfo: {
                                  ...prev.personalInfo,
                                  currentRole: e.target.value,
                                },
                              }))
                            }
                            className="text-sm mt-1"
                            placeholder="Enter current role"
                          />
                        </div>

                        <div>
                          <Label className="text-sm font-medium text-gray-700">
                            Location
                          </Label>
                          <Input
                            value={editFormData.personalInfo?.location || ""}
                            onChange={(e) =>
                              setEditFormData((prev) => ({
                                ...prev,
                                personalInfo: {
                                  ...prev.personalInfo,
                                  location: e.target.value,
                                },
                              }))
                            }
                            className="text-sm mt-1"
                            placeholder="Enter location"
                          />
                        </div>

                        <div>
                          <Label className="text-sm font-medium text-gray-700">
                            Phone
                          </Label>
                          <Input
                            value={editFormData.personalInfo?.phone || ""}
                            onChange={(e) =>
                              setEditFormData((prev) => ({
                                ...prev,
                                personalInfo: {
                                  ...prev.personalInfo,
                                  phone: e.target.value,
                                },
                              }))
                            }
                            className="text-sm mt-1"
                            placeholder="Enter phone number"
                          />
                        </div>

                        <div>
                          <Label className="text-sm font-medium text-gray-700">
                            Email
                          </Label>
                          <Input
                            value={editFormData.personalInfo?.email || ""}
                            onChange={(e) =>
                              setEditFormData((prev) => ({
                                ...prev,
                                personalInfo: {
                                  ...prev.personalInfo,
                                  email: e.target.value,
                                },
                              }))
                            }
                            className="text-sm mt-1"
                            placeholder="Enter email address"
                          />
                        </div>

                        <div>
                          <Label className="text-sm font-medium text-gray-700">
                            GitHub
                          </Label>
                          <Input
                            value={editFormData.personalInfo?.github || ""}
                            onChange={(e) =>
                              setEditFormData((prev) => ({
                                ...prev,
                                personalInfo: {
                                  ...prev.personalInfo,
                                  github: e.target.value,
                                },
                              }))
                            }
                            className="text-sm mt-1"
                            placeholder="Enter GitHub URL"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <Label className="text-sm font-medium text-gray-700">
                            LinkedIn
                          </Label>
                          <Input
                            value={editFormData.personalInfo?.linkedin || ""}
                            onChange={(e) =>
                              setEditFormData((prev) => ({
                                ...prev,
                                personalInfo: {
                                  ...prev.personalInfo,
                                  linkedin: e.target.value,
                                },
                              }))
                            }
                            className="text-sm mt-1"
                            placeholder="Enter LinkedIn URL"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3">
                      {/* Use AI parsed data if available, fallback to personalInfo */}
                      {(aiParsedData.candidate?.fullName ||
                        candidateData.personalInfo?.name) && (
                        <div className="personal-info-row">
                          <span className="font-medium text-gray-600">
                            Full Name:
                          </span>
                          <span className="text-gray-900">
                            {aiParsedData.candidate?.fullName ||
                              candidateData.personalInfo?.name}
                          </span>
                        </div>
                      )}

                      {aiParsedData.candidate?.currentRole && (
                        <div className="personal-info-row">
                          <span className="font-medium text-gray-600">
                            Current Role:
                          </span>
                          <span className="text-gray-900">
                            {aiParsedData.candidate.currentRole}
                          </span>
                        </div>
                      )}

                      {aiParsedData.candidate?.location && (
                        <div className="personal-info-row">
                          <span className="font-medium text-gray-600">
                            Location:
                          </span>
                          <span className="text-gray-900">
                            {aiParsedData.candidate.location}
                          </span>
                        </div>
                      )}

                      {(aiParsedData.candidate?.contact?.phone ||
                        candidateData.personalInfo?.phone) && (
                        <div className="personal-info-row">
                          <span className="font-medium text-gray-600">
                            Phone:
                          </span>
                          <span className="text-gray-900">
                            {aiParsedData.candidate?.contact?.phone ||
                              candidateData.personalInfo?.phone}
                          </span>
                        </div>
                      )}

                      {(aiParsedData.candidate?.contact?.email ||
                        candidateData.personalInfo?.email) && (
                        <div className="personal-info-row">
                          <span className="font-medium text-gray-600">
                            Email:
                          </span>
                          <span className="text-gray-900">
                            {aiParsedData.candidate?.contact?.email ||
                              candidateData.personalInfo?.email}
                          </span>
                        </div>
                      )}

                      {(aiParsedData.candidate?.contact?.github ||
                        candidateData.personalInfo?.github) && (
                        <div className="personal-info-row">
                          <span className="font-medium text-gray-600">
                            GitHub:
                          </span>
                          <a
                            href={
                              aiParsedData.candidate?.contact?.github ||
                              candidateData.personalInfo?.github
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {(
                              aiParsedData.candidate?.contact?.github ||
                              candidateData.personalInfo?.github
                            )?.replace("https://", "")}
                          </a>
                        </div>
                      )}

                      {(aiParsedData.candidate?.contact?.linkedin ||
                        candidateData.personalInfo?.linkedIn) && (
                        <div className="personal-info-row">
                          <span className="font-medium text-gray-600">
                            LinkedIn:
                          </span>
                          <a
                            href={
                              (
                                aiParsedData.candidate?.contact?.linkedin ||
                                candidateData.personalInfo?.linkedIn
                              )?.startsWith("http")
                                ? aiParsedData.candidate?.contact?.linkedin ||
                                  candidateData.personalInfo?.linkedIn
                                : `https://${
                                    aiParsedData.candidate?.contact?.linkedin ||
                                    candidateData.personalInfo?.linkedIn
                                  }`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {(
                              aiParsedData.candidate?.contact?.linkedin ||
                              candidateData.personalInfo?.linkedIn
                            )?.replace("https://", "")}
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render individual candidate questions
  const renderCandidateQuestions = (candidateQuestions) => {
    // Add safety check to prevent rendering if candidateQuestions is null/undefined
    if (!candidateQuestions || !candidateQuestions.candidateId) {
      return (
        <div className="text-center py-12">
          <Brain className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Questions Generated
          </h3>
          <p className="text-gray-500">No questions found for this candidate</p>
        </div>
      );
    }

    if (candidateQuestions.error) {
      return (
        <div className="text-center py-12">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <svg
                className="w-5 h-5 text-red-600 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-red-900">
                  Question Generation Failed
                </h4>
                <p className="text-sm text-red-700 mt-1">
                  {candidateQuestions.error}
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const questions = candidateQuestions.questions || [];

    return (
      <div className="space-y-6">
        {/* Header with candidate name */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
              {candidateQuestions.candidateName?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <h4 className="text-xl font-semibold text-gray-900">
                {candidateQuestions.candidateName}
              </h4>
              <p className="text-sm text-gray-600">
                {questions.length} questions generated
              </p>
            </div>
          </div>
          {candidateQuestions.metadata && (
            <Badge variant="outline" className="text-xs">
              Generated by {candidateQuestions.metadata.generatedBy}
            </Badge>
          )}
        </div>

        {/* Category Input */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium text-blue-900">
              Category <span className="text-red-500">*</span>
            </Label>
            <Input
              value={questionCategories[candidateQuestions.candidateId] || ""}
              onChange={(e) =>
                handleCategoryChange(
                  candidateQuestions.candidateId,
                  e.target.value
                )
              }
              placeholder="Enter category for all questions (e.g., Technical, Programming, General)"
              className="bg-white"
            />
            <p className="text-xs text-blue-700">
              This category will be applied to all questions for{" "}
              {candidateQuestions.candidateName}
            </p>
          </div>
        </div>

        {/* Questions List */}
        {questions.length > 0 ? (
          <div className="space-y-4">
            <h5 className="text-lg font-medium text-gray-900">
              Generated Questions
            </h5>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {questions.map((question, index) => (
                <div
                  key={question.questionId || index}
                  className="bg-white question-card rounded-lg p-4"
                >
                  <div className="space-y-3">
                    {/* Question Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline" className="text-xs">
                          Q{index + 1}
                        </Badge>
                        <Badge
                          variant={
                            question.difficulty === "Easy"
                              ? "secondary"
                              : question.difficulty === "Hard"
                              ? "destructive"
                              : "default"
                          }
                          className="text-xs"
                        >
                          {question.difficulty}
                        </Badge>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {question.type || "multiple-choice"}
                      </Badge>
                    </div>

                    {/* Question Text */}
                    <div className="space-y-2">
                      <p className="font-medium text-gray-900">
                        {question.question}
                      </p>
                    </div>

                    {/* Skill Tags */}
                    {question.relatedSkills &&
                      question.relatedSkills.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-gray-700">
                            Based on skills:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {question.relatedSkills
                              .slice(0, 3)
                              .map((skill, skillIndex) => (
                                <Badge
                                  key={skillIndex}
                                  className="skill-tag text-xs"
                                >
                                  {skill}
                                </Badge>
                              ))}
                            {question.relatedSkills.length > 3 && (
                              <Badge
                                className="skill-tag-more text-xs"
                                onClick={(e) => {
                                  const rect = e.target.getBoundingClientRect();
                                  setSkillTagsPopup({
                                    isOpen: true,
                                    skills: question.relatedSkills.slice(3),
                                    questionId: `Q${index + 1}`,
                                    position: {
                                      x: rect.left + rect.width / 2,
                                      y: rect.top - 10,
                                    },
                                  });
                                }}
                              >
                                +{question.relatedSkills.length - 3} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}

                    {/* Options */}
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">
                        Options:
                      </p>
                      <div className="grid grid-cols-1 gap-2">
                        {question.options &&
                          question.options.map((option, optionIndex) => (
                            <div
                              key={optionIndex}
                              className={cn(
                                "flex items-center space-x-2 p-2 rounded border",
                                question.correctAnswerIndex === optionIndex
                                  ? "bg-green-50 border-green-200 text-green-800"
                                  : "bg-gray-50 border-gray-200"
                              )}
                            >
                              <span className="font-medium text-sm">
                                {String.fromCharCode(65 + optionIndex)}.
                              </span>
                              <span className="text-sm">{option}</span>
                              {question.correctAnswerIndex === optionIndex && (
                                <CheckCircle className="h-4 w-4 text-green-600 ml-auto" />
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <Brain className="mx-auto h-12 w-12 text-gray-400 mb-3" />
            <p className="text-gray-500">
              No questions available for this candidate
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            {/* Multiple Resume Upload Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-lg font-medium">Upload Resumes</Label>
                <div className="flex gap-2">
                  {multipleResumeFiles.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={clearAllResumes}
                      className="flex items-center gap-2 text-red-600 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                      Clear All
                    </Button>
                  )}
                </div>
              </div>

              {/* Skipped Resumes Alert */}
              {skippedResumes.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <X className="h-5 w-5 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-red-800 mb-2">
                        Resumes Skipped ({skippedResumes.length})
                      </h4>
                      <div className="space-y-1">
                        {skippedResumes.map((skipped, index) => (
                          <p key={index} className="text-sm text-red-700">
                            <span className="font-medium">{skipped.fileName}</span> - {skipped.reason}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Hidden File Input */}
              <input
                id="multiple-resume-upload"
                type="file"
                multiple
                accept=".pdf"
                onChange={handleMultipleResumeUpload}
                className="hidden"
              />

              {/* Upload Area */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <div className="space-y-2">
                  <p className="text-lg font-medium text-gray-900">
                    Upload Multiple Resume Files
                  </p>
                  <p className="text-sm text-gray-500">
                    Select multiple PDF files to upload candidate resumes
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('multiple-resume-upload').click()}
                    className="mt-4"
                  >
                    Choose Files
                  </Button>
                </div>
              </div>

              {/* Resume Files List */}
              {multipleResumeFiles.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">
                      Selected Files ({multipleResumeFiles.length})
                    </Label>
                    <Button
                      onClick={uploadAllMultipleResumes}
                      disabled={uploadingMultipleResumes || multipleResumeFiles.every(f => f.status === 'completed')}
                      className="flex items-center gap-2"
                    >
                      {uploadingMultipleResumes && <Loader2 className="h-4 w-4 animate-spin" />}
                      {uploadingMultipleResumes ? "Uploading..." : "Upload All"}
                    </Button>
                  </div>

                  <div className="grid gap-3 max-h-60 overflow-y-auto">
                    {multipleResumeFiles.map((resumeFile) => (
                      <div
                        key={resumeFile.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-blue-600" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {resumeFile.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {(resumeFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {resumeFile.status === 'pending' && (
                            <Badge variant="secondary" className="text-xs">
                              Pending
                            </Badge>
                          )}
                          {resumeFile.status === 'uploading' && (
                            <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">
                              <Loader2 className="h-3 w-3 animate-spin mr-1" />
                              Uploading
                            </Badge>
                          )}
                          {resumeFile.status === 'completed' && (
                            <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Completed
                            </Badge>
                          )}
                          {resumeFile.status === 'error' && (
                            <Badge variant="destructive" className="text-xs">
                              Error
                            </Badge>
                          )}
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeResumeFromList(resumeFile.id)}
                            className="h-6 w-6 p-0 text-gray-400 hover:text-red-600"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Candidates Selection */}
            <div className="space-y-9">
              <div className="flex items-center justify-between">
                <Label className="text-lg font-medium">Select Candidates</Label>
              </div>

              {/* Select Candidate Button or Selected Candidates Display */}
              {selectedExistingCandidates.length === 0 ? (
                <div className="flex justify-center items-center py-9">
                  <Button
                    type="button"
                    onClick={() => setIsCandidateSelectionModalOpen(true)}
                    className="flex items-center gap-2"
                  >
                    <Users className="h-4 w-4" />
                    Select Candidates
                  </Button>
                </div>
              ) : (
                <div className="p-8 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge 
                        variant="secondary" 
                        className="bg-blue-100 text-blue-800 border-blue-200 px-3 py-1"
                      >
                        <Users className="h-4 w-4 mr-1" />
                        {selectedExistingCandidates.length} Candidate{selectedExistingCandidates.length !== 1 ? 's' : ''} Selected
                      </Badge>
                      <div className="text-sm text-blue-700 font-medium">
                        {selectedExistingCandidates.slice(0, 3).map(candidate => candidate.name).join(', ')}
                        {selectedExistingCandidates.length > 3 && (
                          <span 
                            className="cursor-pointer hover:underline text-blue-600 hover:text-blue-800"
                            onClick={() => setIsCandidateSelectionModalOpen(true)}
                          >
                            {` +${selectedExistingCandidates.length - 3} more`}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setIsCandidateSelectionModalOpen(true)}
                      className="flex items-center gap-2 text-blue-700 border-blue-300 hover:bg-blue-100"
                    >
                      <Users className="h-4 w-4" />
                      Edit
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            {/* Show buffer/loading state when extraction is in progress or no skills extracted yet */}
            {extractingResume ||
            (formData.extractedSkills.length === 0 && !extractionAttempted) ||
            (noResumesFound && !showUploadSection) ? (
              <ResumeExtractionBuffer
                extractingResume={extractingResume}
                noResumesFound={noResumesFound}
                extractionAttempted={extractionAttempted}
                candidateCount={selectedExistingCandidates.length}
                onRetry={() => {
                  setExtractionAttempted(false);
                  setNoResumesFound(false);
                  extractResumeInformation(true);
                }}
                onUploadResumes={() => setShowUploadSection(true)}
              />
            ) : noResumesFound && showUploadSection ? (
              /* Resume Upload Section */
              <div className="space-y-6">
                <div className="text-center py-8">
                  <div className="mx-auto w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                    <Upload className="h-10 w-10 text-blue-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Upload Resume Files
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Upload resume files for the selected candidates to proceed
                    with AI analysis.
                  </p>
                </div>

                {/* Upload Interface for Each Candidate */}
                <div className="space-y-4">
                  {selectedExistingCandidates.map((candidate) => (
                    <div
                      key={candidate._id}
                      className="bg-white border border-gray-200 rounded-lg p-6"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                            {candidate.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              {candidate.name}
                            </h4>
                            <p className="text-sm text-gray-500">
                              {candidate.email}
                            </p>
                          </div>
                        </div>
                        {uploadingResumes.has(candidate._id) && (
                          <div className="flex items-center text-blue-600">
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            <span className="text-sm">Uploading...</span>
                          </div>
                        )}
                      </div>

                      <div className="space-y-3">
                        <Label className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Resume File <span className="text-red-500">*</span>
                        </Label>
                        <p className="text-xs text-gray-500">
                          Only PDF format is allowed
                        </p>

                        <div className="flex items-center gap-2">
                          <Input
                            id={`resume-upload-${candidate._id}`}
                            type="file"
                            accept=".pdf,application/pdf"
                            onChange={(e) =>
                              handleResumeFileChange(candidate._id, e)
                            }
                            className="flex-1"
                            disabled={uploadingResumes.has(candidate._id)}
                          />
                          {candidateResumeFiles.has(candidate._id) && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeResumeFile(candidate._id)}
                              disabled={uploadingResumes.has(candidate._id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>

                        {candidateResumeFiles.has(candidate._id) && (
                          <p className="text-sm text-green-600 flex items-center gap-1">
                            <FileText className="h-4 w-4" />
                            {candidateResumeFiles.get(candidate._id)?.name}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Upload Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowUploadSection(false);
                      setCandidateResumeFiles(new Map());
                    }}
                    disabled={uploadingResumes.size > 0}
                  >
                    Cancel
                  </Button>

                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600">
                      {candidateResumeFiles.size} of{" "}
                      {selectedExistingCandidates.length} files selected
                    </span>
                    <Button
                      onClick={uploadAllResumes}
                      disabled={
                        candidateResumeFiles.size === 0 ||
                        uploadingResumes.size > 0
                      }
                      className="flex items-center gap-2"
                    >
                      {uploadingResumes.size > 0 ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4" />
                          Upload & Analyze ({candidateResumeFiles.size})
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Tabular Extracted Information Display */}
                {formData.extractedSkills.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-900">
                        Resume
                      </h3>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setExtractionAttempted(false);
                            setNoResumesFound(false);
                            extractResumeInformation(true);
                          }}
                          disabled={extractingResume}
                          className="text-xs"
                        >
                          {extractingResume ? (
                            <>
                              <Loader2 className="h-3 w-3 animate-spin mr-1" />
                              Re-analyzing...
                            </>
                          ) : (
                            "Re-analyze"
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Eligibility Tabs */}
                    {formData.jobDescription?.passPercentage && (
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <Tabs value={eligibilityTab} onValueChange={setEligibilityTab}>
                          <TabsList className="grid w-full grid-cols-2 p-1 bg-gray-100">
                            <TabsTrigger 
                              value="eligible" 
                              className={cn(
                                "flex items-center gap-2 transition-all",
                                "data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md",
                                "data-[state=active]:hover:bg-green-600",
                                "data-[state=inactive]:bg-white data-[state=inactive]:text-gray-600 hover:text-gray-900"
                              )}
                            >
                              <CheckCircle className="h-4 w-4" />
                              Eligible
                              <Badge 
                                variant="secondary"
                                className={cn(
                                  "ml-1",
                                  "data-[state=active]:bg-white data-[state=active]:text-green-700",
                                  "bg-gray-200 text-gray-700"
                                )}
                              >
                                {categorizeCandidatesByEligibility().eligible.length}
                              </Badge>
                            </TabsTrigger>
                            <TabsTrigger 
                              value="notEligible" 
                              className={cn(
                                "flex items-center gap-2 transition-all",
                                "data-[state=active]:bg-red-500 data-[state=active]:text-white data-[state=active]:shadow-md",
                                "data-[state=active]:hover:bg-red-600",
                                "data-[state=inactive]:bg-white data-[state=inactive]:text-gray-600 hover:text-gray-900"
                              )}
                            >
                              <X className="h-4 w-4" />
                              Not Eligible
                              <Badge 
                                variant="secondary"
                                className={cn(
                                  "ml-1",
                                  "data-[state=active]:bg-white data-[state=active]:text-red-700",
                                  "bg-gray-200 text-gray-700"
                                )}
                              >
                                {categorizeCandidatesByEligibility().notEligible.length}
                              </Badge>
                            </TabsTrigger>
                          </TabsList>
                        </Tabs>
                      </div>
                    )}

                    {/* Tabular View for Candidates */}
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                S.No
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Candidate Name
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Email
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Phone Number
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Experience
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Genuinity
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Skills Matching
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Skills
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {(() => {
                              try {
                                
                                // Get categorized candidates
                                const { eligible, notEligible } = categorizeCandidatesByEligibility();
                                
                                // Debug logging
                              
                              // Select candidates based on active tab
                              const candidatesToDisplay = eligibilityTab === "eligible" ? eligible : notEligible;
                              
                              
                              // If no candidates in this category, show empty state
                              if (candidatesToDisplay.length === 0) {
                                return (
                                  <tr>
                                    <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                                      <div className="flex flex-col items-center gap-2">
                                        {eligibilityTab === "eligible" ? (
                                          <>
                                            <CheckCircle className="h-8 w-8 text-gray-400" />
                                            <p>No eligible candidates found</p>
                                            <p className="text-xs text-gray-400">
                                              Candidates need skills matching ≥ {formData.jobDescription?.passPercentage || 0}%
                                            </p>
                                          </>
                                        ) : (
                                          <>
                                            <X className="h-8 w-8 text-gray-400" />
                                            <p>No candidates below eligibility threshold</p>
                                            <p className="text-xs text-gray-400">
                                              All candidates meet the {formData.jobDescription?.passPercentage || 0}% requirement
                                            </p>
                                          </>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              }
                              
                              return candidatesToDisplay.map((candidate, index) => {
                              const candidateData = formData.candidateResumeData?.find(
                                (data) => data.candidateId === candidate._id
                              );
                              
                              if (!candidateData) {
                                console.error('❌ No candidate data found for:', {
                                  candidateId: candidate._id,
                                  candidateName: candidate.name,
                                  availableDataIds: formData.candidateResumeData?.map(d => d.candidateId)
                                });
                                return null;
                              }
                              
                              const aiParsedData = candidateData.aiParsedData || {};
                              const experienceYears = calculateExperienceYears(aiParsedData);
                              
                              // Get all skills from different categories
                              const allSkills = [
                                ...(aiParsedData.skills?.frontend || []),
                                ...(aiParsedData.skills?.backend || []),
                                ...(aiParsedData.skills?.database || []),
                                ...(aiParsedData.skills?.tools || []),
                                ...(aiParsedData.skills?.other || [])
                              ];
                              
                              const displaySkills = allSkills.length > 0 ? allSkills : candidateData.skills || [];
                              const visibleSkills = displaySkills.slice(0, 2);
                              const remainingSkillsCount = displaySkills.length - 2;
                              
                              // Check if manually overridden
                              const isManuallyOverridden = candidateData.manualEligibilityOverride?.isOverridden;
                              const manualOverrideTo = candidateData.manualEligibilityOverride?.overriddenTo;
                              
                              return (
                                <tr key={candidate._id} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                    {index + 1}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                                        {candidateData.candidateName?.charAt(0)?.toUpperCase() || candidate.name?.charAt(0)?.toUpperCase()}
                                      </div>
                                      <div>
                                        <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                                          {candidateData.candidateName || candidate.name}
                                          {isManuallyOverridden && (
                                            <Badge 
                                              variant="secondary" 
                                              className="text-xs bg-yellow-400 text-yellow-900 border-yellow-600"
                                              title={`Manually moved to ${manualOverrideTo === "eligible" ? "Eligible" : "Not Eligible"}`}
                                            >
                                              Manual Override
                                            </Badge>
                                          )}
                                        </div>
                                        {aiParsedData.candidate?.currentRole && (
                                          <div className="text-xs text-gray-500">
                                            {aiParsedData.candidate.currentRole}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                                    {aiParsedData.candidate?.contact?.email || candidateData.personalInfo?.email || candidate.email || '-'}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                                    {aiParsedData.candidate?.contact?.phone || candidateData.personalInfo?.phone || candidate.mobileNumber || '-'}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap">
                                    <Badge variant="outline" className="text-xs">
                                      {formatExperienceYears(experienceYears)}
                                    </Badge>
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap">
                                    {candidateData.genuinityAnalysis ? (
                                      candidateData.genuinityAnalysis.genuinityScore !== null && 
                                      candidateData.genuinityAnalysis.genuinityScore !== undefined ? (
                                        <div className="flex flex-col gap-1">
                                          <div className="flex items-center gap-2">
                                            <Badge 
                                              variant={
                                                candidateData.genuinityAnalysis.genuinityScore >= 80 ? "default" :
                                                candidateData.genuinityAnalysis.genuinityScore >= 60 ? "secondary" :
                                                "destructive"
                                              }
                                              className="text-sm font-semibold"
                                            >
                                              {`${candidateData.genuinityAnalysis.genuinityScore}%`}
                                            </Badge>
                                          </div>
                                          <span className="text-xs text-gray-500">
                                            {candidateData.genuinityAnalysis.confidenceLevel} Confidence
                                          </span>
                                        </div>
                                      ) : (
                                        <div className="flex flex-col gap-1">
                                          <Badge variant="outline" className="text-xs border-yellow-300 text-yellow-700">
                                            Analysis Failed
                                          </Badge>
                                          <span className="text-xs text-gray-500">
                                            {candidateData.genuinityAnalysis.confidenceLevel || 'Unknown'}
                                          </span>
                                        </div>
                                      )
                                    ) : (
                                      <span className="text-xs text-gray-400">Not analyzed</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap">
                                    {candidateData.skillsMatching && candidateData.skillsMatching.percentage !== null && candidateData.skillsMatching.percentage !== undefined ? (
                                      <div 
                                        className="flex flex-col gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                                        onClick={() => openSkillsMatchingPopup(
                                          candidate._id, 
                                          candidateData.candidateName || candidate.name, 
                                          candidateData.skillsMatching
                                        )}
                                      >
                                        <div className="flex items-center gap-2">
                                          <Badge 
                                            className={cn(
                                              "text-sm font-semibold text-white",
                                              eligibilityTab === "eligible" 
                                                ? "bg-green-600 hover:bg-green-700" 
                                                : "bg-red-600 hover:bg-red-700"
                                            )}
                                          >
                                            {`${candidateData.skillsMatching.percentage}%`}
                                          </Badge>
                                        </div>
                                        <span className="text-xs text-gray-500">
                                          {candidateData.skillsMatching.matchedSkills?.length || 0}/{candidateData.skillsMatching.totalJobSkills || 0} skills
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="text-sm text-gray-600">0%</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-4">
                                    <div className="flex flex-wrap gap-1 items-center">
                                      {visibleSkills.map((skill, skillIndex) => (
                                        <Badge
                                          key={skillIndex}
                                          variant="secondary"
                                          className="text-xs px-2 py-0.5"
                                        >
                                          {skill}
                                        </Badge>
                                      ))}
                                      {remainingSkillsCount > 0 && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="text-xs px-2 py-0.5 h-6 text-blue-600 hover:text-blue-800"
                                          onClick={() => openSkillsPopup(candidate._id, candidateData.candidateName || candidate.name, displaySkills)}
                                        >
                                          +{remainingSkillsCount} more
                                        </Button>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                                    <div className="flex items-center gap-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setActiveTab(candidate._id)}
                                        className="text-xs"
                                      >
                                        <Eye className="h-3 w-3 mr-1" />
                                        View
                                      </Button>
                                      <Button
                                        size="sm"
                                        onClick={() => handleMoveCandidateEligibility(candidate._id, eligibilityTab)}
                                        className={cn(
                                          "text-xs",
                                          eligibilityTab === "eligible" 
                                            ? "bg-red-500 text-white hover:bg-red-600" 
                                            : "bg-green-500 text-white hover:bg-green-600"
                                        )}
                                        title={eligibilityTab === "eligible" ? "Move to Not Eligible" : "Move to Eligible"}
                                      >
                                        {eligibilityTab === "eligible" ? (
                                          <>
                                            <ArrowRight className="h-3 w-3 mr-1" />
                                            Move
                                          </>
                                        ) : (
                                          <>
                                            <ArrowLeft className="h-3 w-3 mr-1" />
                                            Move
                                          </>
                                        )}
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            });
                              } catch (error) {
                                console.error('❌ Table rendering error:', error);
                                return (
                                  <tr>
                                    <td colSpan="9" className="px-4 py-8 text-center text-red-500">
                                      <div className="flex flex-col items-center gap-2">
                                        <X className="h-8 w-8" />
                                        <p>Error rendering table</p>
                                        <p className="text-xs">{error.message}</p>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              }
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Full Resume View Modal - Shown when View button is clicked */}
                    {activeTab && (
                      <Dialog open={!!activeTab} onOpenChange={() => setActiveTab(null)}>
                        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>Resume Details</DialogTitle>
                          </DialogHeader>
                          <div className="mt-4">
                            {selectedExistingCandidates.map((candidate) => (
                              <div
                                key={candidate._id}
                                className={cn(
                                  activeTab === candidate._id ? "block" : "hidden"
                                )}
                              >
                                {renderCandidateTab(candidate)}
                              </div>
                            ))}
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        );

      case 3:
        return (
          <div className="ai-module-step3-parent h-full flex flex-col">
            <div className="ai-module-step3-container flex flex-col h-full">
              {/* Fixed Header with Generate Questions and Add Question Buttons */}
              <div className="ai-module-step3-header flex-shrink-0 bg-white border-b border-gray-200 p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Questions and Difficulty Level</h3>
                    <p className="text-sm text-gray-500">Configure question parameters and generate questions for the exam</p>
                  </div>
                  
                  {/* Action Buttons - Top Right Corner */}
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={() => setShowAddQuestionModal(true)}
                      disabled={!completedSteps.has(2)}
                      size="lg"
                      variant="outline"
                      className="px-6 py-3 border-blue-600 text-blue-600 hover:bg-blue-50"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      Add Question
                    </Button>
                    
                    <Button
                      onClick={handleGenerateQuestionsClick}
                      disabled={!completedSteps.has(2) || generatingQuestions}
                      size="lg"
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700"
                    >
                      <Brain className="h-5 w-5 mr-2" />
                      {generatingQuestions ? 'Generating...' : 'Generate Questions'}
                    </Button>


                  </div>
                </div>
              </div>

              {/* Scrollable Content Area */}
              <div className="ai-module-step3-content flex-1 overflow-y-auto bg-gray-50 min-h-0">

              {!completedSteps.has(2) && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 m-6">
                  <p className="text-sm text-yellow-800">
                    Please complete Step 2 (Resume Extraction) before configuring questions.
                  </p>
                </div>
              )}

            {/* Central Ready to Generate Questions Content with Extended Content for Testing */}
            {completedSteps.has(2) && !questionsGenerated && generatedQuestions.length === 0 && (
              <div className="flex-1 py-8 px-6">
                <div className="max-w-4xl mx-auto space-y-8">
                  {/* Main Ready to Generate Section */}
                  <div className="text-center space-y-6">
                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                      <Brain className="h-10 w-10 text-blue-600" />
                    </div>
                    
                    <div className="space-y-3">
                      <h3 className="text-xl font-semibold text-gray-900">Ready to Generate Questions</h3>
                      <p className="text-gray-600 leading-relaxed max-w-2xl mx-auto">
                        Click the "Generate Questions" button above to configure difficulty levels for each skill and generate personalized questions for your candidates.
                      </p>
                    </div>

                    {/* Current Configuration Display for Single Candidate */}
                    {selectedExistingCandidates.length === 1 && (formData.easyQuestions > 0 || formData.mediumQuestions > 0 || formData.hardQuestions > 0) && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left max-w-md mx-auto">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-green-800">Current Configuration</p>
                            <p className="text-xs text-green-700 mt-1">
                              Total: {(formData.easyQuestions || 0) + (formData.mediumQuestions || 0) + (formData.hardQuestions || 0)} questions
                              {" "}(Easy: {formData.easyQuestions || 0}, Medium: {formData.mediumQuestions || 0}, Hard: {formData.hardQuestions || 0})
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Extended Content for Testing Fixed Header/Footer */}
                  <div className="space-y-6">
                    {/* Job Description Summary */}
                    {formData.jobDescription && (
                      <Card className="bg-blue-50 border-blue-200">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-blue-800">
                            <Briefcase className="h-5 w-5" />
                            Job Description Overview
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm font-medium text-blue-700">Position</Label>
                              <p className="text-sm text-blue-600">{formData.jobDescription.jobTitle || 'Not specified'}</p>
                            </div>
                            <div>
                              <Label className="text-sm font-medium text-blue-700">Experience Level</Label>
                              <p className="text-sm text-blue-600">{formData.jobDescription.experienceLevel || 'Not specified'}</p>
                            </div>
                          </div>
                          
                          {formData.jobDescription.skills && formData.jobDescription.skills.length > 0 && (
                            <div>
                              <Label className="text-sm font-medium text-blue-700 mb-2 block">Required Skills ({formData.jobDescription.skills.length})</Label>
                              <div className="flex flex-wrap gap-2">
                                {formData.jobDescription.skills.map((skill, index) => (
                                  <Badge key={index} variant="secondary" className="bg-blue-100 text-blue-800 border-blue-200">
                                    {skill}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Question Generation Confirmation Dialog */}
            <QuestionGenerationConfirmDialog
              isOpen={showQuestionGenerationConfirm}
              onClose={() => setShowQuestionGenerationConfirm(false)}
              onConfirm={handleQuestionGenerationConfirm}
              manualQuestionsCount={manualQuestionsCount}
              actionType={currentActionType}
            />

            {/* Generate Questions Modal */}
            <Dialog open={showGenerateQuestionsModal} onOpenChange={setShowGenerateQuestionsModal}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Brain className="h-5 w-5 text-blue-600" />
                    Generate Questions Configuration
                  </DialogTitle>
                  <DialogDescription>
                    Configure how questions should be generated for your {selectedExistingCandidates.length} candidate(s)
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                  {/* Question Generation Option */}
                  {selectedExistingCandidates.length > 1 && (
                    <div className="space-y-3">
                      <Label className="text-base font-medium">Question Generation Strategy</Label>
                      <Select
                        value={generateQuestionsFormData.questionGenerationOption}
                        onValueChange={(value) => setGenerateQuestionsFormData(prev => ({
                          ...prev,
                          questionGenerationOption: value
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="same">
                            Same Questions - All candidates get identical questions
                          </SelectItem>
                          <SelectItem value="similar">
                            Similar Questions - Questions vary slightly for each candidate
                          </SelectItem>
                          <SelectItem value="different">
                            Different Questions - Unique questions for each candidate
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-gray-500">
                        {generateQuestionsFormData.questionGenerationOption === "same" && 
                          "All candidates will receive the exact same set of questions."}
                        {generateQuestionsFormData.questionGenerationOption === "similar" && 
                          "Questions will be similar in difficulty and topic but worded differently."}
                        {generateQuestionsFormData.questionGenerationOption === "different" && 
                          "Each candidate will receive completely different questions tailored to their profile."}
                      </p>
                    </div>
                  )}

                  {/* Total Questions */}
                  <div className="space-y-3">
                    <Label className="text-base font-medium">Total Questions</Label>
                    <Input
                      type="number"
                      min="1"
                      max="50"
                      value={generateQuestionsFormData.totalQuestions}
                      onChange={(e) => setGenerateQuestionsFormData(prev => ({
                        ...prev,
                        totalQuestions: parseInt(e.target.value) || 10
                      }))}
                      placeholder="Enter total number of questions"
                    />
                    <p className="text-sm text-gray-500">
                      Recommended: 10-20 questions for optimal exam duration
                    </p>
                  </div>

                  {/* Skills-based Question Configuration */}
                  {formData.jobDescription?.skills?.length > 0 && (
                    <div className="space-y-4">
                      <Label className="text-base font-medium">Questions per Skill & Difficulty</Label>
                      <p className="text-sm text-gray-500">
                        Configure how many questions for each difficulty level per skill
                      </p>
                      
                      <div className="space-y-4 max-h-80 overflow-y-auto">
                        {formData.jobDescription.skills.map((skill, index) => (
                          <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <div className="flex items-center justify-between mb-3">
                              <Label className="text-sm font-medium text-gray-900">{skill}</Label>
                              <span className="text-xs text-gray-500">
                                Total: {
                                  (generateQuestionsFormData.skillQuestions?.[skill]?.easy || 0) +
                                  (generateQuestionsFormData.skillQuestions?.[skill]?.medium || 0) +
                                  (generateQuestionsFormData.skillQuestions?.[skill]?.hard || 0)
                                } questions
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-3">
                              {/* Easy Questions */}
                              <div className="space-y-1">
                                <Label className="text-xs text-green-700 font-medium">Easy</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max="10"
                                  value={generateQuestionsFormData.skillQuestions?.[skill]?.easy || 0}
                                  onChange={(e) => {
                                    const count = parseInt(e.target.value) || 0;
                                    setGenerateQuestionsFormData(prev => ({
                                      ...prev,
                                      skillQuestions: {
                                        ...prev.skillQuestions,
                                        [skill]: {
                                          ...prev.skillQuestions?.[skill],
                                          easy: count
                                        }
                                      }
                                    }));
                                  }}
                                  className="h-8 text-sm"
                                  placeholder="0"
                                />
                              </div>
                              
                              {/* Medium Questions */}
                              <div className="space-y-1">
                                <Label className="text-xs text-yellow-700 font-medium">Medium</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max="10"
                                  value={generateQuestionsFormData.skillQuestions?.[skill]?.medium || 0}
                                  onChange={(e) => {
                                    const count = parseInt(e.target.value) || 0;
                                    setGenerateQuestionsFormData(prev => ({
                                      ...prev,
                                      skillQuestions: {
                                        ...prev.skillQuestions,
                                        [skill]: {
                                          ...prev.skillQuestions?.[skill],
                                          medium: count
                                        }
                                      }
                                    }));
                                  }}
                                  className="h-8 text-sm"
                                  placeholder="0"
                                />
                              </div>
                              
                              {/* Hard Questions */}
                              <div className="space-y-1">
                                <Label className="text-xs text-red-700 font-medium">Hard</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max="10"
                                  value={generateQuestionsFormData.skillQuestions?.[skill]?.hard || 0}
                                  onChange={(e) => {
                                    const count = parseInt(e.target.value) || 0;
                                    setGenerateQuestionsFormData(prev => ({
                                      ...prev,
                                      skillQuestions: {
                                        ...prev.skillQuestions,
                                        [skill]: {
                                          ...prev.skillQuestions?.[skill],
                                          hard: count
                                        }
                                      }
                                    }));
                                  }}
                                  className="h-8 text-sm"
                                  placeholder="0"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Total Questions Validation */}
                      <div className="bg-gray-50 rounded-lg p-3 border">
                        <div className="flex items-center justify-between text-sm">
                          <span>Total Questions from Skills:</span>
                          <span className={cn(
                            "font-medium",
                            (() => {
                              const totalFromSkills = formData.jobDescription.skills.reduce((sum, skill) => {
                                const skillConfig = generateQuestionsFormData.skillQuestions?.[skill];
                                return sum + (skillConfig?.easy || 0) + (skillConfig?.medium || 0) + (skillConfig?.hard || 0);
                              }, 0);
                              return totalFromSkills === generateQuestionsFormData.totalQuestions
                                ? "text-green-600"
                                : "text-red-600";
                            })()
                          )}>
                            {formData.jobDescription.skills.reduce((sum, skill) => {
                              const skillConfig = generateQuestionsFormData.skillQuestions?.[skill];
                              return sum + (skillConfig?.easy || 0) + (skillConfig?.medium || 0) + (skillConfig?.hard || 0);
                            }, 0)} / {generateQuestionsFormData.totalQuestions}
                          </span>
                        </div>
                        
                        {/* Difficulty Breakdown */}
                        <div className="grid grid-cols-3 gap-4 mt-2 text-xs">
                          <div className="text-green-700">
                            Easy: {formData.jobDescription.skills.reduce((sum, skill) => {
                              return sum + (generateQuestionsFormData.skillQuestions?.[skill]?.easy || 0);
                            }, 0)}
                          </div>
                          <div className="text-yellow-700">
                            Medium: {formData.jobDescription.skills.reduce((sum, skill) => {
                              return sum + (generateQuestionsFormData.skillQuestions?.[skill]?.medium || 0);
                            }, 0)}
                          </div>
                          <div className="text-red-700">
                            Hard: {formData.jobDescription.skills.reduce((sum, skill) => {
                              return sum + (generateQuestionsFormData.skillQuestions?.[skill]?.hard || 0);
                            }, 0)}
                          </div>
                        </div>
                        
                        {(() => {
                          const totalFromSkills = formData.jobDescription.skills.reduce((sum, skill) => {
                            const skillConfig = generateQuestionsFormData.skillQuestions?.[skill];
                            return sum + (skillConfig?.easy || 0) + (skillConfig?.medium || 0) + (skillConfig?.hard || 0);
                          }, 0);
                          return totalFromSkills !== generateQuestionsFormData.totalQuestions && (
                            <p className="text-xs text-red-600 mt-1">
                              Please adjust skill questions to match total questions
                            </p>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Job Description Skills Info */}
                  {formData.jobDescription?.skills?.length > 0 && (
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <div className="flex items-start gap-2">
                        <Briefcase className="h-4 w-4 text-blue-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-blue-800">Job Description Skills</p>
                          <p className="text-xs text-blue-700 mt-1">
                            Configure questions and difficulty for each of the {formData.jobDescription.skills.length} skills from the job description
                          </p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {formData.jobDescription.skills.slice(0, 5).map((skill, index) => (
                              <Badge key={index} variant="outline" className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 border-blue-300">
                                {skill}
                              </Badge>
                            ))}
                            {formData.jobDescription.skills.length > 5 && (
                              <Badge variant="outline" className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 border-blue-300">
                                +{formData.jobDescription.skills.length - 5} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setShowGenerateQuestionsModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleGenerateQuestions}
                    disabled={
                      proceedLoading ||
                      generatingQuestions ||
                      (() => {
                        if (!formData.jobDescription?.skills?.length) return false;
                        const totalFromSkills = formData.jobDescription.skills.reduce((sum, skill) => {
                          const skillConfig = generateQuestionsFormData.skillQuestions?.[skill];
                          return sum + (skillConfig?.easy || 0) + (skillConfig?.medium || 0) + (skillConfig?.hard || 0);
                        }, 0);
                        return totalFromSkills !== generateQuestionsFormData.totalQuestions;
                      })()
                    }
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {proceedLoading || generatingQuestions ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {proceedLoading ? "Saving..." : "Generating..."}
                      </>
                    ) : (
                      <>
                        <Brain className="h-4 w-4 mr-2" />
                        Generate Questions
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Generated Questions Display or Loading State */}
            {generatingQuestions ? (
              /* Loading State for Question Generation */
              <Card className="mt-6">
                <CardContent className="py-8">
                  <div className="text-center space-y-4">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">Generating Questions...</h4>
                      <p className="text-sm text-gray-500 mt-1">
                        AI is creating personalized questions for your candidates. This may take a few moments.
                      </p>
                    </div>
                    <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                      <Brain className="h-4 w-4 animate-pulse" />
                      <span>Processing with AI...</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              /* Generated Questions Display */
              (() => {
                const existingQuestionsCheck = checkForExistingManualQuestions();
                return (questionsGenerated || generatedQuestions.length > 0 || existingQuestionsCheck.hasManualQuestions) && (
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      Generated Questions
                    </CardTitle>
                    <CardDescription>
                      {generatedQuestions.length > 0 ? (
                        <>
                          {questionGenerationOption !== "same" && generatedQuestions.length > 1 && (
                            <span className="ml-2 text-blue-600">
                              ({questionGenerationOption === "similar" ? "Similar questions" : "Different questions"} per candidate)
                            </span>
                          )}
                        </>
                      ) : (
                        "Questions will appear here once added"
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {addingManualQuestions ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mr-2" />
                        <span className="text-gray-600">Adding questions...</span>
                      </div>
                    ) : generatedQuestions.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Brain className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p>No questions added yet</p>
                        <p className="text-sm mt-2">Use "Add Question" or "Generate Questions" to add questions</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Consistent Individual Candidate Accordion Display */}
                        <div className="space-y-3">
                          {generatedQuestions.map((candidateQuestions, candidateIndex) => {
                            const isExpanded = activeQuestionTab === candidateQuestions.candidateId;
                            
                            return (
                              <div key={candidateQuestions.candidateId} className="border border-gray-200 rounded-lg overflow-hidden">
                                {/* Individual Candidate Accordion Header */}
                                <button
                                  onClick={() => setActiveQuestionTab(
                                    isExpanded ? null : candidateQuestions.candidateId
                                  )}
                                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors duration-200 flex items-center justify-between text-left"
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                      {candidateQuestions.candidateName?.charAt(0)?.toUpperCase()}
                                    </div>
                                    <div>
                                      <h4 className="font-medium text-gray-900">{candidateQuestions.candidateName}</h4>
                                      <p className="text-sm text-gray-500">
                                        {candidateQuestions.questions?.length || 0} questions total
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <div className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}>
                                      <ChevronRight className="h-4 w-4 text-gray-400" />
                                    </div>
                                  </div>
                                </button>

                                {/* Individual Candidate Questions Content */}
                                {isExpanded && (
                                  <div className="p-4 bg-white border-t border-gray-200">
                                    {candidateQuestions.questions && candidateQuestions.questions.length > 0 ? (
                                      <div className="space-y-4">
                                        {(() => {
                                          // Debug: Log question details for edit/delete button logic
                                          candidateQuestions.questions.forEach((q, idx) => {
                                            console.log(`   Question ${idx + 1}:`, {
                                              questionText: q.question?.substring(0, 50) + '...',
                                              manualQuestionId: q.manualQuestionId,
                                              hasManualQuestionId: !!q.manualQuestionId,
                                              isManual: q.isManual,
                                              category: q.category,
                                              source: q.source,
                                              showEditDelete: !!q.manualQuestionId
                                            });
                                          });
                                          return null;
                                        })()}
                                        {candidateQuestions.questions.map((question, index) => {
                                          const getQuestionSkill = (questionIndex) => {
                                            if (question.skill) return question.skill;
                                            
                                            if (formData.jobDescription?.skills && generateQuestionsFormData.skillQuestions) {
                                              let currentIndex = 0;
                                              for (const skill of formData.jobDescription.skills) {
                                                const skillConfig = generateQuestionsFormData.skillQuestions[skill];
                                                if (skillConfig) {
                                                  const skillTotal = (skillConfig.easy || 0) + (skillConfig.medium || 0) + (skillConfig.hard || 0);
                                                  if (questionIndex >= currentIndex && questionIndex < currentIndex + skillTotal) {
                                                    return skill;
                                                  }
                                                  currentIndex += skillTotal;
                                                }
                                              }
                                            }
                                            return null;
                                          };

                                          const questionSkill = getQuestionSkill(index);

                                          return (
                                            <div key={question.questionId || index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                              <div className="space-y-3">
                                                {/* Question Header */}
                                                <div className="flex items-start justify-between">
                                                  <div className="flex items-center space-x-2">
                                                    <span className="text-sm font-medium text-blue-600">
                                                      Question {index + 1}
                                                    </span>
                                                    {question.difficulty && (
                                                      <Badge
                                                        variant={
                                                          question.difficulty === "Easy" || question.difficulty === "easy"
                                                            ? "secondary"
                                                            : question.difficulty === "Hard" || question.difficulty === "hard"
                                                            ? "destructive"
                                                            : "default"
                                                        }
                                                        className="text-xs"
                                                      >
                                                        {question.difficulty.charAt(0).toUpperCase() + question.difficulty.slice(1)}
                                                      </Badge>
                                                    )}
                                                  </div>
                                                  <div className="flex items-center space-x-1">
                                                    {/* Show edit/delete buttons only for questions that exist in ai_manuals database */}
                                                    {question.manualQuestionId && (
                                                      <>
                                                        <Button
                                                          variant="ghost"
                                                          size="sm"
                                                          className="h-6 w-6 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                                          onClick={() => handleEditManualQuestion(candidateQuestions.candidateId, index, question)}
                                                          title="Edit Question"
                                                        >
                                                          <Edit className="h-3 w-3" />
                                                        </Button>
                                                        <Button
                                                          variant="ghost"
                                                          size="sm"
                                                          className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                          onClick={() => handleDeleteManualQuestion(candidateQuestions.candidateId, index, question)}
                                                          title="Delete Question"
                                                        >
                                                          <Trash2 className="h-3 w-3" />
                                                        </Button>
                                                      </>
                                                    )}
                                                  </div>
                                                </div>

                                                {/* Question Content */}
                                                <div className="text-sm text-gray-900 mb-3 leading-relaxed">
                                                  {renderQuestionText(question.question)}
                                                </div>
                                              
                                                {question.options && question.options.length > 0 && (
                                                  <div className="space-y-2">
                                                    {question.options.map((option, optionIndex) => {
                                                      let isCorrectAnswer = false;
                                                      
                                                      if (typeof question.correctAnswerIndex === 'number') {
                                                        isCorrectAnswer = question.correctAnswerIndex === optionIndex;
                                                      }
                                                      
                                                      if (!isCorrectAnswer && question.correctAnswer) {
                                                        isCorrectAnswer = question.correctAnswer === option;
                                                      }
                                                      
                                                      return (
                                                        <div 
                                                          key={optionIndex} 
                                                          className={`flex items-center space-x-2 p-2 rounded text-sm ${
                                                            isCorrectAnswer
                                                              ? 'bg-green-50 border border-green-200 text-green-800' 
                                                              : 'bg-gray-50 border border-gray-200 text-gray-700'
                                                          }`}
                                                        >
                                                          <span className="font-medium">
                                                            {String.fromCharCode(65 + optionIndex)}.
                                                          </span>
                                                          <div className="flex-1">
                                                            {renderQuestionText(option)}
                                                          </div>
                                                          {isCorrectAnswer && (
                                                            <CheckCircle className="h-4 w-4 text-green-600 ml-auto" />
                                                          )}
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <div className="text-center py-8 text-gray-500">
                                        <Brain className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                                        <p>No questions generated</p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
                );
              })()
            )}
            </div>

              {/* Fixed Footer with Navigation Buttons */}
              <div className="ai-module-step3-footer flex-shrink-0 bg-white border-t border-gray-200 px-4">
                <div className="flex justify-between items-center">
                  <Button
                    variant="outline"
                    onClick={prevStep}
                    disabled={currentStep === 1 || loading || processingResumes}
                    className="flex items-center space-x-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    <span>Previous</span>
                  </Button>

                  {/* Show Next button when any questions exist (manual or generated) */}
                  {(() => {
                    const existingQuestionsCheck = checkForExistingManualQuestions();
                    return (questionsGenerated || existingQuestionsCheck.hasManualQuestions) && (
                      <Button
                        onClick={nextStep}
                        disabled={loading || processingResumes}
                        className="flex items-center space-x-2"
                      >
                        {(loading || processingResumes) ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>{processingResumes ? "Processing..." : "Saving..."}</span>
                          </>
                        ) : (
                          <>
                            <span>Next</span>
                            <ArrowRight className="h-4 w-4" />
                          </>
                        )}
                      </Button>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="ai-module-step4-parent h-full flex flex-col">
            <div className="ai-module-step4-container flex flex-col h-full">
              {/* Fixed Header */}
              <div className="ai-module-step4-header flex-shrink-0 bg-white border-b border-gray-200 p-4">
                <div className="flex justify-between items-center">
                  <div className="text-left">
                    <h3 className="text-lg font-semibold text-gray-900">Exam Configuration</h3>
                    <p className="text-sm text-gray-500">Configure timing, security settings, and finalize your exam</p>
                  </div>
                  <div>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => {
                        setShowConfigurePopup(true);
                      }}
                      className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Settings className="h-4 w-4" />
                      <span>Configure</span>
                    </Button>
                  </div>
                </div>
              </div>

              {/* Scrollable Content Area */}
              <div className="ai-module-step4-content flex-1 overflow-y-auto bg-gray-50 min-h-0">
                <div className="p-4 space-y-6">

                  {/* Assignment Cards Display */}
                  {showAssignmentCards && assignmentCards.length > 0 && (
                    <Card className="border-blue-200 bg-blue-50">
                      <CardHeader>
                        <div className="flex justify-between items-center">
                          <div>
                            <CardTitle className="text-lg font-semibold text-blue-900">
                              Exam Assignment Schedule
                            </CardTitle>
                            <CardDescription className="text-blue-700">
                              Review and confirm the exam assignments for each candidate
                            </CardDescription>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleCancelAssignments}
                              className="text-gray-600 hover:text-gray-800"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {assignmentCards.map((card, index) => (
                            <Card key={index} className="border-l-4 border-l-blue-500 bg-white">
                              <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                  <CardTitle className="text-base flex items-center gap-2">
                                    <Badge variant="outline">Day {card.batchNumber}</Badge>
                                    <span className="font-semibold">{card.candidateName}</span>
                                  </CardTitle>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditAssignmentCard(index)}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                </div>
                              </CardHeader>
                              <CardContent className="space-y-2">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div className="flex items-center gap-2">
                                    <Calendar className="h-4 w-4 text-gray-500" />
                                    <span className="text-gray-600">Date:</span>
                                    <span className="font-medium">{card.examDate}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-gray-500" />
                                    <span className="text-gray-600">Time:</span>
                                    <span className="font-medium">{card.examTime}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-gray-500" />
                                    <span className="text-gray-600">Duration:</span>
                                    <span className="font-medium">{card.duration} min</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-gray-500" />
                                    <span className="text-gray-600">Grace:</span>
                                    <span className="font-medium">{card.graceTime} min</span>
                                  </div>
                                </div>
                                <div className="flex gap-2 mt-2 flex-wrap">
                                  {card.enableProctoring && (
                                    <Badge variant="secondary" className="text-xs">Proctoring</Badge>
                                  )}
                                  {card.captureImages && (
                                    <Badge variant="secondary" className="text-xs">Screenshots</Badge>
                                  )}
                                  {card.trackTabSwitching && (
                                    <Badge variant="secondary" className="text-xs">Tab Tracking</Badge>
                                  )}
                                  {card.faceDetection && (
                                    <Badge variant="secondary" className="text-xs">Face Detection</Badge>
                                  )}
                                  {card.enableProctoring && card.captureFrequency && (
                                    <Badge variant="outline" className="text-xs">
                                      Capture: {card.captureFrequency}s
                                    </Badge>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

            {/* Eligible Candidates Section - Only shown when assignment cards are NOT displayed */}
            {!showAssignmentCards && (() => {
              const { eligible } = categorizeCandidatesByEligibility();
              
              if (eligible.length === 0) {
                return (
                  <Card className="border-orange-200 bg-orange-50">
                    <CardContent className="pt-6 text-center">
                      <div className="space-y-4">
                        <div className="text-orange-600">
                          <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <h3 className="text-lg font-medium">No Eligible Candidates</h3>
                          <p className="text-sm mt-2">
                            None of the selected candidates meet the eligibility criteria for exam assignment.
                          </p>
                        </div>
                        <div className="text-xs text-gray-600 bg-white p-3 rounded border">
                          <p className="font-medium mb-1">Eligibility Requirements:</p>
                          <p>• Skills matching: {formData.jobDescription?.passPercentage || 0}% threshold</p>
                          <p>• Experience requirements must be met</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              }
              
              if (eligible.length === 1) {
                const eligibleCandidate = eligible[0];
                return (
                  <Card className="border-green-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-blue-600" />
                        <div className="flex items-center gap-2">
                          <span>Exam Settings for {eligibleCandidate.name}</span>
                          <Badge className="bg-green-100 text-green-800 border-green-300 text-xs">
                            Eligible
                          </Badge>
                        </div>
                      </CardTitle>
                    </CardHeader>
                <CardContent className="space-y-6">
                  {/* Exam Date and Time */}
                  <div className="space-y-3">
                    <Label className="text-base font-medium">Exam Date and Time</Label>
                    <Input
                      type="datetime-local"
                      value={formData.examDateTime}
                      onChange={(e) => handleInputChange("examDateTime", e.target.value)}
                      className="w-full"
                    />
                  </div>

                  {/* Exam Duration */}
                  <div className="space-y-3">
                    <Label className="text-base font-medium">Exam Duration (minutes)</Label>
                    <Input
                      type="number"
                      min="15"
                      max="300"
                      value={formData.examDuration}
                      onChange={(e) => handleInputChange("examDuration", parseInt(e.target.value) || 60)}
                      placeholder="Enter duration in minutes"
                    />
                  </div>

                  {/* Grace Time */}
                  <div className="space-y-3">
                    <Label className="text-base font-medium">Grace Time (minutes)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="60"
                      value={formData.graceTime}
                      onChange={(e) => handleInputChange("graceTime", parseInt(e.target.value) || 60)}
                      placeholder="Enter grace time in minutes"
                    />
                  </div>

                  {/* Security Settings */}
                  <div className="space-y-4">
                    <Label className="text-base font-medium">Security Settings</Label>
                    
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="enableProctoring"
                          checked={formData.enableProctoring}
                          onCheckedChange={(checked) => handleInputChange("enableProctoring", checked)}
                        />
                        <Label htmlFor="enableProctoring" className="text-sm">Enable Proctoring</Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="captureImages"
                          checked={formData.captureImages}
                          onCheckedChange={(checked) => handleInputChange("captureImages", checked)}
                        />
                        <Label htmlFor="captureImages" className="text-sm">Capture Images During Exam</Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="trackTabSwitching"
                          checked={formData.trackTabSwitching}
                          onCheckedChange={(checked) => handleInputChange("trackTabSwitching", checked)}
                        />
                        <Label htmlFor="trackTabSwitching" className="text-sm">Track Tab Switching</Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="faceDetection"
                          checked={formData.faceDetection}
                          onCheckedChange={(checked) => handleInputChange("faceDetection", checked)}
                        />
                        <Label htmlFor="faceDetection" className="text-sm">Face Detection Monitoring</Label>
                      </div>
                    </div>
                  </div>

                  {/* Advanced Settings */}
                  <div className="space-y-4">
                    <Label className="text-base font-medium">Advanced Settings</Label>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm">Language</Label>
                        <Select
                          value={formData.language}
                          onValueChange={(value) => handleInputChange("language", value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="English">English</SelectItem>
                            <SelectItem value="Spanish">Spanish</SelectItem>
                            <SelectItem value="French">French</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm">Passing Score (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={formData.passingScore?.[0] || 60}
                          onChange={(e) => handleInputChange("passingScore", [parseInt(e.target.value) || 60])}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          }

          // Multiple candidates case - simplified without individual settings
          return (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <p className="text-sm text-gray-600">
                  All {eligible.length} eligible candidates will use the same configuration from the Configure popup
                </p>
                <div className="flex items-center justify-center gap-2 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-green-700">{eligible.length} Eligible for Exam Assignment</span>
                  </div>
                </div>
              </div>

              {/* Simple candidate list without individual configuration */}
              <Card className="border-green-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-green-600" />
                    <span>Eligible Candidates</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {eligible.map((candidate) => (
                      <div
                        key={candidate._id}
                        className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg"
                      >
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium text-green-900">{candidate.name}</span>
                        <Badge className="bg-green-100 text-green-800 border-green-300 text-xs ml-auto">
                          Eligible
                        </Badge>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <strong>Note:</strong> All candidates will use the same exam configuration (timing, security settings, etc.) 
                      as defined in the Configure popup. Individual assignments will be scheduled based on your batch and timing settings.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          );
        })()} {/* End of eligible candidates section conditional */}
                </div>
              </div>

              {/* Fixed Footer with Navigation Buttons */}
              <div className="ai-module-step4-footer flex-shrink-0 bg-white border-t border-gray-200">
                <div className="px-6 pt-4 pb-6">
                  <div className="flex justify-between items-center">
                    <Button
                      variant="outline"
                      onClick={prevStep}
                      disabled={currentStep === 1 || loading || processingResumes}
                      className="flex items-center space-x-2"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      <span>Previous</span>
                    </Button>

                    <Button
                      onClick={nextStep}
                      disabled={
                        loading || 
                        processingResumes || 
                        (currentStep === 4 && !showAssignmentCards) // Disable Complete button until assignment cards are shown
                      }
                      title={
                        (currentStep === 4 && !showAssignmentCards) 
                          ? "Please click 'Configure' to generate assignment schedule first" 
                          : ""
                      }
                      className="flex items-center space-x-2"
                    >
                      {(loading || processingResumes) ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>{processingResumes ? "Processing..." : "Saving..."}</span>
                        </>
                      ) : currentStep === totalSteps ? (
                        <>
                          <CheckCircle className="h-4 w-4" />
                          <span>Complete</span>
                        </>
                      ) : (
                        <>
                          <span>Next</span>
                          <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div key={componentKey} className={cn(
      isModal ? "h-full overflow-hidden" : "h-screen overflow-hidden"
    )}>
      <div className={cn(
        "ai-module-container flex flex-col",
        isModal ? "modal h-full min-h-0 bg-white relative overflow-hidden" : "non-modal h-full overflow-hidden"
      )}>
        {/* Fixed Header with Job Information - Tab Style */}
        <div className={cn(
          "bg-blue-600 text-white px-4 py-2 flex-shrink-0 relative z-20",
          isModal ? "shadow-lg" : "sticky top-0"
        )}>
          <div className={cn(
            isModal ? "w-full max-w-none" : "max-w-7xl mx-auto"
          )}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {jobDescriptionDataState && (
                  <div className="flex items-center space-x-2">
                    <Briefcase className="h-4 w-4 text-blue-200" />
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-white">
                        {jobDescriptionDataState.jobTitle}
                      </span>
                      {jobDescriptionDataState.skills && jobDescriptionDataState.skills.length > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="text-blue-200 text-xs">•</span>
                          {jobDescriptionDataState.skills.slice(0, 5).map((skill, index) => (
                            <Badge 
                              key={index} 
                              variant="secondary" 
                              className="bg-blue-500 text-blue-100 text-xs px-1.5 py-0.5 border-blue-400 h-5"
                            >
                              {skill}
                            </Badge>
                          ))}
                          {jobDescriptionDataState.skills.length > 5 && (
                            <Badge 
                              variant="secondary" 
                              className="bg-blue-500 text-blue-100 text-xs px-1.5 py-0.5 border-blue-400 h-5 cursor-pointer hover:bg-blue-400 transition-colors"
                              onClick={openJobSkillsPopup}
                            >
                              +{jobDescriptionDataState.skills.length - 5} more
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {isModal && onClose && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleModalClose}
                  className="text-blue-200 hover:text-white hover:bg-blue-700 h-6 w-6 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable Main Content */}
        <div className={cn(
          "flex-1 bg-gray-50 min-h-0 overflow-hidden",
          currentStep === 3 || currentStep === 4 ? "flex flex-col" : ""
        )}>
          <div className={cn(
            "h-full overflow-y-auto",
            currentStep === 3 || currentStep === 4 ? "flex flex-col min-h-0" : "p-4",
            isModal && currentStep !== 3 && currentStep !== 4 ? "w-full max-w-none pb-20" : "",
            !isModal && currentStep !== 3 && currentStep !== 4 ? "max-w-7xl mx-auto" : ""
          )}>

            {/* Step Content */}
            <div className={cn(
              currentStep === 3 || currentStep === 4 ? "h-full flex flex-col" : "mb-6"
            )}>
              {renderStepContent()}
            </div>
          </div>
        </div>

        {/* Fixed Footer - Navigation Buttons (Hidden for Step 3 and Step 4 as they have their own fixed footer) */}
        {currentStep !== 3 && currentStep !== 4 && (
          <div className={cn(
            "bg-white border-t border-gray-200 px-6 py-6 flex-shrink-0 relative z-20",
            isModal ? "shadow-lg" : "sticky bottom-0"
          )}>
            <div className={cn(
              isModal ? "w-full max-w-none" : "max-w-7xl mx-auto"
            )}>
              <div className="flex justify-between items-center min-h-[48px]">
                <Button
                  variant="outline"
                  onClick={prevStep}
                  disabled={currentStep === 1 || loading || processingResumes}
                  className="flex items-center space-x-2 h-10 px-4"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Previous</span>
                </Button>

                <Button
                  onClick={nextStep}
                  disabled={loading || processingResumes || currentStep === totalSteps}
                  className="flex items-center space-x-2 h-10 px-4"
                >
                  {(loading || processingResumes) ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>{processingResumes ? "Processing Resumes..." : "Saving..."}</span>
                    </>
                  ) : currentStep === totalSteps ? (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>Complete</span>
                    </>
                  ) : (
                    <>
                      <span>Next</span>
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Save Confirmation Dialog */}
        <Dialog open={showSaveConfirmDialog} onOpenChange={setShowSaveConfirmDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Save Draft?</DialogTitle>
              <DialogDescription>
                You have unsaved changes. Would you like to save your progress as a draft?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => handleSaveConfirmation(false)}
              >
                Don't Save
              </Button>
              <Button
                onClick={() => handleSaveConfirmation(true)}
              >
                Save Draft
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Candidate Selection Modal */}
        <CandidateSelectionModal
          isOpen={isCandidateSelectionModalOpen}
          onClose={() => setIsCandidateSelectionModalOpen(false)}
          selectedCandidates={selectedExistingCandidates}
          onCandidatesChange={setSelectedExistingCandidates}
          availableCandidates={availableCandidates}
          onSearch={(searchTerm) => {
            setCandidateSearch(searchTerm);
            // Clear existing timeout
            if (window.candidateSearchTimeout) {
              clearTimeout(window.candidateSearchTimeout);
            }
            // Set new timeout for search
            window.candidateSearchTimeout = setTimeout(async () => {
              await fetchCandidates(searchTerm);
            }, 300);
          }}
          searchTerm={candidateSearch}
          loadingCandidates={loadingCandidates}
          onAddCandidate={() => {
            setIsCandidateSelectionModalOpen(false);
            setTimeout(() => {
              setIsAddCandidateModalOpen(true);
            }, 100);
          }}
        />

        {/* Add Candidate Modal */}
        <AddCandidateModal
          isOpen={isAddCandidateModalOpen}
          onClose={() => setIsAddCandidateModalOpen(false)}
          onSuccess={(newCandidate) => {
            // Add the new candidate to available candidates
            setAvailableCandidates(prev => [...prev, newCandidate]);
            // Optionally auto-select the new candidate
            setSelectedExistingCandidates(prev => [...prev, newCandidate]);
            // Close modal and refresh candidates
            setIsAddCandidateModalOpen(false);
            fetchCandidates();
          }}
        />



        {/* Skills Popup Dialog */}
        <Dialog open={skillsPopup.isOpen} onOpenChange={(open) => !open && closeSkillsPopup()}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Star className="h-5 w-5 text-blue-600" />
                Skills for {skillsPopup.candidateName}
              </DialogTitle>
              <DialogDescription>
                All skills extracted from the candidate's resume
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto">
              {skillsPopup.skills && skillsPopup.skills.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {skillsPopup.skills.map((skill, index) => (
                    <Badge 
                      key={index} 
                      variant="secondary" 
                      className="bg-blue-100 text-blue-800 border-blue-300"
                    >
                      {skill}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No skills found</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={closeSkillsPopup}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Skills Matching Popup Dialog */}
        <Dialog open={skillsMatchingPopup.isOpen} onOpenChange={(open) => !open && closeSkillsMatchingPopup()}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Skills Matching Details for {skillsMatchingPopup.candidateName}
              </DialogTitle>
              <DialogDescription>
                Detailed breakdown of skills matching between candidate and job requirements
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 max-h-96 overflow-y-auto">
              {/* Matching Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-blue-900">Matching Summary</h3>
                  <Badge 
                    variant="secondary" 
                    className={cn(
                      "text-sm font-medium",
                      skillsMatchingPopup.percentage >= 75 ? "bg-green-100 text-green-800 border-green-300" :
                      skillsMatchingPopup.percentage >= 50 ? "bg-yellow-100 text-yellow-800 border-yellow-300" :
                      "bg-red-100 text-red-800 border-red-300"
                    )}
                  >
                    {skillsMatchingPopup.percentage}% Match
                  </Badge>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <div className="font-medium text-gray-900">{skillsMatchingPopup.matchedSkills?.length || 0}</div>
                    <div className="text-gray-600">Matched Skills</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium text-gray-900">{skillsMatchingPopup.totalJobSkills || 0}</div>
                    <div className="text-gray-600">Required Skills</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium text-gray-900">{skillsMatchingPopup.totalCandidateSkills || 0}</div>
                    <div className="text-gray-600">Candidate Skills</div>
                  </div>
                </div>
              </div>

              {/* Matched Skills */}
              {skillsMatchingPopup.matchedSkills && skillsMatchingPopup.matchedSkills.length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-medium text-green-800 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Matched Skills ({skillsMatchingPopup.matchedSkills.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {skillsMatchingPopup.matchedSkills.map((skill, index) => (
                      <Badge 
                        key={index} 
                        variant="secondary" 
                        className="bg-green-100 text-green-800 border-green-300"
                      >
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Unmatched Skills */}
              {skillsMatchingPopup.unmatchedSkills && skillsMatchingPopup.unmatchedSkills.length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-medium text-red-800 flex items-center gap-2">
                    <X className="h-4 w-4" />
                    Missing Skills ({skillsMatchingPopup.unmatchedSkills.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {skillsMatchingPopup.unmatchedSkills.map((skill, index) => (
                      <Badge 
                        key={index} 
                        variant="secondary" 
                        className="bg-red-100 text-red-800 border-red-300"
                      >
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={closeSkillsMatchingPopup}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Job Skills Popup Dialog */}
        <Dialog open={jobSkillsPopup.isOpen} onOpenChange={(open) => !open && closeJobSkillsPopup()}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-blue-600" />
                Job Requirements: {jobSkillsPopup.jobTitle}
              </DialogTitle>
              <DialogDescription>
                All skills required for this position
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto">
              {jobSkillsPopup.skills && jobSkillsPopup.skills.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {jobSkillsPopup.skills.map((skill, index) => (
                    <Badge 
                      key={index} 
                      variant="secondary" 
                      className="bg-blue-100 text-blue-800 border-blue-300"
                    >
                      {skill}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No skills defined</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={closeJobSkillsPopup}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Question Modal */}
        <AddQuestionModal
          isOpen={showAddQuestionModal}
          onClose={() => setShowAddQuestionModal(false)}
          onAddManually={handleAddQuestionManually}
          onAddFromQuestionBank={handleAddFromQuestionBank}
        />

        {/* Manual Question Modal */}
        <ManualQuestionModal
          isOpen={showManualQuestionModal}
          onClose={() => setShowManualQuestionModal(false)}
          eligibleCandidates={categorizeCandidatesByEligibility().eligible}
          aiModuleId={aiModuleId}
          onQuestionsAdded={handleManualQuestionsAdded}
          questionGenerationChoice={questionGenerationChoice} // Pass the choice
        />

        {/* Question Bank Selection Modal */}
        <QuestionBankSelectionModal
          isOpen={showQuestionBankModal}
          onClose={() => setShowQuestionBankModal(false)}
          onQuestionsSelected={handleQuestionBankSelection}
          eligibleCandidates={categorizeCandidatesByEligibility().eligible}
          questionGenerationChoice={questionGenerationChoice} // Pass the choice
        />

        {/* Edit Question Modal (for both manual and AI-generated questions) */}
        <EditManualQuestionModal
          isOpen={showEditQuestionModal}
          onClose={() => {
            setShowEditQuestionModal(false);
            setEditingManualQuestion(null);
          }}
          question={editingManualQuestion?.question}
          onSave={saveEditedManualQuestion}
          loading={loading}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-600" />
                Delete Question
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this question? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            {questionToDelete && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-gray-900 mb-2">
                  {questionToDelete.question.question}
                </p>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {questionToDelete.question.difficulty}
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    {questionToDelete.question.category}
                  </Badge>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDeleteConfirmDialog(false);
                  setQuestionToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={confirmDeleteManualQuestion}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Question
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Assignment Modal */}
        <Dialog open={showEditAssignmentModal} onOpenChange={setShowEditAssignmentModal}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Edit Assignment
                {editingAssignmentIndex !== null && assignmentCards[editingAssignmentIndex] && (
                  <Badge variant="outline" className="ml-2">
                    {assignmentCards[editingAssignmentIndex].candidateName}
                  </Badge>
                )}
              </DialogTitle>
              <DialogDescription>
                Modify the exam timing, date, and proctoring settings for this candidate
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 p-4">
              {/* Date and Time Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Exam Timing</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="editExamDate" className="text-sm font-medium">
                      Exam Date <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="editExamDate"
                      type="date"
                      value={editAssignmentData.examDate || ''}
                      onChange={(e) => setEditAssignmentData(prev => ({
                        ...prev,
                        examDate: e.target.value
                      }))}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="editExamTime" className="text-sm font-medium">
                      Exam Time <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="editExamTime"
                      type="time"
                      value={editAssignmentData.examTime || ''}
                      onChange={(e) => setEditAssignmentData(prev => ({
                        ...prev,
                        examTime: e.target.value
                      }))}
                      className="w-full"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="editDuration" className="text-sm font-medium">
                      Duration (minutes) <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="editDuration"
                      type="number"
                      min="1"
                      value={editAssignmentData.duration || ''}
                      onChange={(e) => setEditAssignmentData(prev => ({
                        ...prev,
                        duration: e.target.value
                      }))}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="editGraceTime" className="text-sm font-medium">
                      Grace Time (minutes)
                    </Label>
                    <Input
                      id="editGraceTime"
                      type="number"
                      min="0"
                      value={editAssignmentData.graceTime || ''}
                      onChange={(e) => setEditAssignmentData(prev => ({
                        ...prev,
                        graceTime: e.target.value
                      }))}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Proctoring Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Proctoring Settings</h3>
                
                {/* Enable Proctoring - Main Toggle */}
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="editEnableProctoring" className="text-sm font-medium">
                      Enable Proctoring
                    </Label>
                    <p className="text-xs text-gray-500">
                      Monitor candidate during the exam
                    </p>
                  </div>
                  <Switch
                    id="editEnableProctoring"
                    checked={editAssignmentData.enableProctoring || false}
                    onCheckedChange={(checked) => setEditAssignmentData(prev => ({
                      ...prev,
                      enableProctoring: checked,
                      // If disabling proctoring, also disable dependent features
                      captureImages: checked ? prev.captureImages : false,
                      trackTabSwitching: checked ? prev.trackTabSwitching : false,
                      faceDetection: checked ? prev.faceDetection : false,
                    }))}
                  />
                </div>

                {/* Conditional Proctoring Options */}
                {editAssignmentData.enableProctoring && (
                  <div className="space-y-4 pl-4 border-l-2 border-blue-200 bg-blue-50 p-4 rounded-r-lg">
                    <p className="text-sm text-blue-700 font-medium mb-3">
                      Proctoring Options
                    </p>
                    
                    {/* Capture Images */}
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label htmlFor="editCaptureImages" className="text-sm font-medium">
                          Capture Images
                        </Label>
                        <p className="text-xs text-gray-500">
                          Take periodic screenshots during exam
                        </p>
                      </div>
                      <Switch
                        id="editCaptureImages"
                        checked={editAssignmentData.captureImages || false}
                        onCheckedChange={(checked) => setEditAssignmentData(prev => ({
                          ...prev,
                          captureImages: checked
                        }))}
                      />
                    </div>

                    {/* Track Tab Switching */}
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label htmlFor="editTrackTabSwitching" className="text-sm font-medium">
                          Track Tab Switching
                        </Label>
                        <p className="text-xs text-gray-500">
                          Monitor when candidate switches browser tabs
                        </p>
                      </div>
                      <Switch
                        id="editTrackTabSwitching"
                        checked={editAssignmentData.trackTabSwitching || false}
                        onCheckedChange={(checked) => setEditAssignmentData(prev => ({
                          ...prev,
                          trackTabSwitching: checked
                        }))}
                      />
                    </div>

                    {/* Face Detection */}
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label htmlFor="editFaceDetection" className="text-sm font-medium">
                          Face Detection
                        </Label>
                        <p className="text-xs text-gray-500">
                          Ensure candidate presence throughout exam
                        </p>
                      </div>
                      <Switch
                        id="editFaceDetection"
                        checked={editAssignmentData.faceDetection || false}
                        onCheckedChange={(checked) => setEditAssignmentData(prev => ({
                          ...prev,
                          faceDetection: checked
                        }))}
                      />
                    </div>

                    {/* Capture Frequency */}
                    <div className="space-y-2">
                      <Label htmlFor="editCaptureFrequency" className="text-sm font-medium">
                        Capture Frequency (seconds)
                      </Label>
                      <Input
                        id="editCaptureFrequency"
                        type="number"
                        min="5"
                        max="300"
                        value={editAssignmentData.captureFrequency || 10}
                        onChange={(e) => setEditAssignmentData(prev => ({
                          ...prev,
                          captureFrequency: e.target.value
                        }))}
                        className="w-full"
                      />
                      <p className="text-xs text-gray-500">
                        How often to capture screenshots (5-300 seconds)
                      </p>
                    </div>
                  </div>
                )}

                {/* Warnings Allowed */}
                <div className="space-y-2">
                  <Label htmlFor="editWarningsAllowed" className="text-sm font-medium">
                    Warnings Allowed
                  </Label>
                  <Input
                    id="editWarningsAllowed"
                    type="number"
                    min="0"
                    max="10"
                    value={editAssignmentData.warningsAllowed || 0}
                    onChange={(e) => setEditAssignmentData(prev => ({
                      ...prev,
                      warningsAllowed: e.target.value
                    }))}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500">
                    Number of warnings before disqualification
                  </p>
                </div>
              </div>

              {/* Advanced Settings */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Advanced Settings</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Language</Label>
                    <Select
                      value={editAssignmentData.language || 'English'}
                      onValueChange={(value) => setEditAssignmentData(prev => ({
                        ...prev,
                        language: value
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="English">English</SelectItem>
                        <SelectItem value="Spanish">Spanish</SelectItem>
                        <SelectItem value="French">French</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Passing Score (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={editAssignmentData.passingScore || 60}
                      onChange={(e) => setEditAssignmentData(prev => ({
                        ...prev,
                        passingScore: e.target.value
                      }))}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="flex justify-between p-4">
              <Button
                variant="outline"
                onClick={handleCancelEditAssignment}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEditedAssignment}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={!editAssignmentData.examDate || !editAssignmentData.examTime || !editAssignmentData.duration}
              >
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Configure Popup */}
        <ConfigurePopup
          isOpen={showConfigurePopup}
          onClose={() => setShowConfigurePopup(false)}
          eligibleCandidates={categorizeCandidatesByEligibility().eligible}
          onSave={handleConfigurationSave}
          initialData={formData}
        />

        {/* Completion Confirmation Dialog */}
        <Dialog open={showCompletionConfirmDialog} onOpenChange={() => {}}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-blue-600" />
                Complete AI Module
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to complete and create the exam assignments? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => handleCompletionConfirmation(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleCompletionConfirmation(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating Configuration & Assignments...
                  </>
                ) : (
                  "Yes, Complete"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
});

// Wrap AIModule with ErrorBoundary to handle DOM manipulation errors
const AIModuleWithErrorBoundary = forwardRef((props, ref) => (
  <ErrorBoundary>
    <AIModule {...props} ref={ref} />
  </ErrorBoundary>
));

// Set display name for debugging
AIModuleWithErrorBoundary.displayName = 'AIModuleWithErrorBoundary';

// Export both named and default
export { AIModuleWithErrorBoundary };
export default AIModuleWithErrorBoundary;